/* SpawnAnything — app: renderer, world/biomes, particles, camera, input, UI and the frame loop. */
import * as THREE from 'three';
import './style.css';
import SimWorker from './sim/sim.worker.js?worker&inline';
import {
  AOF, BONE, BOOT, BOOT_D, CREST, CREST_D, DARK, DEC,
  FACE, GOLD, GOLD_D, KEY, LEATH, LEATH_D, MAIL1, MAIL2,
  MODELS, OLIVE, OLIVE_D, OLIVE_L, P, PRESETS, RECIPES, ROLE,
  RPROPS, SKIN, SKIN_D, SLIT, STEEL, STEEL_D, STEEL_L, T,
  T2, TEAMS, TL, TYPES, VARIANTS, VoxModel, WEAPONS, addOutline,
  buildArmy, chk, compileRecipe, finishGeo, hash3, headsTall, humanoid, hx,
  lum, mapColors, meshAO, meshGreedy, modelCenter, mutantN, rHex, rPrim,
  rProps, ramp, recipeModel, registerMutant, rotX, satBoost, teamColor, tmpC,
} from './game/voxel.js';
import { generate } from './net/generate.js';
import { BLOCKED_MESSAGE, isBlocked } from './game/blocklist.js';
const DAILY_LIMIT_MESSAGE = 'Für heute sind genug neue KI-Figuren entstanden. Figuren aus der Bibliothek und deiner Sammlung gehen weiter.';
import { AI_FACE_MODE } from './game/faceMode.js';
import { createPhotoUpload } from './ui/photoUpload.js';
import { showRating } from './ui/rating.js';
import { kampfOf, KAMPF_WEAPON } from './game/combatRole.js';
import { createBattleLink } from './features/battleLink.js'; // feature:link
import { createMutators } from './features/mutators.js'; // feature:mutators
import { ERA_RECIPES, ERA_TOOLS, ERA_TYPES, ERA_WEAPONS, ERA_WEAPON_FX } from './content/eras.js';
import { ERAS } from './content/eras.js';
import { BUILDINGS, validateBuildingRecipe, fallbackBuildingRecipe } from './game/buildings.js';
import { buildingSimData, spawnReserve, compiledFor } from './game/buildingSetup.js';
import { createBuildingLayer } from './game/buildingLayer.js';
import { cellAt, heightAt, inBounds, isWalkable } from './world/terrain.js';
import { buildTerrainMesh, paletteFromStyle } from './world/terrainMesh.js';
import { createGroundDetail } from './world/groundDetail.js';
import { createBattleAudio } from './audio/sfx.js';
import { createWeaponFx } from './fx/weaponFx.js';
import { mountSoundPanel } from './ui/soundPanel.js';
import { TERRAIN_KEYS, TERRAIN_PRESETS, terrainForPreset, workerTerrain } from './world/terrainPresets.js';
import { createShop } from './ui/shop.js';
import { gateOf } from './game/unlocks.js';
import { createMultiplayer } from './net/mpClient.js';
import { packTyped, unpackTyped, packF32, unpackF32, validTerrain, mpKey } from './net/mpSetup.js';
import { validateUnitRecipe, fallbackUnitRecipe } from './game/recipeSchema.js';
import { repairOwnRecipe } from './game/recipeRepair.js'; // own AI figures: dock floating parts, re-hang wrong ones (library: untouched)
import { createRecorder } from './replay/recorder.js';
import { createReplayPlayer } from './replay/player.js';
import { createClipper, bestWindow } from './ui/clip.js';
import { createWeather, EV_WX_BOLT } from './features/weather.js'; // feature:weather
import { OUTLINE_GLSL, noOutline, outlineRadius, outlineUniforms, updateOutline } from './render/outlinePass.js';
import { bakeImpostor, makeImpostorMesh } from './render/impostors.js';
import { makeCuller } from './render/culling.js';
import { makeBlobShadows } from './render/blobShadows.js';
import { AUTO_STEPS, createAutoQuality } from './render/autoQuality.js';
import { createBet } from './features/bet.js'; // feature:bet
import { initTopMenu } from './ui/topMenu.js';
import { createDice } from './features/dice.js'; // feature:dice
import { MAX_TROOPS, filterPaint, groupRanges, normalizeGroups, paintGroups } from './game/groups.js';
import { createTroopUI, sharesWithNew, sharesWithout, splitCount } from './ui/troops.js';
import { unitCost } from './features/campaignCosts.js';
import { createZones } from './features/zone.js'; // feature:zone
import { createFormation } from './features/formation.js'; // feature:formation
import { DISASTER_TOOLS, createDisasterFx } from './features/disaster.js'; // feature:disaster
import { createGrabHand } from './features/grab.js'; // feature:grab
import { mountArmies, mountBody, mountDispose, mountEvent, mountMeshes, mountRender, mountSync, mountUI } from './features/mount.js'; // feature:mount
import { createCrater } from './features/crater.js'; // feature:crater
import { createThrownFx, planThrow, THROW_KINDS } from './fx/thrown.js'; // feature:thrown
import { createWavesUI } from './features/waves.js'; // feature:waves
import { createSlowmo } from './features/slowmo.js'; // feature:slowmo
import { createCampaign } from './features/campaign.js'; // feature:campaign
import * as convert from './features/convert.js'; // feature:convert
import { createWorldOptions } from './features/worldOptions.js'; // Welt-Optionen: one window, a card per world mechanic
import { createBuildWindow } from './ui/buildWindow.js'; // Bauen: build menu window with 3D cards
import { collection } from './game/collection.js'; // Meine Sammlung: saved AI figures and buildings
import { initThemeSwitch } from './ui/theme.js'; // UI designs A/B/C ("Comic, aber ruhig"), switch with U
import { initDock } from './ui/dock.js'; // battle HUD: one collapsible bar at the bottom
import { createArmyWindow } from './ui/armyWindow.js'; // "Armeen" window
import { createSetupHub } from './ui/setupHub.js'; // panel tiles that open the menu windows
import { createMainMenu } from './ui/mainMenu.js'; // Hauptmenü (draft "1 · Titelbild")
import { createPauseMenu } from './ui/pauseMenu.js'; // Esc in a battle: Weiter / Aufstellung / Hauptmenü
import { createWindow, anyWindowOpen } from './ui/window.js';
import { createSelectBox } from './ui/selectBox.js'; // white selection box in the setup
import { createItemWindow } from './ui/itemWindow.js'; // Items window + hotbar: the digit keys follow TOOL_KEYS (updated in place)
import { createBattleStats } from './features/stats.js'; // feature:stats

// era content packs (urzeit … zukunft) join the sandbox roster
Object.assign(RECIPES, ERA_RECIPES); Object.assign(TYPES, ERA_TYPES); Object.assign(WEAPONS, ERA_WEAPONS);
// explosion kind 4 = green comic stink cloud (proj only drives visuals; must stay < 10, arcs add 10)
const KIND_GAS = 4;
for (const [k, f] of Object.entries(ERA_WEAPON_FX)) if (f.explosion === 'gas' && WEAPONS[k]) WEAPONS[k].proj = KIND_GAS;

const $ = s => document.querySelector(s);
const nf = new Intl.NumberFormat('de-DE');
const fmt = n => nf.format(Math.round(n));
const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
const COARSE = matchMedia('(pointer: coarse)').matches;
if ((import.meta.env && import.meta.env.DEV) || new URLSearchParams(location.search).has('fps')) document.documentElement.classList.add('dev'); // fps readout: dev server or ?fps

/* ---------------- renderer & scene ---------------- */
const canvas = $('#c');
let renderer;
// frame cap (0 = free, 60, 30); the 30-fps battery mode also asks the browser for the low-power GPU (on the next start)
let fpsCap = 0; try { fpsCap = +localStorage.getItem('aw-fps') || 0; } catch (e) { }
try {
  // no default-framebuffer MSAA: the scene is always drawn into post.rt (outline pass), the canvas only gets one quad
  renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: fpsCap === 30 ? 'low-power' : 'high-performance' });
} catch (e) {
  $('#err').hidden = false; $('#err').textContent = 'WebGL ist auf diesem Gerät nicht verfügbar. Bitte einen aktuellen Browser nutzen.'; throw e;
}
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
const scene = new THREE.Scene();
const SKY = new THREE.Color(0xa9d6ee);
scene.background = SKY;
scene.fog = new THREE.Fog(SKY, 200, 900);
const camera = new THREE.PerspectiveCamera(42, 1, 0.3, 6000);
const hemi = new THREE.HemisphereLight(0xe2f1ff, 0x7d9447, 1.45);
scene.add(hemi);
const sun = new THREE.DirectionalLight(0xfff0d6, 2.35);
sun.shadow.bias = -0.0004; sun.shadow.normalBias = 0.03;
scene.add(sun, sun.target);
const uTime = { value: 0 };
const uGTime = { value: 0 };

/* --- unit shader patch --- */
const ANIM_HEAD = `
uniform vec3 uPivot[12]; uniform float uRole[12]; uniform float uBase[12];
uniform float uAim; uniform float uAtk; uniform float uRecoil; uniform float uTime; uniform float uWave;
attribute float aPart; attribute vec4 aAnim; attribute float aEmit; varying float vEmit;
mat3 rX(float a){float c=cos(a),s=sin(a);return mat3(1.,0.,0., 0.,c,s, 0.,-s,c);}
mat3 rY(float a){float c=cos(a),s=sin(a);return mat3(c,0.,-s, 0.,1.,0., s,0.,c);}
mat3 rZ(float a){float c=cos(a),s=sin(a);return mat3(c,s,0., -s,c,0., 0.,0.,1.);}
float swingCurve(float t){
  if(t<0.3) return mix(0.,-2.6,smoothstep(0.,1.,t/0.3));
  if(t<0.55) return mix(-2.6,-0.45,smoothstep(0.,1.,(t-0.3)/0.25));
  return mix(-0.45,0.,(t-0.55)/0.45);
}
float wv(float k, float ph, float walk){ return sin(uTime*1.8 - k*0.7 + ph*0.12) * uWave * (0.45 + 0.55*min(walk,1.0)); }
void animUnit(inout vec3 p, inout vec3 n){
  int i = int(aPart + 0.5);
  float role = uRole[i]; vec3 piv = uPivot[i]; float a = uBase[i];
  float walk = min(aAnim.x, 1.6); float ph = aAnim.y; float atk = aAnim.z; float ex = aAnim.w;
  float s = sin(ph); float on = step(0.0, atk);
  float idle = sin(uTime*2.1 + ph) * (1.0 - min(walk, 1.0));
  mat3 R = mat3(1.0); vec3 off = vec3(0.0);
  if (role < 0.5) { return; }
  else if (role < 1.5) { a += sin(ph*2.0)*0.05*walk + idle*0.03; if (uAtk > 0.5 && uAtk < 1.5) a += on*0.55*sin(atk*3.14159); R = rX(a); if (uWave > 0.0) off.x += wv(-1.0, ph, walk); }
  else if (role < 2.5) { a += -s*0.55*walk*(1.0-uAim) + idle*0.04; R = rX(a); }
  else if (role < 3.5) {
    a += s*0.55*walk*(1.0-uAim) - idle*0.04;
    if (on > 0.5) { if (uAtk < 0.5) a += swingCurve(atk); else if (uAtk < 2.5) a += -0.22*sin(atk*3.14159); else a += -0.9*sin(atk*3.14159); }
    R = rX(a);
  }
  else if (role < 4.5) { a += s*0.7*walk; R = rX(a); }
  else if (role < 5.5) { a += -s*0.7*walk; R = rX(a); }
  else if (role < 6.5) { R = rY(sin(ph*0.5)*0.22*walk + sin(uTime*1.3 + ph)*0.08); }
  else if (role < 7.5) { a += on*0.65*sin(atk*3.14159) + 0.06 + 0.04*sin(uTime*2.0 + ph); R = rX(a); if (uWave > 0.0) off.x += wv(-1.0, ph, walk); }
  else if (role < 8.5) { if (uAtk > 1.5 && uAtk < 2.5) off.z = -uRecoil*on*sin(atk*3.14159); R = rY(ex); }
  else if (role < 9.5) { R = rZ(-(0.12 + 0.75*abs(sin(ph*2.0))*min(walk,1.0)) - idle*0.05); }
  else if (role < 10.5) { R = rZ((0.12 + 0.75*abs(sin(ph*2.0))*min(walk,1.0)) + idle*0.05); }
  else { off.x = wv(uBase[i], ph, walk); }
  p = R * (p - piv + off) + piv; n = R * n;
}
`;
// per-voxel ±4 % brightness jitter for the greedy near LOD (uVox = voxel grid offset xyz + voxel size)
const VOXJIT_V = '\nvarying vec3 vVoxP; varying vec3 vVoxN;';
const VOXJIT_F = `
uniform vec4 uVox; varying vec3 vVoxP; varying vec3 vVoxN;
void voxJitter(inout vec3 c){ if (vEmit > 0.5) return; vec3 g = floor(vVoxP / uVox.w + uVox.xyz - vVoxN * 0.5); c *= 0.96 + 0.08 * fract(sin(dot(g, vec3(12.9898, 78.233, 37.719))) * 43758.5453); }`;
function makeUnitMaterial(A) {
  const uni = {
    uPivot: { value: A.pivots }, uRole: { value: A.roles }, uBase: { value: A.bases },
    uAim: { value: A.aim ? 1 : 0 }, uAtk: { value: A.atkStyle }, uRecoil: { value: 0.35 * A.stats.s }, uTime, uWave: { value: A.m.waveAmp || 0 },
    uVox: { value: new THREE.Vector4(...A.jit0) },
  };
  // jit: the greedy-meshed near LOD gets its per-voxel brightness jitter in the fragment shader (merged faces share one colour)
  const mkMat = jit => {
    const mat = new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: TOON_GRAD });
    mat.onBeforeCompile = sh => {
      Object.assign(sh.uniforms, uni);
      sh.vertexShader = sh.vertexShader.replace('void main() {', ANIM_HEAD + (jit ? VOXJIT_V : '') + '\nvoid main() {')
        .replace('#include <beginnormal_vertex>', 'vec3 objectNormal = vec3(normal); vec3 animPos = position; animUnit(animPos, objectNormal); vEmit = aEmit;' + (jit ? ' vVoxP = position; vVoxN = normal;' : ''))
        .replace('#include <begin_vertex>', 'vec3 transformed = animPos;');
      sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'varying float vEmit;' + (jit ? VOXJIT_F : '') + '\nvoid main() {')
        .replace('#include <color_fragment>', '#include <color_fragment>' + (jit ? '\nvoxJitter(diffuseColor.rgb);' : ''))
        .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += diffuseColor.rgb * vEmit * 1.6;');
      convert.shader(sh); // feature:convert
    };
    mat.customProgramCacheKey = () => jit ? 'unit-toon-j' : 'unit-toon';
    return mat;
  };
  const mat = mkMat(false), mat0 = mkMat(true);
  const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  depth.onBeforeCompile = sh => {
    Object.assign(sh.uniforms, uni);
    sh.vertexShader = sh.vertexShader.replace('void main() {', ANIM_HEAD + '\nvoid main() {')
      .replace('#include <begin_vertex>', 'vec3 transformed = position; vec3 dn = vec3(0.,1.,0.); animUnit(transformed, dn); vEmit = aEmit;');
  };
  depth.customProgramCacheKey = () => 'unit-depth-v2';
  return { mat, mat0, depth };
}

/* --- GPU particles: analytic trajectories, zero per-frame CPU --- */
const PART_HEAD = `
uniform float uGT;
attribute vec4 aSpawn; attribute vec4 aVel; attribute vec4 aMisc; attribute vec4 aCol;
varying vec3 vPCol; varying float vFade;
mat3 rAxis(vec3 ax, float a){ float c=cos(a), s=sin(a), t=1.-c; return mat3(t*ax.x*ax.x+c, t*ax.x*ax.y+s*ax.z, t*ax.x*ax.z-s*ax.y, t*ax.x*ax.y-s*ax.z, t*ax.y*ax.y+c, t*ax.y*ax.z+s*ax.x, t*ax.x*ax.z+s*ax.y, t*ax.y*ax.z-s*ax.x, t*ax.z*ax.z+c); }
`;
const PART_BODY = `
  float age = uGT - aSpawn.w; float life = aVel.w;
  float size = aMisc.x; float g = aMisc.y; float spin = aMisc.z; float kind = aMisc.w;
  float vis = step(0.0, age) * step(age, life);
  vec3 p0 = aSpawn.xyz; vec3 v = aVel.xyz; vec3 pos; float sc = size; float rotAge = age;
  if (kind < 0.5) {
    float gy = aCol.w;
    float h0 = max(p0.y - size*0.5 - gy, 0.0);
    float t1 = (v.y + sqrt(max(v.y*v.y + 2.0*g*h0, 0.0))) / g;
    if (age < t1) { pos = p0 + v*age; pos.y -= 0.5*g*age*age; }
    else {
      float vy1 = -(v.y - g*t1) * 0.32; vec2 vh = v.xz * 0.4; float t2 = 2.0*vy1/g; float a2 = age - t1;
      vec3 ph = vec3(p0.x + v.x*t1, gy + size*0.5, p0.z + v.z*t1);
      float aa = min(a2, t2);
      pos = ph + vec3(vh.x*aa, vy1*aa - 0.5*g*aa*aa, vh.y*aa);
      rotAge = t1 + aa;
    }
    sc *= clamp((life - age) / 1.5, 0.0, 1.0);
    vFade = 1.0;
  } else {
    float k = 2.2;
    float dr = (1.0 - exp(-k*age)) / k;
    pos = p0 + v*dr; pos.y += (kind > 1.5 && kind < 2.5 ? 0.9 : -g*0.5) * age * age * (kind > 1.5 && kind < 2.5 ? 0.3 : 1.0);
    float f = age / life;
    sc = size * (kind > 1.5 && kind < 2.5 ? (0.5 + f*1.3) * (1.0 - f*f) : (1.0 - f));
    vFade = 1.0 - f;
  }
  vec3 ax = normalize(vec3(fract(sin(aSpawn.w*91.3+p0.x)*437.5)-0.5, 0.6, fract(sin(aSpawn.w*13.7+p0.z)*917.1)-0.5));
  mat3 RR = rAxis(ax, spin * rotAge);
  vPCol = aCol.rgb;
`;
function makeParticles(max, lit) {
  const box = new THREE.BoxGeometry(1, 1, 1);
  const g = new THREE.InstancedBufferGeometry();
  g.index = box.index; g.setAttribute('position', box.getAttribute('position')); g.setAttribute('normal', box.getAttribute('normal'));
  const mk = (n, init) => { const a = new THREE.InstancedBufferAttribute(new Float32Array(max * n), n); a.setUsage(THREE.DynamicDrawUsage); if (init) for (let i = 0; i < max; i++) init(a.array, i * n); g.setAttribute(n === 3 ? 'aCol' : '', a); return a; };
  const aSpawn = new THREE.InstancedBufferAttribute(new Float32Array(max * 4), 4); for (let i = 0; i < max; i++) aSpawn.array[i * 4 + 3] = -1e6;
  const aVel = new THREE.InstancedBufferAttribute(new Float32Array(max * 4), 4);
  const aMisc = new THREE.InstancedBufferAttribute(new Float32Array(max * 4), 4);
  const aCol = new THREE.InstancedBufferAttribute(new Float32Array(max * 4), 4); // rgb + ground height for the bounce
  for (const a of [aSpawn, aVel, aMisc, aCol]) a.setUsage(THREE.DynamicDrawUsage);
  g.setAttribute('aSpawn', aSpawn); g.setAttribute('aVel', aVel); g.setAttribute('aMisc', aMisc); g.setAttribute('aCol', aCol);
  g.instanceCount = max;
  const mat = lit ? new THREE.MeshLambertMaterial() : new THREE.MeshBasicMaterial({ fog: true });
  mat.onBeforeCompile = sh => {
    sh.uniforms.uGT = uGTime;
    sh.vertexShader = sh.vertexShader.replace('void main() {', PART_HEAD + '\nvoid main() {');
    if (lit) sh.vertexShader = sh.vertexShader
      .replace('#include <beginnormal_vertex>', PART_BODY + '\nvec3 objectNormal = RR * normal;')
      .replace('#include <begin_vertex>', 'vec3 transformed = (RR * position) * sc * vis + pos;');
    else sh.vertexShader = sh.vertexShader
      .replace('#include <begin_vertex>', PART_BODY + '\nvec3 transformed = (RR * position) * sc * vis + pos;');
    sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'varying vec3 vPCol; varying float vFade;\nvoid main() {')
      .replace('#include <color_fragment>', '#include <color_fragment>\ndiffuseColor.rgb *= vPCol;' + (lit ? '' : ' diffuseColor.rgb *= 0.55 + 0.9*vFade;'));
  };
  mat.customProgramCacheKey = () => lit ? 'pdebris' : 'pfx';
  noOutline(mat);
  const mesh = new THREE.Mesh(g, mat); mesh.frustumCulled = false; mesh.renderOrder = 1;
  const sys = { mesh, max, head: 0, lo: Infinity, hi: -1, wrapped: false, aSpawn, aVel, aMisc, aCol,
    spawn(x, y, z, vx, vy, vz, life, size, grav, spin, kind, r, gg, b, gy = 0) {
      const i = this.head; this.head = (this.head + 1) % this.max; if (this.head === 0) this.wrapped = true;
      const s4 = i * 4;
      aSpawn.array[s4] = x; aSpawn.array[s4 + 1] = y; aSpawn.array[s4 + 2] = z; aSpawn.array[s4 + 3] = uGTime.value;
      aVel.array[s4] = vx; aVel.array[s4 + 1] = vy; aVel.array[s4 + 2] = vz; aVel.array[s4 + 3] = life;
      aMisc.array[s4] = size; aMisc.array[s4 + 1] = grav; aMisc.array[s4 + 2] = spin; aMisc.array[s4 + 3] = kind;
      aCol.array[s4] = r; aCol.array[s4 + 1] = gg; aCol.array[s4 + 2] = b; aCol.array[s4 + 3] = gy;
      if (i < this.lo) this.lo = i; if (i > this.hi) this.hi = i;
    },
    flush() {
      if (this.hi < 0) return;
      const lo = this.lo, n = this.hi - this.lo + 1;
      for (const [a, k] of [[aSpawn, 4], [aVel, 4], [aMisc, 4], [aCol, 4]]) { a.clearUpdateRanges(); a.addUpdateRange(lo * k, n * k); a.needsUpdate = true; }
      this.lo = Infinity; this.hi = -1;
    },
    clear() { aSpawn.array.fill(0); for (let i = 0; i < this.max; i++) aSpawn.array[i * 4 + 3] = -1e6; aSpawn.clearUpdateRanges(); aSpawn.needsUpdate = true; this.head = 0; },
  };
  return sys;
}

/* --- visual styles --- */
const WIESE = {
  name: 'Sommerwiese', desc: 'Hell und freundlich, wie in deinem Video.',
  sky: 0xa9d6ee, fog: [1.6, 5], hemi: [0xe2f1ff, 0x7d9447, 1.45], sun: [0xfff0d6, 2.35], sunDir: [-0.5, 0.82, 0.35], exposure: 1.0,
  ground: [0x98c94a, 0xbcd95e, 0x77ad44, 0xcfd47a], grass: { lo: 0x6fa63a, hi: 0xd2e67a, h: 1, dens: 1 },
  flowers: [0xe0443a, 0x4a6fe0, 0xf4f1e8, 0xf2c94c, 0xd96bb0],
  props: [
    { kind: 'round', pal: [0x3f8f4f, 0x4e9e57, 0x2f7a45, 0x5aa95e], n: 30 },
    { kind: 'round', pal: [0xd9a35a, 0xc98a45, 0xe6b86a], n: 8 },
    { kind: 'pine', pal: [0x2f6e45, 0x3a7d50, 0x285f3b], n: 6 },
    { kind: 'rock', pal: [0x8a8f96, 0x737880, 0x9ea3a9], n: 30, single: true },
  ],
  amb: null, post: { vig: 0.18 },
};
const STYLES = {
  wiese: WIESE,
  gold: {
    name: 'Goldene Stunde', desc: 'Tiefe Abendsonne, lange Schatten, Miniatur-Look durch Tilt-Shift.',
    sky: 0xf1c08a, fog: [1.2, 4], hemi: [0xffd9a8, 0x8a6a3a, 1.05], sun: [0xffa860, 3.1], sunDir: [-0.75, 0.38, 0.55], exposure: 1.05,
    ground: [0xa9a64a, 0xc9b35a, 0x8f8f3e, 0xdcb466], grass: { lo: 0x8a7f2e, hi: 0xf0c860, h: 1.3, dens: 1 }, flowers: [0xf2c94c, 0xe8761e],
    props: [
      { kind: 'round', pal: [0xd9822b, 0xe8a33c, 0xc8642a, 0xb8541f], n: 26 },
      { kind: 'round', pal: [0x6f8f3a, 0x84a044], n: 10 },
      { kind: 'rock', pal: [0x9a8a78, 0x857565], n: 24, single: true },
    ],
    amb: { type: 'leaves', n: 380, color: [1.3, 0.62, 0.22] }, post: { tilt: 0.9, vig: 0.38, sat: 1.12, con: 1.05, tint: [1.06, 1.0, 0.92] },
  },
  nacht: {
    name: 'Fackelnacht', desc: 'Dunkle Nacht, Fackeln werfen warme Lichtinseln, Glühwürmchen schweben.',
    sky: 0x16213a, fog: [1.1, 3.6], hemi: [0x7f9ad8, 0x1c2433, 1.5], sun: [0xa9c2ff, 1.5], sunDir: [0.4, 0.8, -0.4], exposure: 1.25,
    ground: [0x3f6338, 0x4c6d3e, 0x34532f, 0x5a6c46], grass: { lo: 0x1d3519, hi: 0x4f6d37, h: 1, dens: 0.8 }, flowers: [],
    props: [
      { kind: 'pine', pal: [0x1d3d2c, 0x234835, 0x183424], n: 34 },
      { kind: 'rock', pal: [0x4a4f58, 0x3c4048], n: 26, single: true },
    ],
    torches: true, amb: { type: 'fireflies', n: 420, color: [1.8, 1.7, 0.6] }, post: { vig: 0.6, sat: 0.92, tint: [0.95, 0.98, 1.06] },
  },
  neon: {
    name: 'Neon-Sumpf', desc: 'Türkiser Nebel, pinke und grüne Voxel-Säulen, Wasserflächen.',
    sky: 0x63c6ba, fog: [0.6, 2.4], hemi: [0xa6f7e8, 0x2a5a4a, 1.35], sun: [0xffe6f0, 1.6], sunDir: [-0.4, 0.85, 0.5], exposure: 1.0,
    ground: [0x3faa58, 0x5cc86a, 0x2f8f6a, 0x7ad37a], water: 0x3a9ccc, grass: { lo: 0x2f8a4a, hi: 0x8be08a, h: 0.9, dens: 0.8 }, flowers: [0xff4f9a, 0x6ff0ff],
    props: [
      { kind: 'pillar', pal: [0xd8326b, 0xb02a5c, 0xe8487f], n: 22 },
      { kind: 'pillar', pal: [0x34c79a, 0x22a58a, 0x5fe0b0, 0x1f8f78], n: 26 },
      { kind: 'ruin', pal: [0x3a4a4a, 0x2e3c3c, 0x4a5a5a], n: 6, single: true },
    ],
    amb: { type: 'spores', n: 450, color: [1.6, 0.6, 1.1] }, post: { tilt: 0.45, vig: 0.32, sat: 1.18 },
  },
  wueste: {
    name: 'Wüstensturm', desc: 'Heller Sand, hohes goldenes Gras, orange Korallenbüsche, Staub in der Luft.',
    sky: 0xefe0c6, fog: [1.0, 3.6], hemi: [0xfff4e0, 0xc9a878, 1.4], sun: [0xfff0d0, 2.6], sunDir: [-0.55, 0.7, 0.45], exposure: 1.0,
    ground: [0xe6d9bf, 0xf0e5cf, 0xd8c7a4, 0xe4d2ae], grass: { lo: 0xa8771f, hi: 0xf2c252, h: 2.4, dens: 1.25 }, flowers: [],
    props: [
      { kind: 'bush', pal: [0xe8761e, 0xf0902a, 0xd6601a, 0xf5a33c], n: 28 },
      { kind: 'dead', pal: [0x8a6a4a, 0x6b5038], n: 8, single: true },
      { kind: 'rock', pal: [0xd9c39a, 0xc7ae84, 0xe4d2ae], n: 26, single: true },
    ],
    amb: { type: 'dust', n: 700, color: [1.0, 0.9, 0.72] }, post: { tilt: 0.55, vig: 0.22, sat: 1.05, tint: [1.04, 1.0, 0.95] },
  },
  winter: {
    name: 'Winterkrieg', desc: 'Verschneites Feld, dunkle Tannen mit Schneekappen, fallender Schnee.',
    sky: 0xd5e3ee, fog: [1.0, 3.4], hemi: [0xeaf4ff, 0x9fb4c8, 1.45], sun: [0xfff6ea, 2.0], sunDir: [-0.5, 0.6, 0.5], exposure: 0.95,
    ground: [0xeef3f8, 0xe2eaf3, 0xd2deea, 0xfbfdff], snow: 1, grass: { lo: 0x7f978f, hi: 0xd8e4e4, h: 0.8, dens: 0.35 }, flowers: [],
    props: [
      { kind: 'pine', pal: [0x2f5e4a, 0x3a6d55, 0x27503f], snow: true, n: 36 },
      { kind: 'rock', pal: [0x8a96a2, 0x75808c], n: 22, single: true, snow: true },
    ],
    amb: { type: 'snow', n: 1600, color: [1.2, 1.22, 1.28] }, post: { vig: 0.25, sat: 0.9, tint: [0.96, 1.0, 1.06] },
  },
  dorf: {
    name: 'Mittelmeerdorf', desc: 'Weiße Häuser mit Terrakotta-Dächern, Zypressen, satte Felder.',
    sky: 0xbfe4f2, fog: [1.5, 5], hemi: [0xf2f8ff, 0x9aaa4a, 1.5], sun: [0xfff2dc, 2.4], sunDir: [-0.45, 0.8, 0.4], exposure: 1.0,
    ground: [0xa6c64a, 0xbfd45a, 0x8ab640, 0xd6d872], grass: { lo: 0x88b23a, hi: 0xe6e86a, h: 1, dens: 1 }, flowers: [0xe0443a, 0xf4f1e8, 0xd96bb0],
    props: [
      { kind: 'house', pal: [0xf3eee2, 0xd9643a, 0xe7e0d0, 0xc4532e], n: 9, cluster: [2, 5], spread: 26 },
      { kind: 'cypress', pal: [0x2f5e3a, 0x3a6b42], n: 16 },
      { kind: 'round', pal: [0x5f9a3a, 0x6fae44], n: 12 },
    ],
    amb: null, post: { tilt: 0.5, vig: 0.15, sat: 1.1 },
  },
  comic: {
    name: 'Knallbunt', desc: 'Satte Cartoon-Farben unter strahlend blauem Himmel.',
    sky: 0x8fd3ff, fog: [1.8, 6], hemi: [0xffffff, 0x8aa050, 1.2], sun: [0xffffff, 2.8], sunDir: [-0.5, 0.8, 0.4], exposure: 1.0,
    ground: [0x7fd04a, 0x9ee05a, 0x66bf44, 0xb6e46e], grass: { lo: 0x58b03a, hi: 0xc6f07a, h: 1, dens: 0.8 }, flowers: [0xff3b3b, 0x3b6bff, 0xffffff, 0xffd23b],
    props: [
      { kind: 'round', pal: [0x2fae4f, 0x3fc05e], n: 30 },
      { kind: 'rock', pal: [0x9aa0aa, 0x80868f], n: 20, single: true },
    ],
    amb: null, post: { sat: 1.3, con: 1.12, vig: 0.1 },
  },
  pixel: {
    ...WIESE, name: 'Retro-Pixel', desc: 'Grobe Pixel wie in einem 16-Bit-Spiel. Nebenbei der schnellste Modus für schwache Geräte.',
    post: { pixel: 3, sat: 1.2, con: 1.05 },
  },
  film: {
    name: 'Epischer Kriegsfilm', desc: 'Entsättigt, bewölkt, Asche in der Luft, im Look großer Schlachtfilme.',
    sky: 0x9aa3aa, fog: [0.9, 3.2], hemi: [0xc8d0d8, 0x5a5a48, 1.45], sun: [0xe6eaee, 1.7], sunDir: [-0.3, 0.9, 0.3], exposure: 1.15,
    ground: [0x85825f, 0x948d69, 0x737152, 0xa39774], grass: { lo: 0x434a2c, hi: 0x8a885a, h: 1.2, dens: 0.6 }, flowers: [],
    props: [
      { kind: 'dead', pal: [0x4a3e34, 0x5a4c40], n: 22, single: true },
      { kind: 'pine', pal: [0x2a3a30, 0x324438], n: 18 },
      { kind: 'rock', pal: [0x6a6e72, 0x5a5e62, 0x7a7e82], n: 34, single: true },
      { kind: 'ruin', pal: [0x7a7a76, 0x6a6a66, 0x8a8a84], n: 8, single: true },
    ],
    amb: { type: 'ash', n: 900, color: [0.75, 0.75, 0.78] }, post: { sat: 0.6, con: 1.1, vig: 0.45, tint: [0.98, 1.0, 1.03] },
  },
};
const STYLE_KEYS = Object.keys(STYLES);
let styleKey = 'wiese';
try { const k = localStorage.getItem('aw-style'); if (k && STYLES[k]) styleKey = k; } catch (e) { }
let style = STYLES[styleKey];
const TOON_GRAD = new THREE.DataTexture(new Uint8Array([95, 165, 255]), 3, 1, THREE.RedFormat);
TOON_GRAD.minFilter = TOON_GRAD.magFilter = THREE.NearestFilter; TOON_GRAD.needsUpdate = true;
// feature:thrown: your own grenade / barrel rumbles when it lands (the explosion itself arrives as a normal event)
const thrown = createThrownFx({ scene, toonGrad: TOON_GRAD, onLand: (x, z, vk, own) => { if (own) shake((vk ? TOOLS.fass : TOOLS.granate).aoe * 1.2, x, z); } });

/* --- environment --- */
let envMeshes = [];
let torchPos = [];
const groundMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
const GU = {
  cA: { value: new THREE.Color() }, cB: { value: new THREE.Color() }, cC: { value: new THREE.Color() }, cD: { value: new THREE.Color() },
  cW: { value: new THREE.Color(0x3a9ccc) }, uWater: { value: 0 }, uSnow: { value: 0 }, uT: uTime,
  uTorch: { value: Array.from({ length: 24 }, () => new THREE.Vector4(0, 0, 1, 0)) }, uTorchCol: { value: new THREE.Color(0xff9a3c) },
};
const groundDetail = createGroundDetail(THREE, { snow: GU.uSnow }); // 0.5 m sub-voxel colours on terrain + flat field (src/world/groundDetail.js)
const gdOff = new URLSearchParams(location.search).get('gd') === '0'; // ?gd=0: plain vertex colours (cost measurement)
groundDetail.enabled = !gdOff;
groundMat.onBeforeCompile = sh => {
  Object.assign(sh.uniforms, GU);
  sh.vertexShader = sh.vertexShader.replace('void main() {', 'varying vec3 vW;\nvoid main() {').replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
  sh.fragmentShader = sh.fragmentShader.replace('void main() {', `varying vec3 vW; uniform vec3 cA, cB, cC, cD, cW, uTorchCol; uniform float uWater, uSnow, uT; uniform vec4 uTorch[24];
float h2(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
float vn(vec2 p){ vec2 i=floor(p), f=fract(p); f=f*f*(3.-2.*f); return mix(mix(h2(i),h2(i+vec2(1,0)),f.x), mix(h2(i+vec2(0,1)),h2(i+vec2(1,1)),f.x), f.y); }
float fbm(vec2 p){ float s=0., a=.5; for(int i=0;i<4;i++){ s+=a*vn(p); p*=2.03; a*=.5; } return s; }
void main() {`).replace('#include <color_fragment>', `#include <color_fragment>
  vec2 q = vW.xz;
  float n1 = fbm(q*0.018); float n2 = fbm(q*0.07 + 13.0); float n3 = fbm(q*0.22 + 7.0);
  vec3 g = mix(cA, cB, smoothstep(0.38, 0.68, n1));
  g = mix(g, cC, smoothstep(0.55, 0.8, n2) * 0.75);
  g = mix(g, cD, smoothstep(0.72, 0.9, n1) * 0.5);
  float tile = h2(floor(q * 2.0));
  g *= 0.93 + tile * 0.07 + (n3 - 0.5) * 0.08;
  if (uWater > 0.5) {
    float wn = fbm(q*0.025 + 40.0);
    float wm = smoothstep(0.6, 0.625, wn);
    vec3 wc = cW * (0.86 + 0.14*sin(q.x*0.9 + uT*1.4)*sin(q.y*0.8 - uT*1.1));
    wc += step(0.992, h2(floor(q*4.0) + floor(uT*3.0))) * 0.5;
    g = mix(g, g*0.78, smoothstep(0.57, 0.6, wn) * (1.0 - wm));
    g = mix(g, wc, wm);
  }
  if (uSnow > 0.0) g += step(0.994, h2(floor(q*8.0))) * 0.3 * uSnow;
  diffuseColor.rgb = g;`).replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  vec3 tl = vec3(0.0);
  for (int i = 0; i < 24; i++) { vec4 t = uTorch[i]; if (t.w <= 0.0) continue; float d = distance(vW.xz, t.xy); float f = 1.0 - smoothstep(0.0, t.z, d); tl += f * f * t.w; }
  totalEmissiveRadiance += diffuseColor.rgb * uTorchCol * tl;`);
  groundDetail.injectFlat(sh);
};
const ground = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), groundMat);
ground.rotation.x = -Math.PI / 2; ground.scale.set(8000, 8000, 1); ground.receiveShadow = true;
scene.add(ground);

/* --- terrain: terraced diorama (src/world/terrain.js) instead of the flat field; 'flach' keeps the classic plane --- */
// per-style colours for cliffs, paths, banks and water (grass comes from style.ground, snow from style.snow)
const TERRAIN_PAL = {
  gold: { rock: [0xb08a5a, 0x9a7650, 0xc49a68, 0x86684a], path: 0xd8b070, pathEdge: 0xc29a5c, sand: 0xe8c890, water: 0x5a9cc0, waterDeep: 0x2f6688 },
  nacht: { rock: [0x55606e, 0x485260, 0x626d7a, 0x3c4552], path: 0x6a6650, pathEdge: 0x5a5644, sand: 0x6c6a58, bed: 0x26302a, water: 0x2c5f8c, waterDeep: 0x173a5c, foam: 0xa8c0d8 },
  neon: { rock: [0x5a3a6e, 0x4a2e5c, 0x6e4a82, 0x3c2550], path: 0x2f7f6a, pathEdge: 0x276a58, sand: 0x7ad3a0, bed: 0x1f4a4a, waterDeep: 0x1d5f8a, foam: 0xc8fff0 },
  wueste: { rock: [0xd2a46a, 0xc0905a, 0xe0b67c, 0xb07e4c], path: 0xc9a36a, pathEdge: 0xb48e58, sand: 0xf3e6c8, bed: 0x8a7a5a, water: 0x4aa8c8, waterDeep: 0x2a7a9a },
  dorf: { rock: [0xd8c8a8, 0xc4b494, 0xe6d8bc, 0xb0a080], path: 0xd9b98a, pathEdge: 0xc4a474 },
  comic: { water: 0x3cb4ff, waterDeep: 0x1f7fd0 },
  film: { rock: [0x7a7468, 0x6a645a, 0x868074, 0x5c574e], path: 0x8a7e66, pathEdge: 0x786c56, sand: 0x9a9078, bed: 0x3e443a, water: 0x5a7a88, waterDeep: 0x34505c, foam: 0xd0d6d8 },
};
const terrainPalette = (key, st) => ({ ...paletteFromStyle(st), ...(TERRAIN_PAL[key] || {}) });
const PLANE_Y = -2.32; // the flat plane sinks below the terrain skirt and becomes the table the diorama stands on
let terrainChoice = 'flach', mapSeed = 0, terrain = null, terrainKey = 'flach', sentTerrainKey = null, terrainVis = null, terrainVisKey = '';
try { const k = localStorage.getItem('aw-terrain'); if (k && TERRAIN_PRESETS[k]) terrainChoice = k; } catch (e) { }
const terrainMat = new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: TOON_GRAD });
terrainMat.onBeforeCompile = sh => {
  sh.uniforms.uTorch = GU.uTorch; sh.uniforms.uTorchCol = GU.uTorchCol;
  sh.vertexShader = sh.vertexShader.replace('void main() {', 'varying vec3 vW;\nvoid main() {').replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
  sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'varying vec3 vW; uniform vec3 uTorchCol; uniform vec4 uTorch[24];\nvoid main() {').replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  vec3 tl = vec3(0.0);
  for (int i = 0; i < 24; i++) { vec4 t = uTorch[i]; if (t.w <= 0.0) continue; float d = distance(vW.xz, t.xy); float f = 1.0 - smoothstep(0.0, t.z, d); tl += f * f * t.w; }
  totalEmissiveRadiance += diffuseColor.rgb * uTorchCol * tl;`);
  groundDetail.injectTerrain(sh);
};
terrainMat.customProgramCacheKey = () => 'terrain-toon-gd';
const crater = createCrater({ THREE, scene, terrainMat, groundMat, toonGrad: TOON_GRAD, terrain: () => terrain, env: () => envMeshes, groundH: (x, z) => groundH(x, z), resetup: () => setupBattle(readGroups(), true) }); // feature:crater
/** Ground height at a world position: terrain (inside the map), the sunken plane (outside), 0 on the flat field. */
function groundH(x, z) { return terrain ? (inBounds(terrain, x, z) ? heightAt(terrain, x, z) : PLANE_Y) : crater.flatH(x, z); } // feature:crater (flat field: crater floor)
// is (x,z) a good spot for a prop with a footprint of rc cells around it (flat, dry, no path or ramp)?
function propSpot(x, z, rc) {
  if (!terrain || !inBounds(terrain, x, z)) return true;
  const n = terrain.n, c = cellAt(terrain, x, z), gx = c % n, gz = (c / n) | 0, L = terrain.level[c];
  for (let dz = -rc; dz <= rc; dz++) for (let dx = -rc; dx <= rc; dx++) {
    const x2 = gx + dx, z2 = gz + dz; if (x2 < 0 || z2 < 0 || x2 >= n || z2 >= n) return false;
    const j = z2 * n + x2;
    if (terrain.water[j] || terrain.path[j] || terrain.rampDir[j] >= 0 || terrain.level[j] !== L) return false;
  }
  return true;
}
// (re)build the terrain mesh for the current terrain + style (palette is baked into vertex colours)
function refreshTerrainVis() {
  const vk = terrainKey + '|' + styleKey;
  if (vk === terrainVisKey) return;
  terrainVisKey = vk;
  if (terrainVis) { terrainVis.dispose(); terrainVis = null; }
  ground.position.y = terrain ? PLANE_Y : 0; gplane.constant = terrain ? -PLANE_Y : 0;
  if (!terrain) { groundDetail.bind(null); return; }
  terrainVis = buildTerrainMesh(terrain, THREE, { palette: terrainPalette(styleKey, style), material: terrainMat, uTime, edgeStrips: false });
  terrainVis.terrainMesh.receiveShadow = true; terrainVis.terrainMesh.castShadow = true;
  terrainVis.waterMesh.material.depthWrite = false; noOutline(terrainVis.waterMesh.material); // lines stop at the water surface
  scene.add(terrainVis.group);
  groundDetail.bind(terrainVis.detail);
  crater.bind(terrainVis); // feature:crater
}
// terrain = preset choice + map seed (taken from the battle seed when the map is first needed; kept across rematches)
function ensureTerrain() {
  crater.reset(); // feature:crater (a new battle starts on untouched ground)
  if (terrainChoice !== 'flach' && !mapSeed) mapSeed = battleSeed;
  const key = terrainChoice === 'flach' ? 'flach' : terrainChoice + ':' + mapSeed;
  if (key !== terrainKey) { terrainKey = key; terrain = terrainForPreset(terrainChoice, mapSeed); envKey = ''; }
  refreshTerrainVis();
}
// setup-message fields: the terrain itself only when it changed since the last setup (the worker keeps it)
function terrainMsg() {
  const m = { terrainSpec: { preset: terrainChoice, seed: terrain ? mapSeed : 0 } };
  if (sentTerrainKey !== terrainKey) { sentTerrainKey = terrainKey; m.terrain = workerTerrain(terrain); }
  return m;
}
// heightfield ray march (exact against the terrain heights, no triangle raycast): first hit or false
const _rp = new THREE.Vector3();
function rayTerrain(r, out) {
  const o = r.origin, d = r.direction; if (d.y > -1e-4) return false;
  const top = terrain.levels * terrain.stepHeight + 0.5, bot = PLANE_Y - 0.5;
  let t = o.y > top ? (top - o.y) / d.y : 0;
  const tEnd = Math.min((bot - o.y) / d.y, t + 6000), step = 0.35;
  let prev = t;
  for (let k = 0; k < 4000 && t <= tEnd; k++, prev = t, t += step) {
    r.at(t, _rp);
    if (_rp.y > groundH(_rp.x, _rp.z)) continue;
    let a = prev, b = t;
    for (let it = 0; it < 10; it++) { const m = (a + b) / 2; r.at(m, _rp); if (_rp.y > groundH(_rp.x, _rp.z)) a = m; else b = m; }
    r.at(b, out); out.y = groundH(out.x, out.z);
    return true;
  }
  return false;
}

const grassMat = noOutline(new THREE.MeshLambertMaterial({ vertexColors: true }));
grassMat.onBeforeCompile = sh => {
  sh.uniforms.uTime = uTime;
  sh.vertexShader = sh.vertexShader.replace('void main() {', 'uniform float uTime;\nvoid main() {').replace('#include <begin_vertex>', `#include <begin_vertex>
  vec4 wp0 = instanceMatrix * vec4(0.,0.,0.,1.);
  float sway = sin(uTime*1.7 + wp0.x*0.35 + wp0.z*0.27) * 0.07 + sin(uTime*3.1 + wp0.x) * 0.02;
  transformed.x += sway * position.y * 2.2; transformed.z += sway * position.y * 1.2;`);
};
function mergeGeos(list) {
  const pos = [], nor = [], col = [], idx = []; let off = 0;
  for (const g of list) {
    const gi = g.index ? g.index.array : null; const p = g.getAttribute('position'), n = g.getAttribute('normal'), c = g.getAttribute('color');
    for (let i = 0; i < p.count; i++) { pos.push(p.getX(i), p.getY(i), p.getZ(i)); nor.push(n.getX(i), n.getY(i), n.getZ(i)); col.push(c.getX(i), c.getY(i), c.getZ(i)); }
    if (gi) for (const k of gi) idx.push(k + off);
    off += p.count;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3)); g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3)); g.setIndex(idx);
  return g;
}
function tuftGeo(lo, hi, hMul) {
  const parts = [];
  const blades = [[-0.07, 0, 0.42], [0.05, 0.03, 0.34], [0.0, -0.06, 0.5], [0.09, -0.04, 0.27], [-0.03, 0.08, 0.3]];
  const cLo = new THREE.Color(lo), cHi = new THREE.Color(hi);
  for (const [x, z, h0] of blades) {
    const h = h0 * hMul;
    const b = new THREE.BoxGeometry(0.05, h, 0.05); b.translate(x, h / 2, z);
    const c = []; const pa = b.getAttribute('position');
    for (let i = 0; i < pa.count; i++) { const t = pa.getY(i) / h; tmpC.copy(cLo).lerp(cHi, t); c.push(tmpC.r, tmpC.g, tmpC.b); }
    b.setAttribute('color', new THREE.Float32BufferAttribute(c, 3));
    parts.push(b);
  }
  return mergeGeos(parts);
}
function makeEnvMat(toon) {
  const m = toon ? new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: TOON_GRAD }) : new THREE.MeshLambertMaterial({ vertexColors: true });
  m.onBeforeCompile = sh => {
    sh.vertexShader = sh.vertexShader.replace('void main() {', 'attribute float aEmit; varying float vEmit;\nvoid main() {\nvEmit = aEmit;');
    sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'varying float vEmit;\nvoid main() {').replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += diffuseColor.rgb * vEmit * 2.4;');
  };
  m.customProgramCacheKey = () => 'env' + (toon ? 't' : '');
  return m;
}
let envMat = makeEnvMat(true);
const shadeHex = (c, f) => { const r = Math.min(255, ((c >> 16) & 255) * f) | 0, g = Math.min(255, ((c >> 8) & 255) * f) | 0, b = Math.min(255, (c & 255) * f) | 0; return (r << 16) | (g << 8) | b; };
function snowCap(m) {
  const add = [];
  for (const [k, v] of m.map) { const [x, y, z] = DEC(k); if (!m.map.has(KEY(x, y + 1, z)) && hash3(x, y, z) > 0.2) add.push([k, v]); }
  for (const [k, v] of add) m.map.set(k, { c: 0xf4f8fc, p: v.p, e: 0 });
}
function propModel(kind, pal, seed, opt = {}) {
  const R = rng(seed); const m = new VoxModel(0.3);
  const pc = (x, y, z) => pal[(hash3(x + seed, y, z) * pal.length) | 0];
  const bark = (x, y, z) => hash3(x, y, z) > 0.5 ? 0x6b4a2b : 0x5a3d22;
  if (kind === 'round') {
    m.box(-1, 0, -1, 0, 6, 0, bark); m.sphere(0, 8 + ((R() * 2) | 0), 0, 3.6 + R() * 1.2, pc);
    for (let k = 0; k < 3; k++) m.sphere(((R() * 6) | 0) - 3, 7 + ((R() * 5) | 0), ((R() * 6) | 0) - 3, 2.2 + R() * 1.2, pc);
  } else if (kind === 'pine') {
    m.box(-1, 0, -1, 0, 4, 0, bark); const H = 13 + ((R() * 5) | 0);
    for (let y = 3; y < H; y += 2) m.sphere(0, y, 0, Math.max(0.9, (H - y) * 0.4), pc, 0.45);
  } else if (kind === 'cypress') {
    m.box(-1, 0, -1, 0, 2, 0, bark); m.sphere(0, 8, 0, 2.0 + R() * 0.5, pc, 3.0);
  } else if (kind === 'pillar') {
    m.vs = 0.32; const h = 6 + ((R() * 12) | 0), w = 1 + ((R() * 2) | 0); const col = pal[(R() * pal.length) | 0];
    m.box(0, 0, 0, 0, 3, 0, 0x3a2a30);
    m.box(-w, 3, -w, w, 3 + h, w, (x, y, z) => shadeHex(col, 0.72 + 0.32 * ((y - 3) / h) + (hash3(x, y, z) - 0.5) * 0.08));
  } else if (kind === 'bush') {
    for (let k = 0; k < 3; k++) m.sphere(((R() * 4) | 0) - 2, 1 + ((R() * 2) | 0), ((R() * 4) | 0) - 2, 1.8 + R() * 1.2, pc, 0.8);
  } else if (kind === 'dead') {
    m.box(-1, 0, -1, 0, 9, 0, pc);
    for (let b = 0; b < 5; b++) {
      const y0 = 4 + ((R() * 6) | 0), dx = R() < 0.5 ? -1 : 1, dz = R() < 0.5 ? -1 : 1, L = 3 + ((R() * 3) | 0);
      for (let k = 1; k <= L; k++) m.set(dx * k, y0 + ((k * 0.6) | 0), dz * ((k * 0.5) | 0), pc(k, y0, b));
    }
  } else if (kind === 'rock') {
    m.sphere(0, 1, 0, 2.4 + R() * 1.2, pc, 0.6); m.sphere(((R() * 4) | 0) - 2, 1, ((R() * 4) | 0) - 2, 1.6 + R(), pc, 0.6);
  } else if (kind === 'ruin') {
    const L = 6 + ((R() * 8) | 0);
    for (let x = 0; x < L; x++) { const h = 1 + ((R() * 7) | 0); m.box(x, 0, 0, x, h, 1, pc); }
    for (let k = 0; k < 4; k++) { const x = ((R() * L) | 0), z = 3 + ((R() * 3) | 0); m.box(x, 0, z, x + 1, 0, z + 1, pc); }
  } else if (kind === 'house') {
    m.vs = 0.35; const w = 3 + ((R() * 3) | 0), d = 3 + ((R() * 2) | 0), h = 5 + ((R() * 4) | 0);
    m.box(-w, 0, -d, w, h, d, (x, y, z) => hash3(x, y, z) > 0.75 ? pal[2] : pal[0]);
    for (let x = -w + 1; x < w; x += 3) for (let y = 2; y < h - 1; y += 3) { m.set(x, y, d, 0x3a4450); m.set(x, y, -d, 0x3a4450); }
    for (let z = -d + 1; z < d; z += 3) for (let y = 2; y < h - 1; y += 3) { m.set(w, y, z, 0x3a4450); m.set(-w, y, z, 0x3a4450); }
    m.box(0, 0, d, 0, 2, d, 0x7a4a2a);
    for (let k = 0; ; k++) { const x0 = -w - 1 + k, x1 = w + 1 - k; if (x0 > x1) break; m.box(x0, h + 1 + k, -d - 1, x1, h + 1 + k, d + 1, (x, y, z) => hash3(x, y, z) > 0.5 ? pal[1] : pal[3]); }
    m.box(w - 2, h + 2, -d + 1, w - 1, h + 5, -d + 2, pal[2]);
  } else if (kind === 'torch') {
    m.vs = 0.14;
    m.box(0, 0, 0, 1, 12, 1, (x, y, z) => hash3(x, y, z) > 0.5 ? 0x5a3d22 : 0x4a3220);
    m.box(-1, 13, -1, 2, 13, 2, 0x3a3a3a);
    m.glow(1).box(0, 14, 0, 1, 16, 1, 0xffb340).set(0, 17, 1, 0xffe08a).set(1, 17, 0, 0xff7a20).set(-1, 14, 0, 0xff7a20).set(2, 15, 1, 0xff9a30).glow(0);
  }
  for (const k of [...m.map.keys()]) if (DEC(k)[1] < 0) m.map.delete(k);
  if (opt.snow) snowCap(m);
  return m;
}
// outline width in metres, independent of the prop's voxel size (props use 0.3 m voxels, units ~0.07 m)
const ENV_OUTLINE_M = 0.1;
function envGeo(m) { const ctr = modelCenter(m); ctr.cx = 0; ctr.cz = 0; ctr.y0 = 0; return addOutline(meshAO(m, 0, ctr, 0.05, false, 1.1), m.vs, ENV_OUTLINE_M / m.vs); }
function rng(seed) { let s = seed >>> 0; return () => { s = (s + 0x6D2B79F5) | 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
function styleGeos(st) {
  if (st._geos) return st._geos;
  const props = st.props.map((d, di) => [0, 1, 2].map(v => envGeo(propModel(d.kind, d.pal, 101 + di * 17 + v * 5, { snow: d.snow }))));
  st._geos = { props, tuft: tuftGeo(st.grass.lo, st.grass.hi, st.grass.h), torch: st.torches ? envGeo(propModel('torch', [], 7)) : null };
  return st._geos;
}
function buildEnvironment(wd, Q, st) {
  for (const m of envMeshes) { scene.remove(m); m.dispose && m.dispose(); }
  envMeshes = []; torchPos = [];
  const G = styleGeos(st);
  const { W, ext, extZ } = wd;
  const R = rng(1234);
  const span = W + 120;
  const mat = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3(), ps = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
  const gc = new THREE.Color();
  const add = m => { scene.add(m); envMeshes.push(m); return m; };
  const ng = Math.round(Q.grass * st.grass.dens);
  if (ng > 0) {
    const gm = new THREE.InstancedMesh(G.tuft, grassMat, ng);
    for (let i = 0; i < ng; i++) {
      ps.set((R() * 2 - 1) * span, 0, (R() * 2 - 1) * span); q.setFromAxisAngle(up, R() * 6.28); const s = 0.7 + R() * 0.9; sc.set(s, s * (0.8 + R() * 0.6), s);
      if (terrain) { ps.y = groundH(ps.x, ps.z); if (!propSpot(ps.x, ps.z, 0)) sc.setScalar(0); } // no grass in water or on paths
      mat.compose(ps, q, sc); gm.setMatrixAt(i, mat); gc.setRGB(0.85 + R() * 0.25, 0.9 + R() * 0.2, 0.8 + R() * 0.2); gm.setColorAt(i, gc);
    }
    gm.receiveShadow = true; gm.renderOrder = 1; gm.userData.grass = true; add(gm);
  }
  if (st.flowers.length) {
    const nfl = Math.round(Q.grass / 6);
    const fm = new THREE.InstancedMesh(new THREE.BoxGeometry(0.12, 0.12, 0.12), noOutline(new THREE.MeshLambertMaterial()), nfl);
    for (let i = 0; i < nfl; i++) {
      ps.set((R() * 2 - 1) * span, 0.12 + R() * 0.2, (R() * 2 - 1) * span); q.identity(); sc.set(1, 1, 1);
      if (terrain) { ps.y += groundH(ps.x, ps.z); if (!propSpot(ps.x, ps.z, 0)) sc.setScalar(0); }
      mat.compose(ps, q, sc); fm.setMatrixAt(i, mat); fm.setColorAt(i, gc.setHex(st.flowers[(R() * st.flowers.length) | 0]));
    }
    fm.renderOrder = 1; fm.userData.grass = true; add(fm);
  }
  const outside = (pad, range) => { let x, z, tries = 0; do { x = (R() * 2 - 1) * (W + range); z = (R() * 2 - 1) * (W + range); tries++; } while (Math.abs(x) < ext + pad && Math.abs(z) < extZ + pad && tries < 60); return [x, z]; };
  // props hug the battlefield edge (dense near it, thinning outwards) so every style reads on screen
  const nearEdge = pad => {
    const d = pad + Math.pow(R(), 1.8) * 140;
    if (R() < (ext / (ext + extZ))) { const sx = R() < 0.5 ? -1 : 1; return [sx * (ext + d), (R() * 2 - 1) * (extZ + 40)]; }
    const sz = R() < 0.5 ? -1 : 1; return [(R() * 2 - 1) * (ext + 40), sz * (extZ + d)];
  };
  const qm = Q.trees / 40;
  st.props.forEach((d, di) => {
    const lists = [[], [], []];
    const nC = Math.max(1, Math.round(d.n * qm));
    for (let c = 0; c < nC; c++) {
      const [cx, cz] = R() < 0.8 ? nearEdge(d.kind === 'rock' ? 3 : 8) : outside(12, 160);
      const cnt = d.single ? 1 : (d.cluster ? d.cluster[0] + ((R() * (d.cluster[1] - d.cluster[0] + 1)) | 0) : 3 + ((R() * 8) | 0));
      const spread = d.spread || 14;
      const foot = d.kind === 'house' ? 2 : (d.kind === 'round' || d.kind === 'pine' || d.kind === 'cypress' || d.kind === 'ruin') ? 1 : 0;
      for (let i = 0; i < cnt; i++) {
        const rot = d.kind === 'house' ? ((R() * 4) | 0) * Math.PI / 2 : R() * 6.28;
        const s = d.kind === 'house' ? 0.9 + R() * 0.3 : 0.8 + R() * 0.7;
        const li = (R() * 3) | 0, px = cx + (R() - 0.5) * spread, pz = cz + (R() - 0.5) * spread;
        if (terrain && !propSpot(px, pz, foot)) continue; // terrain: only on flat dry ground, never on paths or ramps
        lists[li].push([px, pz, s, rot]);
      }
    }
    lists.forEach((l, v) => {
      if (!l.length) return;
      const im = new THREE.InstancedMesh(G.props[di][v], envMat, l.length);
      l.forEach(([x, z, s, r], i) => { mat.compose(ps.set(x, groundH(x, z), z), q.setFromAxisAngle(up, r), sc.set(s, d.kind === 'rock' ? s * 0.8 : s, s)); im.setMatrixAt(i, mat); });
      im.castShadow = Q.shadows > 0; im.receiveShadow = true; add(im);
    });
  });
  if (st.torches && G.torch) {
    const pts = [];
    const ex = ext + 9, ez = extZ + 9, stp = 15;
    for (let x = -ex; x <= ex; x += stp) { pts.push([x, -ez], [x, ez]); }
    for (let z = -ez + stp; z < ez; z += stp) { pts.push([-ex, z], [ex, z]); }
    for (let i = 0; i < 30; i++) pts.push(outside(6, 60));
    for (let i = 0; i < 26; i++) pts.unshift([(R() * 2 - 1) * ext * 0.9, (R() * 2 - 1) * extZ * 0.9]);
    let tp = pts.slice(0, 260);
    if (terrain) tp = tp.filter(([x, z]) => !inBounds(terrain, x, z) || isWalkable(terrain, x, z)); // no torches in the river
    const im = new THREE.InstancedMesh(G.torch, envMat, tp.length);
    tp.forEach(([x, z], i) => { mat.compose(ps.set(x, groundH(x, z), z), q.identity(), sc.set(1, 1, 1)); im.setMatrixAt(i, mat); });
    im.castShadow = false; add(im);
    torchPos = tp.map(([x, z]) => [x, z, Math.random() * 10, groundH(x, z)]);
  }
}

/* --- torch lighting: 24 light pools on the ground + 4 real point lights near the camera --- */
const torchLights = [0, 1, 2, 3].map(() => new THREE.PointLight(0xff9a3c, 0, 24, 1.4));
let torchNear = [], torchSortT = 0;
function updateTorches(now) {
  if (!style.torches || !torchPos.length) return;
  if (now - torchSortT > 300) {
    torchSortT = now;
    torchNear = torchPos.map(p => [p, (p[0] - camCur.tx) ** 2 + (p[1] - camCur.tz) ** 2]).sort((a, b) => a[1] - b[1]).slice(0, 24).map(a => a[0]);
  }
  const t = uTime.value; // feature:slowmo: flicker runs on game time
  for (let i = 0; i < 24; i++) {
    const v = GU.uTorch.value[i], p = torchNear[i];
    if (!p) { v.w = 0; continue; }
    const fl = 0.85 + 0.15 * Math.sin(t * 11 + p[2]) * Math.sin(t * 7.3 + p[2] * 2);
    v.set(p[0], p[1], 10, 1.6 * fl);
  }
  torchLights.forEach((l, i) => { const p = torchNear[i]; if (!p) { l.intensity = 0; return; } l.position.set(p[0], p[3] + 2.6, p[1]); l.intensity = 38 * (0.85 + 0.15 * Math.sin(t * 12 + p[2])); });
}

/* --- ambient particles: wrapped around the camera, fully on the GPU --- */
const AMB_TYPES = {
  snow: { vel: [0.4, -2.0, 0.25], sway: 0.6, size: 0.09 },
  dust: { vel: [2.6, 0.15, 1.0], sway: 0.5, size: 0.07, hMul: 0.5 },
  leaves: { vel: [1.4, -0.9, 0.7], sway: 1.1, size: 0.13, hMul: 0.8 },
  fireflies: { vel: [0, 0.05, 0], sway: 1.3, size: 0.08, h: 4, pulse: 1 },
  ash: { vel: [0.5, -0.7, 0.3], sway: 0.5, size: 0.07 },
  spores: { vel: [0.1, 0.35, 0.05], sway: 0.9, size: 0.08, hMul: 0.4, pulse: 1 },
};
let amb = null;
function buildAmbient(Qn) {
  if (amb) { scene.remove(amb.mesh); amb.mesh.geometry.dispose(); amb.mesh.material.dispose(); amb = null; }
  const a = style.amb; if (!a) return;
  const T0 = AMB_TYPES[a.type];
  const n = Math.round(a.n * Qn.ambMul);
  const box = new THREE.BoxGeometry(1, 1, 1);
  const g = new THREE.InstancedBufferGeometry();
  g.index = box.index; g.setAttribute('position', box.getAttribute('position'));
  const seed = new Float32Array(n * 4); for (let i = 0; i < seed.length; i++) seed[i] = Math.random();
  g.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 4)); g.instanceCount = n;
  const u = { uT: uTime, uO: { value: new THREE.Vector3() }, uBox: { value: new THREE.Vector3(100, 40, 100) }, uVel: { value: new THREE.Vector3(...T0.vel) }, uSway: { value: T0.sway }, uSize: { value: T0.size }, uPulse: { value: T0.pulse || 0 }, uDist: { value: 50 } };
  const m = new THREE.MeshBasicMaterial({ color: new THREE.Color(a.color[0], a.color[1], a.color[2]), fog: true });
  m.onBeforeCompile = sh => {
    Object.assign(sh.uniforms, u);
    sh.vertexShader = sh.vertexShader.replace('void main() {', 'uniform float uT, uSway, uSize, uPulse, uDist; uniform vec3 uO, uBox, uVel; attribute vec4 aSeed;\nvoid main() {')
      .replace('#include <begin_vertex>', `vec3 p = aSeed.xyz * uBox + uVel * uT * (0.7 + aSeed.w * 0.6);
  p.x += sin(uT * 1.3 + aSeed.w * 20.0) * uSway; p.z += cos(uT * 1.1 + aSeed.w * 13.0) * uSway;
  vec2 rel = mod(p.xz - uO.xz + uBox.xz * 0.5, uBox.xz) - uBox.xz * 0.5;
  float y = mod(p.y, uBox.y);
  float sc = uSize * max(1.0, uDist / 45.0) * (uPulse > 0.5 ? (0.3 + 0.7 * max(0.0, sin(uT * 2.6 + aSeed.w * 40.0))) : 1.0);
  vec3 transformed = position * sc + vec3(uO.x + rel.x, y + uO.y, uO.z + rel.y);`);
  };
  noOutline(m);
  const mesh = new THREE.Mesh(g, m); mesh.frustumCulled = false; mesh.renderOrder = 1;
  scene.add(mesh);
  amb = { mesh, u, T: T0 };
}
function updateAmbient() {
  if (!amb) return;
  const B = clamp(camCur.dist * 1.5, 40, 320);
  amb.u.uO.value.set(camCur.tx, terrain ? groundH(camCur.tx, camCur.tz) : 0, camCur.tz);
  amb.u.uBox.value.set(B, amb.T.h || clamp(camCur.dist * 0.9, 20, 140) * (amb.T.hMul || 1), B);
  amb.u.uDist.value = camCur.dist;
}

/* --- post-processing: grading, vignette, tilt-shift, pixelation (one cheap full-screen pass) --- */
const post = { rt: null, active: false, pixel: 0, scene: new THREE.Scene(), cam: new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1) };
post.mat = new THREE.ShaderMaterial({
  uniforms: { tD: { value: null }, uRes: { value: new THREE.Vector2(1, 1) }, uTilt: { value: 0 }, uVig: { value: 0 }, uSat: { value: 1 }, uCon: { value: 1 }, uTint: { value: new THREE.Vector3(1, 1, 1) }, ...outlineUniforms() },
  vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
  fragmentShader: `uniform sampler2D tD; uniform vec2 uRes; uniform float uTilt, uVig, uSat, uCon; uniform vec3 uTint; varying vec2 vUv;
${OUTLINE_GLSL}
void main(){
  vec4 c0 = texture2D(tD, vUv); vec3 col = c0.rgb;
  float ol = outlineAt(vUv, c0.a); // comic outline (src/render/outlinePass.js)
  if (ol > 0.0) col = mix(col, outlineColor(), ol);
  if (uOLOn > 1.5) { gl_FragColor = vec4(olDbg, 1.0); return; } // debug view: bend, silhouette, crease (bench &oldbg=1)
  if (uTilt > 0.0) {
    float f = abs(vUv.y - 0.55) * 2.0;
    float b = smoothstep(0.22, 1.0, f) * uTilt * 7.0;
    if (b > 0.05) { vec3 acc = col; for (int i = 1; i < 12; i++) { float a = float(i) * 2.39996; float r = sqrt(float(i)) / 3.3; acc += texture2D(tD, vUv + vec2(cos(a), sin(a)) * r * b / uRes).rgb; } col = acc / 12.0; col = mix(col, outlineColor(), ol * (1.0 - min(1.0, b * 0.35))); }
  }
  col *= uTint;
  float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = mix(vec3(l), col, uSat);
  col = max((col - 0.18) * uCon + 0.18, 0.0);
  vec2 d = vUv - 0.5; col *= 1.0 - uVig * dot(d, d) * 1.8;
  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`,
  depthTest: false, depthWrite: false,
});
post.scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), post.mat));
const _dbs = new THREE.Vector2();
function setupPost(Qn) {
  const p = style.post || {};
  post.pixel = p.pixel || 0;
  const U = post.mat.uniforms;
  U.uTilt.value = Qn.tilt === false ? 0 : (p.tilt || 0); U.uVig.value = p.vig || 0; U.uSat.value = p.sat || 1; U.uCon.value = p.con || 1;
  U.uTint.value.set(...(p.tint || [1, 1, 1]));
  post.active = true; // the outline pass needs the scene in post.rt (with depth) for every style
  resizePost(Qn);
}
function resizePost(Qn) {
  if (!post.active) { if (post.rt) { post.rt.dispose(); post.rt = null; } return; }
  renderer.getDrawingBufferSize(_dbs);
  let w = _dbs.x, h = _dbs.y;
  if (post.pixel) { w = Math.max(1, Math.round(innerWidth / post.pixel)); h = Math.max(1, Math.round(innerHeight / post.pixel)); }
  const samples = post.pixel ? 0 : (Qn.msaa || 0), key = w + 'x' + h + ':' + post.pixel + ':' + samples;
  if (!post.rt || post.rt._key !== key) {
    if (post.rt) { post.rt.depthTexture.dispose(); post.rt.dispose(); }
    const filt = post.pixel ? THREE.NearestFilter : THREE.LinearFilter;
    post.rt = new THREE.WebGLRenderTarget(w, h, { type: THREE.HalfFloatType, samples, minFilter: filt, magFilter: filt, depthTexture: new THREE.DepthTexture(w, h) });
    post.rt._key = key;
  }
  post.mat.uniforms.tD.value = post.rt.texture; post.mat.uniforms.uRes.value.set(w, h);
  post.mat.uniforms.tDepth.value = post.rt.depthTexture; post.olR = outlineRadius(renderer.getPixelRatio(), !!post.pixel);
  post.mat.uniforms.uOLCrease.value = Qn.creases === false ? 0 : 1;
}

/* --- projectiles & tracers --- */
const projMesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), noOutline(new THREE.MeshBasicMaterial()), 3000);
projMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); projMesh.frustumCulled = false; projMesh.count = 0; projMesh.renderOrder = 1;
projMesh.setColorAt(0, new THREE.Color(1, 1, 1));
scene.add(projMesh);
const TRMAX = 2500;
const trGeo = new THREE.BoxGeometry(1, 1, 1);
const trMat = noOutline(new THREE.MeshBasicMaterial({ color: 0xffe9a0 }));
const trT = new THREE.InstancedBufferAttribute(new Float32Array(TRMAX), 1); trT.array.fill(-1e6); trT.setUsage(THREE.DynamicDrawUsage);
trGeo.setAttribute('aT0', trT);
trMat.onBeforeCompile = sh => {
  sh.uniforms.uGT = uGTime;
  sh.vertexShader = sh.vertexShader.replace('void main() {', 'uniform float uGT; attribute float aT0;\nvoid main() {').replace('#include <begin_vertex>', 'vec3 transformed = position * step(uGT - aT0, 0.07) * step(0.0, uGT - aT0);');
};
const trMesh = new THREE.InstancedMesh(trGeo, trMat, TRMAX); trMesh.frustumCulled = false; trMesh.renderOrder = 1; trMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
scene.add(trMesh);
let trHead = 0, trLo = Infinity, trHi = -1;
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _v = new THREE.Vector3(), _s = new THREE.Vector3(), _e = new THREE.Euler();
function addTracer(x0, y0, z0, x1, y1, z1, w) {
  const i = trHead; trHead = (trHead + 1) % TRMAX;
  const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0, L = Math.hypot(dx, dy, dz) || 0.01;
  _e.set(-Math.atan2(dy, Math.hypot(dx, dz)), Math.atan2(dx, dz), 0, 'YXZ'); _q.setFromEuler(_e);
  _m.compose(_v.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2), _q, _s.set(w, w, L));
  trMesh.setMatrixAt(i, _m); trT.array[i] = uGTime.value;
  if (i < trLo) trLo = i; if (i > trHi) trHi = i;
}
function flushTracers() {
  if (trHi < 0) return;
  trMesh.instanceMatrix.clearUpdateRanges(); trMesh.instanceMatrix.addUpdateRange(trLo * 16, (trHi - trLo + 1) * 16); trMesh.instanceMatrix.needsUpdate = true;
  trT.clearUpdateRanges(); trT.addUpdateRange(trLo, trHi - trLo + 1); trT.needsUpdate = true;
  trLo = Infinity; trHi = -1;
}

/* --- selection rings --- */
const ringGeo = new THREE.RingGeometry(0.85, 1, 40); ringGeo.rotateX(-Math.PI / 2);
const hoverRing = new THREE.Mesh(ringGeo, noOutline(new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, depthWrite: false })));
const possRing = new THREE.Mesh(ringGeo, noOutline(new THREE.MeshBasicMaterial({ color: 0xffc93c, transparent: true, opacity: 0.95, depthWrite: false })));
hoverRing.visible = possRing.visible = false; hoverRing.renderOrder = possRing.renderOrder = 2;
scene.add(hoverRing, possRing);

/* ---------------- quality ---------------- */
// every preset renders at the native pixel ratio (sharp on HiDPI/Retina); performance comes from effects and LODs
const NATIVE_PR = () => Math.min(devicePixelRatio || 1, 3);
// impDist 0 everywhere: far units stay animated 3D meshes (user: sprite impostors looked pixelated and had no attack
// animations). src/render/impostors.js stays for experiments (?bench=1 &imp=…).
const QUAL = {
  low: { ambMul: 0.4, pr: NATIVE_PR(), tilt: false, shadows: 0, lod0: 90, lod0Tris: 400e3, lod0Dist: 22, impDist: 0, grass: 2500, trees: 22, debris: 9000, fx: 2500, debrisMul: 0.45 },
  medium: { ambMul: 1, pr: NATIVE_PR(), shadows: 1024, lod0: 350, lod0Tris: 1.5e6, lod0Dist: 38, impDist: 0, grass: 9000, trees: 40, debris: 26000, fx: 5000, debrisMul: 0.8 },
  high: { ambMul: 1.4, pr: NATIVE_PR(), msaa: devicePixelRatio >= 2 ? 0 : 4, shadows: 2048, lod0: 1400, lod0Tris: 4e6, lod0Dist: 70, impDist: 0, grass: 22000, trees: 60, debris: 50000, fx: 9000, debrisMul: 1 },
};
let qMode = 'auto';
try { qMode = localStorage.getItem('aw-quality') || 'auto'; } catch (e) { }
$('#quality').value = qMode;
const weak = COARSE || (navigator.hardwareConcurrency || 4) <= 4;
let Q = QUAL[qMode === 'auto' ? (weak ? 'low' : 'medium') : qMode];
let pr = Q.pr;
let debris, fx;
function applyQuality() {
  pr = Q.pr; renderer.setPixelRatio(pr);
  renderer.shadowMap.enabled = Q.shadows > 0; sun.castShadow = Q.shadows > 0;
  if (Q.shadows) { sun.shadow.mapSize.set(Q.shadows, Q.shadows); if (sun.shadow.map) { sun.shadow.map.dispose(); sun.shadow.map = null; } }
  if (debris) { scene.remove(debris.mesh); debris.mesh.geometry.dispose(); }
  if (fx) { scene.remove(fx.mesh); fx.mesh.geometry.dispose(); }
  debris = makeParticles(Q.debris, true); fx = makeParticles(Q.fx, false);
  scene.add(debris.mesh, fx.mesh);
  buildAmbient(Q); setupPost(Q);
  groundDetail.enabled = !gdOff;
  scene.traverse(o => { if (o.material) o.material.needsUpdate = true; });
}
applyQuality();
/* "Automatisch": give up effects first, resolution last (src/render/autoQuality.js) */
function setShadows(on) {
  if (renderer.shadowMap.enabled === on) return;
  renderer.shadowMap.enabled = on; sun.castShadow = on;
  if (on) sun.shadow.mapSize.set(Q.shadows, Q.shadows);
  for (const A of armies) if (A) { A.lod0.castShadow = A.lod1.castShadow = on; if (A.imp) A.imp.mesh.castShadow = on; }
  bLayer.shadows(on);
  scene.traverse(o => { if (o.material) o.material.needsUpdate = true; });
}
let autoPr = 0; // a forced pixel ratio (?bench=1&dpr=…); 0 = native
function applyAutoLevel(l) {
  const B = QUAL[weak ? 'low' : 'medium'], taken = k => l > AUTO_STEPS.indexOf(k), lod = taken('lod2') ? 0.4 : taken('lod') ? 0.6 : 1;
  Q = { ...B, tilt: taken('tilt') ? false : B.tilt, shadows: taken('shadows') ? 0 : B.shadows, lod0Dist: B.lod0Dist * lod, impDist: B.impDist * lod,
    debrisMul: B.debrisMul * (taken('fx') ? 0.5 : 1), creases: !taken('creases') };
  setShadows(Q.shadows > 0);
  for (const m of envMeshes) if (m.userData.grass) m.visible = !taken('grass');
  groundDetail.enabled = !gdOff && !taken('ground');
  pr = autoPr || NATIVE_PR(); // resolution is never lowered
  renderer.setPixelRatio(pr); setupPost(Q);
}
const autoQ = createAutoQuality(applyAutoLevel);

/* ---------------- worker ---------------- */
let worker = new SimWorker(); // swapped for a second worker while a clip is rendered (see the replay section)
// feature setup fields that must reach every multiplayer client: each entry returns a plain JSON object
const setupExtras = [];
// feature:mutators — odd rules (moon gravity, chain explosions, mini/giant armies, sprint), a card in the world options;
// off where balance was measured (bets, campaign, daily battle, defence). Lazy lookups: those features are created later.
const mutators = createMutators({
  active: () => { try { return !betNeutral && !campaign.active && !waves.on; } catch { return true; } },
  onChange: () => { if (simState === 'setup') { setupBattle(readGroups(), placeMode === 'paint'); if (placeMode === 'paint') rebuildHash(); } },
});
setupExtras.push(mutators.msg);
// feature:bet — bet battles run on neutral ground (flat, clear sky, no infection, zones or craters): the odds were
// measured that way (src/features/betPool.js), world options must not tilt a bet
let betNeutral = null; // { terrainChoice, mapSeed } of the sandbox while a bet session is open
const collectExtras = () => {
  const o = Object.assign({}, ...setupExtras.map(f => f()));
  if (betNeutral) { o.weatherChoice = 'klar'; o.crater = false; delete o.zones; delete o.convert; }
  return o;
};
// feature:weather — adds the resolved weather to every setup message; panel choice, GPU rain/snow, lightning, HUD
const weatherFx = createWeather({ scene, camera, worker, sky: SKY, hemi, sun, uTime, uGTime, view: () => camCur, groundH, quality: () => Q, fx: () => fx, debris: () => debris, shake,
  getArmies: () => armies, styleKey: () => styleKey, isSetup: () => simState === 'setup',
  onChange: () => { setupBattle(readGroups(), placeMode === 'paint'); if (placeMode === 'paint') rebuildHash(); } });
setupExtras.push(weatherFx.extras); // feature:weather
setupExtras.push(crater.msg); // feature:crater: { crater: on } (multiplayer: the host's option)
let gen = 0, N = 0, armies = [], world = { W: 200, ext: 100, extZ: 100 }, worldReady = false;
let prevB = null, curB = null, tCur = 0, frameMs = 1000 / 30;
let simState = 'setup', paused = false, timeScale = 1, kino = false;
let lastInfo = { alive: [0, 0], hp: [1, 1], over: false, winner: -1, t: 0 }, hp0 = [1, 1];
let possessed = -1, pInfo = null, overShown = false, deathSwitchAt = 0;
// first-person view of the possessed unit (toggle V / button / scroll in; remembered): eye at the unit's head, mouse look
let fpv = (() => { try { return localStorage.getItem('aw-fpv') === '1'; } catch { return false; } })(), fpvPitch = -0.08, lockLostAt = 0;
/* sound + weapon/tool VFX (src/audio, src/fx): fed from the worker events below */
const sfx = createBattleAudio({ camera, weapons: WEAPONS, fxHints: ERA_WEAPON_FX, armies: () => armies });
const wfx = createWeaponFx({ scene, uGTime, parts: () => ({ fx, debris }), groundH, explode: (x, y, z, aoe, kind) => spawnExplosion(x, y, z, aoe, kind), onImpact: (x, y, z, aoe) => sfx.impact(x, y, z, aoe),
  armies: () => armies, teams: TEAMS, fxHints: ERA_WEAPON_FX, shake: (a, x, z) => shake(a, x, z), gen: () => gen });
mountSoundPanel(sfx.engine);
// feature:mount — live view of renderer state for src/features/mount.js (getters: these are reassigned at runtime)
const mountCtx = { scene, camera, groundH, toast, makeUnitMaterial, get Q() { return Q; }, get debris() { return debris; }, get fx() { return fx; }, get curB() { return curB; }, get split() { return split; }, get GRP() { return GRPc; }, get N() { return N; }, get possessed() { return possessed; }, get rState() { return rState; }, get fpv() { return fpv && !capture; },
  blob: (x, g, z, r, y) => { if (Q.shadows === 0 && blobs) blobs.add(x, g, z, r, y); } }; // low quality: blob shadows instead of shadow maps
// feature:stats — battle statistics (live box, "Statistik ansehen", camera to the deadliest unit)
const battleStats = createBattleStats({
  armies: () => armies, terrain: () => terrain, buildings: () => bSim,
  focusUnit: i => {
    const s = unitState(i, {}); if (!s || !s.alive) return false;
    if (possessed >= 0) setPossess(-1);
    kino = false; syncKino();
    camGoal.tx = s.x; camGoal.tz = s.z; camGoal.dist = Math.min(camGoal.dist, 26);
    return true;
  },
});

// the close-up LOD is a detail-2 model (~4x the triangles): cap how many units use it per army
const lod0Cap = geo => Math.max(8, Math.floor(Q.lod0Tris / Math.max(1, (geo.index ? geo.index.count : geo.attributes.position.count) / 3)));
function unitMeshes(A) {
  const { mat, mat0, depth } = makeUnitMaterial(A);
  A.mats = { mat, mat0, depth };
  const cap1 = (placeMode === 'paint' ? Math.max(256, A.stats.count * 2) : Math.max(1, A.stats.count)) + (A.reserve || 0), cap0 = Math.min(cap1, Q.lod0, Math.ceil(lod0Cap(A.geo0) / Math.max(1, armies.length - 1))); // mixed armies share the close-up budget (2 groups: as before)
  A.varS = new Float32Array(cap1); for (let i = 0; i < cap1; i++) A.varS[i] = A.stats.s * (0.955 + ((i * 0.618034) % 1) * 0.09);
  const mk = (geo, cap, mat) => {
    const g = geo.clone();
    const anim = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); anim.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('aAnim', anim);
    const im = new THREE.InstancedMesh(g, mat, cap); im.instanceMatrix.setUsage(THREE.DynamicDrawUsage); im.frustumCulled = false;
    im.customDepthMaterial = depth; im.castShadow = Q.shadows > 0; im.receiveShadow = Q.shadows > 0; im.count = 0;
    return im;
  };
  A.lod0 = mk(A.geo0, cap0, mat0); A.lod1 = mk(A.geo1, cap1, mat);
  // comic outlines come from the screen-space pass (src/render/outlinePass.js); no hull meshes any more
  convert.meshes(A); // feature:convert
  scene.add(A.lod0, A.lod1);
  // far units: camera-facing sprite impostors baked from the near LOD (src/render/impostors.js)
  A.imp = Q.impDist ? makeImpostorMesh(bakeImpostor(renderer, A, ANIM_HEAD), cap1, TOON_GRAD) : null;
  if (A.imp) { A.imp.mesh.castShadow = Q.shadows > 0; scene.add(A.imp.mesh); } // receiving would self-shadow the flat sprite
  if (A.mount) mountMeshes(A, mountCtx); // feature:mount
}
function disposeArmy(A) { if (!A) return; if (A.imp) { scene.remove(A.imp.mesh); A.imp.dispose(); } for (const m of [A.lod0, A.lod1]) { scene.remove(m); m.geometry.dispose(); m.dispose(); } A.mats.mat.dispose(); A.mats.mat0.dispose(); A.mats.depth.dispose(); }
function refreshUnitMaterials() {
  for (const A of armies) {
    if (!A) continue;
    const old = A.mats; A.mats = makeUnitMaterial(A);
    for (const m of [A.lod0, A.lod1]) { m.material = m === A.lod0 ? A.mats.mat0 : A.mats.mat; m.customDepthMaterial = A.mats.depth; }
    old.mat.dispose(); old.mat0.dispose(); old.depth.dispose();
  }
}

function setupBattle(cfgs, keepCam, rp) { // rp = { seed, sim }: a replay's recorded setup (sent to the worker as is)
  if (waves.on && !rp) waves.prepare(cfgs); // feature:waves
  // unit groups (mixed armies, src/game/groups.js): classic [blue, red] configs are one troop per side
  const gs = normalizeGroups(cfgs).map(c => (rp ? c : mutators.cfg(c))); // feature:mutators: mini / giant armies
  if (placeMode === 'paint') { const pc = paintGroups(gs, paint, paintS).counts; gs.forEach((c, g) => { c.count = pc[g]; }); }
  mountDispose(armies, mountCtx); // feature:mount
  armies.forEach(disposeArmy);
  armies = gs.map(c => { const A = buildArmy(c.team, c); A.slot = c.slot; if (!rp) mutators.stats(A.stats); return A; }); // feature:mutators: sprint
  mountArmies(armies, gs); // feature:mount
  const bm = bldSetup();
  if (rp) { bm.reserve = rp.sim.reserve || [0, 0]; bSim = rp.sim.buildings || []; bLayer.set(bSim); convert.adopt(rp.sim.convert || null, armies); } // feature:convert: the recorded options
  fm.reset(); // feature:formation: every army starts with "advance"
  if (waves.on && !rp) waves.extend(bm); // feature:waves
  applyRanges(bm.reserve);
  armies.forEach(unitMeshes);
  autoQ.hold(); // rebuild + impostor bake + shader compiles are not a slow GPU
  gen++; prevB = curB = null;
  simState = 'setup'; paused = false; overShown = false; possessed = -1; pInfo = null;
  battleStats.reset(); // feature:stats
  setPossess(-1, true);
  debris.clear(); fx.clear(); thrown.reset(); // feature:thrown
  suppressFrame = !!keepCam;
  battleSeed = rp ? rp.seed : betNeutral && betNeutral.seed ? betNeutral.seed : ((Math.random() * 2147483646) | 0) + 1; // feature:bet: the pool's seed
  ensureTerrain();
  if (rp) { sentTerrainKey = terrainKey; postSetup({ cmd: 'setup', gen, ...rp.sim, terrain: workerTerrain(terrain) }, true); }
  else postSetup({ cmd: 'setup', gen, seed: battleSeed, armies: groupStats(), positions: paintPositions(), minW: placeMode === 'paint' || placedB.length ? ARENA.W : 0, ...bm, ...terrainMsg() });
  if (placeMode === 'paint') localBuffer();
  resetTotals();
  updateCountsUI(); updateUIState();
}
// group ranges like the worker (reserve slots on each side's last group), the first index of team 1 and every unit's group
function applyRanges(reserve) {
  armies.forEach(A => { A.reserve = 0; });
  for (let t = 0; t < 2; t++) { const gs = armies.filter(A => A.team === t); if (gs.length) gs[gs.length - 1].reserve = reserve[t] | 0; }
  const R = groupRanges(armies.map(A => A.team), armies.map(A => A.stats.count), reserve);
  armies.forEach((A, g) => { A.start = R.start[g]; A.resStart = R.resStart[g]; A.end = R.end[g]; });
  split = R.split; N = R.N; GRPc = R.GRP;
}
// worker setup: every group's stats with its side (one group per side = the classic message plus `team`)
const groupStats = () => armies.map(A => ({ ...A.stats, team: A.team }));
const teamSum = (t, f) => armies.reduce((s, A) => s + (A.team === t ? f(A) : 0), 0);
function resetTotals() {
  hp0 = [0, 1].map(t => Math.max(1, teamSum(t, A => A.stats.hp * A.stats.count)));
  lastInfo = { alive: [0, 1].map(t => teamSum(t, A => A.stats.count)), hp: hp0.slice(), t: 0, over: false, winner: -1 };
}
const teamArmies = t => armies.filter(A => A && A.team === t);
const firstOf = t => armies.find(A => A && A.team === t) || null;
// the group the paint brush sets (team brushTeam, its selected troop; first troop if that slot is not in the battle)
const brushSlotNow = () => (troops[brushTeam][troopSel[brushTeam]] || { slot: 0 }).slot; // the selected troop of the brush side
const brushGroup = () => armies.find(A => A.team === brushTeam && A.slot === brushSlotNow()) || firstOf(brushTeam);
const radiusAt = (t, s) => { const A = armies.find(G => G.team === t && G.slot === s) || firstOf(t); return A ? A.stats.r : 0.5; };
/* ---------------- placement: painted armies ---------------- */
const ARENA = { W: 230, ext: 140, extZ: 140 };
let battleSeed = 1;
let split = 0; // first unit index of team 1 (team 0 = its groups + spawn reserve)
let GRPc = new Uint8Array(0); // group (index into armies) of every unit slot, see applyRanges
let placeMode = 'count', brushTeam = 0, brushN = 1, painting = false, erasing = false, paintDirty = false, lastSync = 0;
let eraser = false; // paint tool: false = brush, true = eraser (button or X; Shift + drag erases for one stroke)
let lastStampT = 0, lastStampX = 0, lastStampZ = 0, suppressFrame = false, envKey = '';
const paint = [[], []];
const paintS = [[], []]; // troop slot (0-3) of every painted position, parallel to paint[t] (src/game/groups.js)
let paintUnit = [new Int32Array(0), new Int32Array(0)]; // unit index of every painted position
// painted positions per group (worker order) and where each painted position ends up
function paintPositions() {
  if (placeMode !== 'paint') return null;
  const r = paintGroups(armies, paint, paintS, armies.map(A => A.start)); paintUnit = r.unitOf;
  return r.positions;
}
const envKeyOf = w => [Math.round(w.W / 8), Math.round(w.ext / 8), Math.round(w.extZ / 8), styleKey, Q.grass, terrainKey].join(':');
function localBuffer() {
  // show painted units instantly; the worker's first frame replaces this with identical data
  const n = paint[0].length / 2 + paint[1].length / 2, b = new Float32Array(Math.max(n, N) * 10);
  const cen = paint.map(a => { let sx = 0, sz = 0; const k = a.length / 2; for (let i = 0; i < k; i++) { sx += a[2 * i]; sz += a[2 * i + 1]; } return k ? [sx / k, sz / k] : [0, 0]; });
  paintPositions(); // paintUnit: where every painted unit sits
  for (let t = 0; t < 2; t++) {
    const a = paint[t], e = cen[1 - t], hasE = paint[1 - t].length > 0;
    for (let k = 0; k < a.length / 2; k++) {
      const i = paintUnit[t][k]; if (i < 0) continue;
      const o = i * 10, x = a[2 * k], z = a[2 * k + 1];
      b[o] = x; b[o + 1] = groundH(x, z); b[o + 2] = z; b[o + 3] = hasE ? Math.atan2(e[0] - x, e[1] - z) : (t ? -Math.PI / 2 : Math.PI / 2);
      b[o + 6] = (i * 2.399) % 6.283; b[o + 7] = -1; b[o + 9] = 1;
    }
  }
  prevB = curB = b; tCur = performance.now();
}
function regrowMeshes(A) {
  for (const m of [A.lod0, A.lod1]) scene.remove(m);
  if (A.imp) { scene.remove(A.imp.mesh); A.imp.dispose(); }
  A.lod0.geometry.dispose(); A.lod1.geometry.dispose(); A.mats.mat.dispose(); A.mats.mat0.dispose(); A.mats.depth.dispose();
  unitMeshes(A);
}
function syncPaint() {
  paintDirty = false; lastSync = performance.now();
  if (!armies[0] || simState !== 'setup') return;
  const pc = paintGroups(armies, paint, paintS).counts;
  armies.forEach((A, g) => { A.stats.count = pc[g]; A.cfg.count = pc[g]; });
  const bm = bldSetup(); // after the new counts: the zombies' extra reserve follows the painted victims
  applyRanges(bm.reserve);
  for (const A of armies) if (A.stats.count + A.reserve > A.lod1.instanceMatrix.count) regrowMeshes(A);
  gen++; suppressFrame = true;
  postSetup({ cmd: 'setup', gen, seed: battleSeed, armies: groupStats(), positions: paintPositions(), minW: ARENA.W, ...bm, ...terrainMsg() });
  localBuffer();
  resetTotals();
  updateCountsUI();
}
const PHASH = new Map(), HC = 3; let phMaxR = 0;
const hk = (cx, cz) => cx * 65536 + cz;
function hAdd(x, z, r) { const k = hk(Math.floor(x / HC), Math.floor(z / HC)); let a = PHASH.get(k); if (!a) PHASH.set(k, a = []); a.push(x, z, r); if (r > phMaxR) phMaxR = r; }
function rebuildHash() { PHASH.clear(); phMaxR = 0; for (let t = 0; t < 2; t++) { const a = paint[t], s = paintS[t]; for (let k = 0; k < a.length; k += 2) hAdd(a[k], a[k + 1], radiusAt(t, s[k / 2] | 0)); } }
function occupied(x, z, r) {
  const R = r + phMaxR, x0 = Math.floor((x - R) / HC), x1 = Math.floor((x + R) / HC), z0 = Math.floor((z - R) / HC), z1 = Math.floor((z + R) / HC);
  for (let cx = x0; cx <= x1; cx++) for (let cz = z0; cz <= z1; cz++) {
    const a = PHASH.get(hk(cx, cz)); if (!a) continue;
    for (let j = 0; j < a.length; j += 3) { const dx = a[j] - x, dz = a[j + 1] - z, m = (r + a[j + 2]) * 0.95; if (dx * dx + dz * dz < m * m) return true; }
  }
  return false;
}
const brushSpacing = () => (brushGroup() || { stats: { r: 0.5 } }).stats.r * 2.1 + 0.05;
const brushRadius = () => brushSpacing() * (0.6 + Math.sqrt(brushN) * 0.75);
function stampAt(cx, cy) {
  if (!armies[0] || !groundAt(cx, cy, hit)) return;
  lastStampT = performance.now(); lastStampX = hit.x; lastStampZ = hit.z;
  if (erasing) { eraseAt(hit.x, hit.z, brushRadius(brushTeam)); return; }
  const t = brushTeam, G = brushGroup(), slot = G ? G.slot : 0, r = G ? G.stats.r : 0.5, R = brushRadius(), L = ARENA.W - 10;
  let placed = 0;
  for (let k = 0; k < brushN * 4 && placed < brushN; k++) {
    let x = hit.x, z = hit.z;
    if (brushN > 1) { const a = Math.random() * 6.2832, rr = Math.sqrt(Math.random()) * R; x += Math.cos(a) * rr; z += Math.sin(a) * rr; }
    if (Math.abs(x) > L || Math.abs(z) > L) continue;
    if (terrain && !isWalkable(terrain, x, z)) continue; // not into the river
    if (!campaign.canPaint(t, x, z)) continue; // feature:campaign
    if (paint[t].length / 2 >= MAXC) break;
    if (occupied(x, z, r)) continue;
    paint[t].push(x, z); paintS[t].push(slot); hAdd(x, z, r); placed++;
  }
  if (placed) paintDirty = true;
}
function eraseAt(x, z, R) {
  let ch = false;
  for (let t = 0; t < 2; t++) {
    if (t !== brushTeam) continue; // the eraser removes units of the chosen team only (Blau / Rot)
    if (!campaign.canErase(t)) continue; // feature:campaign
    const a = paint[t];
    filterPaint(paint, paintS, t, k => { const dx = a[2 * k] - x, dz = a[2 * k + 1] - z; if (dx * dx + dz * dz > R * R) return true; ch = true; return false; });
  }
  if (ch) { rebuildHash(); paintDirty = true; }
}
function updateCountsUI() { for (let t = 0; t < 2; t++) $('#hc' + t).textContent = placeMode === 'paint' ? fmt(paint[t].length / 2) + ' gesetzt' : ''; }
function setMode(mode) {
  if (mode === placeMode) return;
  if (mode === 'paint') {
    // start from the current formation so it can be edited by hand
    paint[0] = []; paint[1] = []; paintS[0] = []; paintS[1] = [];
    if (curB && armies[0] && curB.length === N * 10) {
      const L = ARENA.W - 12;
      for (let i = 0; i < N; i++) {
        const o = i * 10; if (!curB[o + 9]) continue;
        const x = curB[o], z = curB[o + 2]; if (Math.abs(x) > L || Math.abs(z) > L) continue;
        const A = armies[GRPc[i]], t = i < split ? 0 : 1; if (paint[t].length / 2 < MAXC) { paint[t].push(x, z); paintS[t].push(A && A.team === t ? A.slot : 0); }
      }
    }
  }
  placeMode = mode;
  $('#modeCount').setAttribute('aria-pressed', String(mode === 'count')); $('#modePaint').setAttribute('aria-pressed', String(mode === 'paint'));
  $('#paintBox').hidden = mode !== 'paint';
  $('#autoNote').hidden = mode === 'paint';
  if (mode === 'paint') { lineTool = true; if (COARSE) touchArmed = false; setEraser(false); } // own armies: the front line first (touch: after tapping a tool)
  setupBattle(readGroups());
  rebuildHash();
}
/* ---------------- tool belt: explosives (more slots come from crates) ---------------- */
const TOOLS = {
  granate: { name: 'Granate', dmg: 140, aoe: 4.5, knock: 10, kind: 0 },
  fass: { name: 'Sprengfass', dmg: 480, aoe: 9, knock: 18, kind: 0 },
  atom: { name: 'Atombombe', dmg: 250000, aoe: 42, knock: 70, kind: 3 },
  ...ERA_TOOLS,
};
const TOOL_KEYS = ['hand', ...Object.keys(TOOLS).sort((a, b) => !!gateOf('tool', a) - !!gateOf('tool', b))].slice(0, 9); // crate tools last (index.html order)
Object.assign(TOOLS, DISASTER_TOOLS); // feature:disaster (own group in the tool belt, key 0; the digit keys stay as they are)
let tool = 'hand', nukeT = 0;
function setTool(k) { if (shop.locked('tool', k)) { shop.open(gateOf('tool', k)); return; } tool = k; document.querySelectorAll('#tools [data-tool]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.tool === k))); }
// feature commands (validated by featCmd in the worker): a lockstep command in multiplayer, otherwise queued for the
// next tick and logged for replays. Plain JSON with integer fields.
function featCommand(c) {
  if (simState === 'replay' || simState === 'setup') return false;
  if (mp.active) mp.send(c); else worker.postMessage({ cmd: 'feat', c });
  return true;
}
function useTool(cx, cy) {
  if (bet.locked() || waves.on) return; // feature:bet, feature:waves (no god tools in the defence mode)
  if (campaign.active && DISASTER_TOOLS[tool]) return; // feature:campaign: only the bought tool charges
  if (disasters.use(tool, cx, cy)) return; // feature:disaster (a feature command, see featCommand)
  const T0 = TOOLS[tool]; if (!T0 || simState === 'replay' || !groundAt(cx, cy, hit)) return;
  if (!campaign.useTool(tool)) return; // feature:campaign (charges; the sim counts them too: bomb carries the key k)
  const x = hit.x, z = hit.z, g0 = gen, k = tool;
  if (THROW_KINDS[k] !== undefined) { // feature:thrown: grenade / barrel fly in as objects, the sim lands them (src/sim/features/thrown.js)
    const from = possessed >= 0 && curB ? { x: rState.x, y: rState.y + armyOf(possessed).stats.h * 0.85, z: rState.z } : null;
    const c = planThrow(k, x, hit.y, z, from, { x: camera.position.x, z: camera.position.z, dist: camCur.dist });
    if (mp.active) mp.send({ t: 'throw', k, ...c }); // lockstep: the sim takes the tool values from the setup's tool table
    else worker.postMessage({ cmd: 'throw', k, ...c, dmg: T0.dmg, aoe: T0.aoe, knock: T0.knock, kind: T0.kind });
    sfx.tool(tool, x, z);
    return; // the shake comes at impact (thrown.onLand)
  }
  const send = () => {
    if (g0 !== gen) return;
    if (mp.active) mp.bomb(k, x, z); // multiplayer: a lockstep command, the sim uses its own tool table
    else worker.postMessage({ cmd: 'bomb', k, x, z, dmg: T0.dmg, aoe: T0.aoe, knock: T0.knock, kind: T0.fx === 'gas' ? KIND_GAS : T0.kind });
    if (T0.fx !== 'gas') shake(T0.aoe * 1.2, x, z); // your own bombs always rumble, at impact time
  };
  sfx.tool(tool, x, z);
  if (!wfx.tool(tool, T0, x, z, send)) send(); // meteor/catapult/artillery/orbital laser: the bomb lands when the visual does
}
// feature:disaster
const disasters = createDisasterFx({ scene, camera, canvas, uGTime, groundAt, groundH, shake, toast, setTool, send: featCommand,
  tool: () => tool, active: () => possessed < 0 && simState === 'running' && !shop.isOpen() && !bet.locked() && !campaign.active, gen: () => gen, parts: () => ({ fx, debris }), units: () => ({ b: curB, n: N }) });
function updateCursorRing() {
  if (!ptr.inside || COARSE) return;
  if (simState === 'setup' && placeMode === 'paint' && armies[0] && !bPlace) {
    if (!groundAt(ptr.x, ptr.y, hit)) return;
    const er = keys.has('ShiftLeft') || keys.has('ShiftRight') || erasing || eraser;
    hoverRing.visible = true; hoverRing.position.set(hit.x, hit.y + 0.06, hit.z); hoverRing.scale.setScalar(brushRadius(brushTeam));
    hoverRing.material.color.setHex(er ? 0xffffff : campaign.paintBlock(brushTeam, hit.x) ? 0xff3b30 : TEAMS[brushTeam].light); // feature:campaign: red beyond the own half
  } else if (simState === 'running' && possessed < 0 && TOOLS[tool]) {
    if (!groundAt(ptr.x, ptr.y, hit)) return;
    hoverRing.visible = true; hoverRing.position.set(hit.x, hit.y + 0.06, hit.z); hoverRing.scale.setScalar(TOOLS[tool].aoe);
    hoverRing.material.color.setHex(0xffa040);
  }
}
worker.onmessage = e => {
  const m = e.data;
  if (m.gen !== gen) return;
  if (rpListeners.size) for (const f of rpListeners) f(m);
  if (m.type === 'hash') { mp.onHash(m); return; } // multiplayer: periodic state hash for the desync check
  if (m.type === 'zone') { zones.message(m); return; } // feature:zone
  if (m.type === 'cmdlog') { if (simState !== 'replay') recorder.log(m.log); return; }
  if (m.type === 'mount') { mountSync(armies, m.st); return; } // feature:mount
  if (m.type === 'crater') { crater.onWorker(m); return; } // feature:crater
  if (m.type === 'waves') { waves.info(m); return; } // feature:waves
  if (m.type === 'campaign') { campaign.status(m.s); return; } // feature:campaign
  if (m.type === 'convert') { convert.frame(m); return; } // feature:convert
  if (m.type === 'statsFrame') { battleStats.update(m.s); return; } // feature:stats
  if (m.type === 'ready') {
    const envW = placeMode === 'paint' ? ARENA : m;
    world = envW; worldReady = true;
    const key = envKeyOf(envW);
    if (key !== envKey) { envKey = key; buildEnvironment(envW, Q, style); setFog(); }
    if (!suppressFrame) frameCamera(true);
    suppressFrame = false;
    $('#loading').hidden = true;
    return;
  }
  if (m.type === 'slowmoCap') { slowmo.onCap(m.cap); return; } // feature:slowmo: worker overload lowers the time scale
  if (m.type === 'frame') {
    const sfm = slowmo.onFrame(m); // feature:slowmo: interpolation span follows the time scale, highlights from events
    if (m.mp) mpFrame(m); else frameMs = sfm; // multiplayer: lockstep tick, host speed, possession check (own interpolation span)
    const old = prevB;
    prevB = curB || m.b; curB = m.b; tCur = performance.now();
    if (old && old !== prevB && old !== curB && old.byteLength) worker.postMessage({ cmd: 'buf', gen, b: old }, [old.buffer]);
    lastInfo = m;
    if (simState === 'running') recorder.frame(m);
    pInfo = m.P;
    handleEvents(m.ev, m.nev);
    grab.events(m.ev, m.nev); // feature:grab
    sfx.scanMelee(prevB, curB, N);
    if (m.bs) { bLayer.update(m.bs, m.gy); sfx.buildings(m.bs, bSim); }
    projData = m.proj; projN = m.np; projT = performance.now();
    thrown.frame(m.th); // feature:thrown (objects in flight)
    if (m.over && !overShown && simState === 'running') { overShown = true; setTimeout(showVictory, 1600); }
  }
};
let projData = new Float32Array(0), projN = 0, projT = 0;

/* ---------------- events -> FX ---------------- */
const SMOKE = [0.42, 0.42, 0.44], FIRE1 = [1.6, 1.0, 0.35], FIRE2 = [1.5, 0.5, 0.12];
function handleEvents(ev, n) {
  if (!n) return;
  let deaths = 0;
  for (let k = 0; k < n; k++) if (ev[k * 8] === 1) deaths++;
  const budget = Q.debris * 0.06;
  for (let k = 0; k < n; k++) {
    const o = k * 8, type = ev[o];
    sfx.event(ev, o);
    if (type === 1) spawnDeath(ev[o + 1] | 0, ev[o + 2], ev[o + 3], ev[o + 4], ev[o + 5], ev[o + 6], ev[o + 7], deaths, budget);
    else if (type === 2) spawnExplosion(ev[o + 1], ev[o + 2], ev[o + 3], ev[o + 4], ev[o + 5]);
    else if (type === 3) { if (!wfx.tracer(ev[o + 1], ev[o + 2], ev[o + 3], ev[o + 4], ev[o + 5], ev[o + 6], ev[o + 7])) addTracer(ev[o + 1], ev[o + 2], ev[o + 3], ev[o + 4], ev[o + 5], ev[o + 6], 0.05); if (Math.random() < 0.3) fx.spawn(ev[o + 1], ev[o + 2], ev[o + 3], 0, 0.5, 0, 0.08, 0.22, 0, 6, 3, 2, 1.6, 0.6); }
    else if (type === 4) { if (!wfx.swing(ev[o + 1], ev[o + 2], ev[o + 3], ev[o + 4], ev[o + 5], ev[o + 6] | 0)) spawnSwing(ev[o + 1], ev[o + 2], ev[o + 3], ev[o + 4], ev[o + 5], (ev[o + 6] | 0) & 1, ev[o + 7]); } // side + 2 * group
    else if (type === 5) { const kind = ev[o + 4]; for (let i = 0; i < (kind === 1 ? 6 : 2); i++) fx.spawn(ev[o + 1], ev[o + 2], ev[o + 3], (Math.random() - 0.5) * 3, Math.random() * 2, (Math.random() - 0.5) * 3, 0.15 + Math.random() * 0.1, kind === 1 ? 0.5 : 0.25, 0, 5, 1, ...FIRE1); }
    else if (type === 6) spawnSlam(ev[o + 1], ev[o + 2], ev[o + 3], (ev[o + 4] | 0) & 1);
    else if (type === 7) hitFlash();
    else if (type === 9) spawnPuff(ev[o + 1], ev[o + 2]);
    else if (type === 10) { for (let i = 0; i < 3; i++) fx.spawn(ev[o + 1] + (Math.random() - 0.5) * 0.6, ev[o + 2] + 0.6 + Math.random() * 0.8, ev[o + 3] + (Math.random() - 0.5) * 0.6, 0, 1.2 + Math.random(), 0, 0.12, 0.6, 0, 3, 1, 0.5, 2.2, 0.8); } // feature:support: healed (green sparks)
    else if (type === EV_WX_BOLT) weatherFx.event(ev, o); // feature:weather
    else if (type >= 13 && type <= 15) mountEvent(type, ev, o, armies, mountCtx); // feature:mount
  }
  disasters.events(ev, n); // feature:disaster
  debris.flush(); fx.flush(); flushTracers();
}
function unitState(i, out) {
  const b = curB; if (!b) return null; const o = i * 10;
  out.x = b[o]; out.y = b[o + 1]; out.z = b[o + 2]; out.yaw = b[o + 3]; out.alive = b[o + 9]; return out;
}
const _st = {};
function spawnDeath(i, x, y, z, kx, kz, kvy, deaths, budget) {
  const A = mountBody(armyOf(i), i); // feature:mount (riderless: the mount's voxels)
  const s = A.stats.s;
  unitState(i, _st);
  const yaw = _st.yaw || 0, c = Math.cos(yaw), sn = Math.sin(yaw);
  let n = Math.round(A.debrisN * Q.debrisMul);
  if (deaths > 1) n = Math.max(3, Math.min(n, Math.floor(budget / deaths)));
  const vsz = A.m.vs * s, gy = groundH(x, z);
  for (let k = 0; k < n; k++) {
    const vi = (Math.random() * A.voxN) | 0;
    const lx = A.vox[vi * 3] * s, ly = A.vox[vi * 3 + 1] * s, lz = A.vox[vi * 3 + 2] * s;
    const wx = x + c * lx + sn * lz, wz = z - sn * lx + c * lz, wy = y + ly;
    const dl = Math.hypot(lx, lz) || 1;
    const out = 1.5 + Math.random() * 3;
    const vx = (c * lx + sn * lz) / dl * out + kx * 0.55 + (Math.random() - 0.5) * 2;
    const vz = (-sn * lx + c * lz) / dl * out + kz * 0.55 + (Math.random() - 0.5) * 2;
    const vy = 2 + Math.random() * 4.5 + kvy * 0.4;
    const big = Math.random() < 0.25 ? 1.8 : 1;
    debris.spawn(wx, wy, wz, vx * Math.sqrt(s), vy * Math.sqrt(s), vz * Math.sqrt(s), COARSE ? 9 : 16 + Math.random() * 8, vsz * big * (0.9 + Math.random() * 0.4), 18, 2 + Math.random() * 9, 0,
      A.vcol[vi * 3], A.vcol[vi * 3 + 1], A.vcol[vi * 3 + 2], gy);
  }
  if (i === possessed) { toast('Gefallen – weiter mit dem nächsten Kameraden'); deathSwitchAt = performance.now() + 300; } // short beat to see the hit, then straight on
}
function spawnExplosion(x, y, z, aoe, kind) {
  const g0 = groundH(x, z);
  if (kind === 3) {
    nukeT = performance.now();
    const S = aoe / 42;
    // fireball
    for (let i = 0; i < 140; i++) { const a = Math.random() * 6.28, el = Math.random() * 1.2, sp = (6 + Math.random() * 20) * S; fx.spawn(x, g0 + 1, z, Math.cos(a) * Math.cos(el) * sp, Math.sin(el) * sp + 4, Math.sin(a) * Math.cos(el) * sp, 1.0 + Math.random() * 0.9, (3 + Math.random() * 5) * S, 0, 2.5, 1, ...(Math.random() < 0.55 ? FIRE1 : FIRE2)); }
    // stem + mushroom cap
    for (let i = 0; i < 90; i++) { const a = Math.random() * 6.28, rr = Math.random() * 4 * S; fx.spawn(x + Math.cos(a) * rr, g0 + 2, z + Math.sin(a) * rr, Math.cos(a) * 1.2, (14 + Math.random() * 22) * S, Math.sin(a) * 1.2, 2.6 + Math.random() * 1.8, (4 + Math.random() * 5) * S, 0, 1.0, 2, ...(Math.random() < 0.3 ? FIRE2 : SMOKE)); }
    for (let i = 0; i < 70; i++) { const a = Math.random() * 6.28, sp = (6 + Math.random() * 10) * S; fx.spawn(x, g0 + 26 * S, z, Math.cos(a) * sp, 3 + Math.random() * 4, Math.sin(a) * sp, 3 + Math.random() * 2, (6 + Math.random() * 6) * S, 0, 1.2, 2, ...SMOKE); }
    // ground shockwave of dust
    for (let i = 0; i < 80; i++) { const a = (i / 80) * 6.28, sp = aoe * (1.1 + Math.random() * 0.4); fx.spawn(x, g0 + 0.5, z, Math.cos(a) * sp, 0.6, Math.sin(a) * sp, 1.2 + Math.random() * 0.5, (1.5 + Math.random() * 2) * S, 0, 1.5, 2, 0.86, 0.78, 0.6); }
    for (let i = 0; i < 80; i++) { const a = Math.random() * 6.28, sp = 2 + Math.random() * 14; debris.spawn(x, g0 + 0.3, z, Math.cos(a) * sp, 8 + Math.random() * 18, Math.sin(a) * sp, 14, 0.25 + Math.random() * 0.5, 18, 5, 0, 0.32, 0.25, 0.16, g0); }
    shake(aoe * 3, x, z);
    return;
  }
  if (kind === KIND_GAS) {
    const n = Math.min(40, 10 + aoe * 3) | 0;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * 6.28, rr = Math.random() * aoe * 0.6, sp = 0.6 + Math.random() * 1.6, g = 0.75 + Math.random() * 0.25;
      fx.spawn(x + Math.cos(a) * rr, y + 0.4, z + Math.sin(a) * rr, Math.cos(a) * sp, 0.6 + Math.random() * 1.2, Math.sin(a) * sp, 2.2 + Math.random() * 1.6, aoe * (0.25 + Math.random() * 0.2), 0, 0.8, 2, 0.45 * g, 1.0 * g, 0.3 * g);
    }
    shakeNear(aoe * 0.3, x, z);
    return;
  }
  const n = Math.min(26, 8 + aoe * 4) | 0;
  for (let i = 0; i < n; i++) {
    const a = Math.random() * 6.28, sp = (2 + Math.random() * 5) * Math.sqrt(aoe / 3);
    const f = Math.random() < 0.5 ? FIRE1 : FIRE2;
    fx.spawn(x, y + 0.3, z, Math.cos(a) * sp, 2 + Math.random() * 5, Math.sin(a) * sp, 0.35 + Math.random() * 0.4, aoe * (0.18 + Math.random() * 0.2), 2, 4, 1, ...f);
  }
  for (let i = 0; i < n * 0.5; i++) {
    const a = Math.random() * 6.28, sp = Math.random() * 2.5;
    fx.spawn(x + (Math.random() - 0.5) * aoe * 0.5, y + 0.5, z + (Math.random() - 0.5) * aoe * 0.5, Math.cos(a) * sp, 1.5 + Math.random() * 2, Math.sin(a) * sp, 1.1 + Math.random() * 0.8, aoe * (0.3 + Math.random() * 0.25), 0, 1.5, 2, ...SMOKE);
  }
  for (let i = 0; i < 6; i++) {
    const a = Math.random() * 6.28, sp = 2 + Math.random() * 4;
    debris.spawn(x, g0 + 0.2, z, Math.cos(a) * sp, 4 + Math.random() * 5, Math.sin(a) * sp, 8, 0.12 + Math.random() * 0.12, 18, 6, 0, 0.36, 0.27, 0.16, g0);
  }
  if (kind === 2) for (let i = 0; i < 16; i++) { const a = Math.random() * 6.28, sp = 2 + Math.random() * 5; fx.spawn(x, y + 0.5, z, Math.cos(a) * sp, 3 + Math.random() * 4, Math.sin(a) * sp, 1.2 + Math.random(), 0.14, 3, 8, 3, 1.4, 1.4, 1.35); }
  // big blasts (Sprengfass and up) still shake from afar; ordinary shells only up close
  if (aoe >= 8) shake(aoe * 0.9, x, z); else shakeNear(aoe * 0.9, x, z);
}
function spawnSwing(x, z, yaw, R, h, team, cleave) {
  const col = cleave ? new THREE.Color(TEAMS[team].plasma) : new THREE.Color(0xe8dcc0);
  const n = cleave ? 22 : 10, g = groundH(x, z);
  for (let i = 0; i < n; i++) {
    const a = yaw + (i / (n - 1) - 0.5) * 2.2, r = R * (0.55 + Math.random() * 0.4);
    fx.spawn(x + Math.sin(a) * r, g + h * (0.6 + Math.random() * 0.6), z + Math.cos(a) * r, Math.cos(a) * 2, 0.5, -Math.sin(a) * 2, cleave ? 0.3 : 0.45, cleave ? Math.max(0.2, R * 0.07) : Math.max(0.15, R * 0.06), 0, 6, cleave ? 3 : 2, col.r * (cleave ? 1.8 : 1), col.g * (cleave ? 1.8 : 1), col.b * (cleave ? 1.8 : 1));
  }
  if (R > 6) shakeNear(R * 0.25, x, z);
}
function spawnSlam(x, z, R, team) {
  const g = groundH(x, z);
  for (let i = 0; i < 36; i++) {
    const a = (i / 36) * 6.28, sp = R * (1.2 + Math.random() * 0.6);
    fx.spawn(x, g + 0.3, z, Math.sin(a) * sp, 1 + Math.random() * 2, Math.cos(a) * sp, 0.7 + Math.random() * 0.4, 0.35 + R * 0.04, 0, 3, 2, 0.85, 0.78, 0.6);
  }
  shake(R * 0.6, x, z);
}
let shakeAmt = 0, shakeFrame = 0;
// screen shake: per frame only the strongest request counts (thousands of hits must not stack), scaled by the user setting
const SHAKE_MUL = { aus: 0, schwach: 0.45, normal: 1 };
let shakeMode = (() => { try { return localStorage.getItem('aw-shake') || 'normal'; } catch { return 'normal'; } })();
if (!(shakeMode in SHAKE_MUL)) shakeMode = 'normal';
function shake(a, x, z) {
  if (REDUCED || !SHAKE_MUL[shakeMode]) return;
  const d = Math.hypot(x - camCur.tx, z - camCur.tz), c = a * 6 / Math.max(12, d + camCur.dist * 0.5);
  if (c > shakeFrame) shakeFrame = c;
}
// ordinary weapon hits only shake when they land right next to a close camera (e.g. a possessed unit)
function shakeNear(a, x, z) { if (camCur.dist < 45 && Math.hypot(x - camCur.tx, z - camCur.tz) < 18) shake(a, x, z); }
let flashT = 0;
function hitFlash() { flashT = performance.now(); }

/* ---------------- camera ---------------- */
const camGoal = { tx: 0, ty: 0, tz: 0, yaw: 0.6, pitch: 0.9, dist: 120 };
const camCur = { ...camGoal };
function frameCamera(snap) {
  const e = Math.max(world.ext, world.extZ);
  camGoal.tx = 0; camGoal.tz = 0; camGoal.ty = groundH(0, 0);
  camGoal.dist = clamp(e * 1.35, 18, 1400); camGoal.pitch = 0.82; camGoal.yaw = 0.55;
  if (snap) Object.assign(camCur, camGoal);
}
const FPV_FOV = 72, BASE_FOV = camera.fov, BASE_NEAR = camera.near;
let lensFpv = false;
function syncLens() {
  const on = possessed >= 0 && fpv && !capture;
  if (on === lensFpv) return;
  lensFpv = on; camera.fov = on ? FPV_FOV : BASE_FOV; camera.near = on ? 0.06 : BASE_NEAR; camera.updateProjectionMatrix();
}
// eye-level camera: position from the rendered (interpolated) unit, look direction from yaw + fpvPitch; no smoothing lag
function fpvCamera(dt) {
  const y = camGoal.yaw, cp = Math.cos(fpvPitch), fx = -Math.sin(y) * cp, fy = Math.sin(fpvPitch), fz = -Math.cos(y) * cp;
  const A = armyOf(possessed), fwd = A.stats.r * 0.35;
  let ex = rState.x - Math.sin(y) * fwd, ey = rState.y + (rState.eye || A.stats.h * 0.88), ez = rState.z - Math.cos(y) * fwd;
  if (shakeFrame > 0) { shakeAmt = Math.min(0.5 * SHAKE_MUL[shakeMode], Math.max(shakeAmt, shakeFrame * SHAKE_MUL[shakeMode])); shakeFrame = 0; }
  if (shakeAmt > 0.001) { const s = shakeAmt * 0.25; ex += (Math.random() - 0.5) * s; ey += (Math.random() - 0.5) * s; ez += (Math.random() - 0.5) * s; shakeAmt *= Math.exp(-dt * 9); }
  camera.position.set(ex, ey, ez); camera.lookAt(ex + fx, ey + fy, ez + fz);
  // keep the orbit state on the unit so leaving the view, culling margins, sound and screen shake behave
  camCur.tx = camGoal.tx = rState.x; camCur.tz = camGoal.tz = rState.z; camCur.ty = camGoal.ty = rState.g; camCur.yaw = y; camCur.dist = 3;
  // shadows: a box reaching ahead of the eye (the orbit default would only cover the unit's own feet)
  const half = 60, sx = ex - Math.sin(y) * 40, sz = ez - Math.cos(y) * 40;
  const sc = sun.shadow.camera; sc.left = -half; sc.right = half; sc.top = half; sc.bottom = -half; sc.near = 1; sc.far = 600; sc.updateProjectionMatrix();
  sun.target.position.set(sx, rState.g, sz);
  const sd = style.sunDir; sun.position.set(sx + sd[0] * 320, rState.g + sd[1] * 320, sz + sd[2] * 320);
}
function setFpv(on, quiet) {
  on = !!on; if (on === fpv) return;
  fpv = on; try { localStorage.setItem('aw-fpv', on ? '1' : '0'); } catch { /* private mode */ }
  if (possessed >= 0) fpvEnter();
  if (!on) { if (document.pointerLockElement === canvas) document.exitPointerLock(); camCur.dist = 3; camGoal.dist = Math.max(camGoal.dist, 8); }
  if (!quiet) toast(on ? 'Ego-Sicht · Maus umsehen, V oder Mausrad zurück' : 'Verfolger-Kamera');
}
// look where the unit faces when the view starts (unit forward = (sin rot, cos rot), camera forward = (-sin yaw, -cos yaw))
function fpvEnter() {
  possRing.visible = possessed >= 0 && !fpv; $('#xhair').hidden = !(possessed >= 0 && fpv); $('#pfpv').setAttribute('aria-pressed', String(fpv));
  if (!fpv || possessed < 0 || !curB) return;
  camGoal.yaw = camCur.yaw = curB[possessed * 10 + 3] + Math.PI; fpvPitch = -0.08;
}
function updateCamera(dt) {
  syncLens();
  if (possessed >= 0 && curB && fpv && !capture) { fpvCamera(dt); return; }
  if (possessed >= 0 && curB) {
    const A = armyOf(possessed);
    // terrain: follow the ground fully, a jump only partly (rState.g = ground under the possessed unit)
    camGoal.tx = rState.x; camGoal.tz = rState.z; camGoal.ty = A.stats.h * 0.45 + rState.g + (rState.y - rState.g) * 0.6;
  } else if (kino && lastInfo) {
    camGoal.yaw += dt * 0.07;
    if (capture) { const p = clashPoint(); if (p) { camGoal.tx += (p.x - camGoal.tx) * Math.min(1, dt * 0.8); camGoal.tz += (p.z - camGoal.tz) * Math.min(1, dt * 0.8); } camGoal.dist *= Math.exp(-dt * 0.015); }
    else if (curB) { let sx = 0, sz = 0, c = 0; const step = Math.max(1, (N / 1500) | 0); for (let i = 0; i < N; i += step) { const o = i * 10; if (!curB[o + 9]) continue; sx += curB[o]; sz += curB[o + 2]; c++; } if (c) { camGoal.tx += (sx / c - camGoal.tx) * Math.min(1, dt * 0.8); camGoal.tz += (sz / c - camGoal.tz) * Math.min(1, dt * 0.8); } }
  }
  if (possessed < 0 && terrain) camGoal.ty = groundH(camGoal.tx, camGoal.tz); // god/kino camera orbits the ground below
  if (possessed < 0 && kino && !capture) slowmo.direct(dt, camGoal); // feature:slowmo: Regie overrides the Kino goal (not while a clip renders)
  const k = 1 - Math.exp(-dt * (possessed >= 0 ? 9 : 7));
  camCur.tx += (camGoal.tx - camCur.tx) * k; camCur.ty += (camGoal.ty - camCur.ty) * k; camCur.tz += (camGoal.tz - camCur.tz) * k;
  let dy = camGoal.yaw - camCur.yaw; while (dy > Math.PI) dy -= 6.2832; while (dy < -Math.PI) dy += 6.2832; camCur.yaw += dy * k;
  camCur.pitch += (camGoal.pitch - camCur.pitch) * k; camCur.dist += (camGoal.dist - camCur.dist) * k;
  const cp = Math.cos(camCur.pitch), sp = Math.sin(camCur.pitch);
  let x = camCur.tx + camCur.dist * cp * Math.sin(camCur.yaw), y = camCur.ty + camCur.dist * sp, z = camCur.tz + camCur.dist * cp * Math.cos(camCur.yaw);
  if (shakeFrame > 0) { shakeAmt = Math.min(1.4 * SHAKE_MUL[shakeMode], Math.max(shakeAmt, shakeFrame * SHAKE_MUL[shakeMode])); shakeFrame = 0; }
  if (shakeAmt > 0.001) { const s = shakeAmt * Math.min(1, camCur.dist / 40); x += (Math.random() - 0.5) * s; y += (Math.random() - 0.5) * s; z += (Math.random() - 0.5) * s; shakeAmt *= Math.exp(-dt * 7); }
  if (terrain) { const gh = groundH(x, z) + 1; if (y < gh) y = gh; } // never dive into a terrace
  camera.position.set(x, y, z); camera.lookAt(camCur.tx, camCur.ty, camCur.tz);
  // shadow frustum follows the view
  const half = clamp(camCur.dist * 0.75, 18, 140);
  const sc = sun.shadow.camera; sc.left = -half; sc.right = half; sc.top = half; sc.bottom = -half; sc.near = 1; sc.far = 600; sc.updateProjectionMatrix();
  const sy = terrain ? camCur.ty : 0;
  sun.target.position.set(camCur.tx, sy, camCur.tz);
  const sd = style.sunDir; sun.position.set(camCur.tx + sd[0] * 320, sy + sd[1] * 320, camCur.tz + sd[2] * 320);
}

/* ---------------- input ---------------- */
const keys = new Set();
const ptr = { x: 0, y: 0, down: false, btn: 0, sx: 0, sy: 0, moved: 0, t0: 0, lmb: false, rmb: false, inside: false };
const touches = new Map();
let pinch0 = 0, mid0 = null;
const ray = new THREE.Raycaster(); const ndc = new THREE.Vector2(); const gplane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0); const hit = new THREE.Vector3();
function groundAt(cx, cy, out) {
  const r = canvas.getBoundingClientRect(); ndc.set(((cx - r.left) / r.width) * 2 - 1, -((cy - r.top) / r.height) * 2 + 1);
  ray.setFromCamera(ndc, camera);
  if (terrain && rayTerrain(ray.ray, out)) return out; // terrain: march the heightfield, else the (sunken) plane
  return ray.ray.intersectPlane(gplane, out);
}
const grab = createGrabHand({ THREE, scene, canvas, camera, send: featCommand, touches, groundH, pickUnit, setTool, toast, shake, shakeNear, // feature:grab (before the pointer handlers: it may swallow a press)
  st: () => ({ tool, possessed, simState, paused, curB, prevB, tCur, frameMs, N, split, GRP: GRPc, armies, gen, camDist: camCur.dist, fx, debris, locked: bet.locked() || waves.on || campaign.active }) });
canvas.addEventListener('contextmenu', e => e.preventDefault());
canvas.addEventListener('pointerdown', e => {
  canvas.focus(); canvas.setPointerCapture(e.pointerId);
  if (zones.pointer(e)) return; // feature:zone
  if (e.pointerType === 'touch') {
    touches.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY, t0: performance.now() });
    if (touches.size === 2) { painting = false; const [a, b] = [...touches.values()]; pinch0 = Math.hypot(a.x - b.x, a.y - b.y); mid0 = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }
    else if (bPlace && simState === 'setup') { /* tap places on release */ }
    else if (simState === 'setup' && placeMode === 'paint' && touchArmed && lineTool) { ptr.x = e.clientX; ptr.y = e.clientY; lineStart(e.clientX, e.clientY); }
    else if (simState === 'setup' && placeMode === 'paint' && touchArmed) { painting = true; erasing = eraser; ptr.x = e.clientX; ptr.y = e.clientY; stampAt(e.clientX, e.clientY); }
    return;
  }
  ptr.down = true; ptr.btn = e.button; ptr.sx = ptr.x = e.clientX; ptr.sy = ptr.y = e.clientY; ptr.moved = 0; ptr.t0 = performance.now();
  if (bPlace && simState === 'setup' && e.button === 0) { if (e.shiftKey) removeBuildingAt(e.clientX, e.clientY); else placeBuildingAt(e.clientX, e.clientY); ptr.down = false; return; }
  if (simState === 'setup' && placeMode === 'paint' && e.button === 0 && lineTool && !e.shiftKey) { lineStart(e.clientX, e.clientY, e.ctrlKey || e.metaKey); return; } // before the selection box: Ctrl/Cmd adds a block here
  if (simState === 'setup' && selBox && selBox.down(e, { paintMode: placeMode === 'paint' })) { ptr.selecting = true; return; } // selection box / moving a selection
  if (simState === 'setup' && placeMode === 'paint' && e.button === 0) { painting = true; erasing = e.shiftKey || eraser; stampAt(e.clientX, e.clientY); return; }
  if (possessed >= 0) {
    if (fpv && e.pointerType === 'mouse' && document.pointerLockElement !== canvas && canvas.requestPointerLock) { try { const p = canvas.requestPointerLock(); if (p && p.catch) p.catch(() => {}); } catch { /* not allowed here */ } }
    if (e.button === 0) ptr.lmb = true; if (e.button === 2) sendSpecial = true;
  }
});
// first person: mouse look while the pointer is locked (or while a button is held without the lock)
function fpvLook(dx, dy) { camGoal.yaw -= dx * 0.0026; fpvPitch = clamp(fpvPitch - dy * 0.0026, -1.35, 1.35); }
document.addEventListener('pointerlockchange', () => { if (document.pointerLockElement !== canvas) lockLostAt = performance.now(); });
canvas.addEventListener('pointermove', e => {
  if (e.pointerType === 'touch') {
    const t = touches.get(e.pointerId); if (!t) return;
    const dx = e.clientX - t.x, dy = e.clientY - t.y; t.x = e.clientX; t.y = e.clientY;
    if (lineDrag && touches.size === 1) { lineMove(e.clientX, e.clientY); return; }
    if (painting && touches.size === 1) { ptr.x = e.clientX; ptr.y = e.clientY; paintMove(e.clientX, e.clientY); return; }
    // first person: a finger that started on the right half looks around, the left half is the joystick
    if (possessed >= 0 && fpv) { if (t.sx > innerWidth * 0.5) fpvLook(dx * 1.7, dy * 1.7); else { joy.x = clamp((t.x - t.sx) / 60, -1, 1); joy.y = clamp((t.y - t.sy) / 60, -1, 1); } return; }
    if (touches.size === 1) {
      if (possessed >= 0) { joy.x = clamp((t.x - t.sx) / 60, -1, 1); joy.y = clamp((t.y - t.sy) / 60, -1, 1); }
      else { camGoal.yaw -= dx * 0.006; camGoal.pitch = clamp(camGoal.pitch + dy * 0.004, 0.25, 1.45); kino = false; syncKino(); }
    } else if (touches.size === 2) {
      const [a, b] = [...touches.values()]; const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch0 > 0) camGoal.dist = clamp(camGoal.dist * pinch0 / d, 4, 1600); pinch0 = d;
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      if (mid0 && possessed < 0) panBy(mid.x - mid0.x, mid.y - mid0.y); mid0 = mid;
    }
    return;
  }
  if (possessed >= 0 && fpv) { if (document.pointerLockElement === canvas || ptr.down) fpvLook(e.movementX || 0, e.movementY || 0); ptr.x = e.clientX; ptr.y = e.clientY; return; }
  const dx = e.clientX - ptr.x, dy = e.clientY - ptr.y; ptr.x = e.clientX; ptr.y = e.clientY;
  if (ptr.selecting) { ptr.moved += Math.abs(dx) + Math.abs(dy); selBox.move(e); return; }
  if (lineDrag) { lineMove(e.clientX, e.clientY); return; }
  if (painting) { paintMove(e.clientX, e.clientY); return; }
  if (!ptr.down || possessed >= 0) { hoverDirty = true; return; }
  ptr.moved += Math.abs(dx) + Math.abs(dy);
  const paintCam = simState === 'setup'; // setup: left drag selects / paints, the right button turns the camera
  // setup: Shift + left drag turns (like the plain drag in the battle), Shift + right drag pans
  const turnSetup = paintCam && ptr.btn === 0 && e.shiftKey;
  if (!turnSetup && (ptr.btn === 1 || e.shiftKey || (ptr.btn === 2 && !paintCam))) panBy(dx, dy);
  else { camGoal.yaw -= dx * 0.005; camGoal.pitch = clamp(camGoal.pitch + dy * 0.004, 0.25, 1.45); }
  if (ptr.moved > 6) { kino = false; syncKino(); }
});
/* ---- "Formation": drag a front line (user decision 02.10.2026). The side's whole mix (total x shares) lines up behind it
   by role: close combat in front, ranged behind, giants beyond the ends; the front faces the enemy. ---- */
let lineTool = false, lineDrag = null, lineViz = null;
// touch: one finger turns the camera until a placement tool was tapped on purpose (no army from the first touch)
let touchArmed = !COARSE;
const statCache = new Map();
function troopStats(t, T) { // radius / role of a troop (built once per look)
  const k = [T.type, T.weapon, T.size, T.t, T.width, T.head, T.mount].join('|');
  if (!statCache.has(k)) { const A = armies.find(G => G.team === t && G.slot === T.slot && G.cfg.type === T.type && G.cfg.weapon === T.weapon && G.cfg.size === T.size); statCache.set(k, A ? A.stats : buildArmy(t, { ...T, count: 1 }).stats); }
  return statCache.get(k);
}
// mix = [{ ...troop, n }]: what one line places (sandbox: the side's troops by share; the campaign: the chosen card)
function lineMix(t) {
  const c = campaign.lineMix && campaign.lineMix(t); if (c) return c;
  storeTroop(t);
  const list = troops[t], n = splitCount(total(t), list.map(T => T.share));
  return list.map((T, k) => ({ ...T, n: n[k] }));
}
function formationPositions(t, ax, az, bx, bz) {
  const items = lineMix(t).map(T => { const S = troopStats(t, T); return { slot: T.slot, n: T.n, r: S.r, melee: S.melee, giant: S.giant }; }).filter(o => o.n > 0);
  const main = items.filter(o => !o.giant).sort((a, b) => (a.melee ? 0 : 1) - (b.melee ? 0 : 1));
  let L = Math.hypot(bx - ax, bz - az), ux = (bx - ax) / (L || 1), uz = (bz - az) / (L || 1);
  if (L < 2) { // a click: a block of the usual width, its line across the field
    const o = main[0] || items[0]; ux = 0; uz = 1;
    L = o ? Math.sqrt(items.reduce((s, q) => s + q.n, 0) * 2.2) * (o.r * 2 * 1.22 + 0.15) : 10;
    ax -= ux * L / 2; az -= uz * L / 2;
  }
  const mx = ax + ux * L / 2, mz = az + uz * L / 2;
  // the front faces the enemy: its painted centre, else the other half of the field
  const e = paint[1 - t]; let ex = t ? -1 : 1, ez = 0;
  if (e.length) { let sx = 0, sz = 0; for (let k = 0; k < e.length; k += 2) { sx += e[k]; sz += e[k + 1]; } ex = sx / (e.length / 2) - mx; ez = sz / (e.length / 2) - mz; }
  let nx = uz, nz = -ux; if (nx * ex + nz * ez < 0) { nx = -nx; nz = -nz; }
  const out = []; let depth = 0;
  for (const o of main) {
    const sp = o.r * 2 * 1.22 + 0.15, cols = Math.max(1, Math.floor(L / sp) + 1), rows = Math.ceil(o.n / cols);
    for (let k = 0; k < o.n; k++) {
      const col = k % cols, row = (k / cols) | 0, inRow = row === rows - 1 ? o.n - row * cols : cols;
      const a = (col - (inRow - 1) / 2) * sp, b = depth + o.r + row * sp;
      out.push([mx + ux * a - nx * b, mz + uz * a - nz * b, o.slot, o.r]);
    }
    depth += o.r * 2 + (rows - 1) * sp + 1.2;
  }
  let side = 1; const wing = [L / 2, L / 2];
  for (const o of items.filter(q => q.giant)) {
    const sp = o.r * 2.3, cols = Math.max(1, Math.ceil(Math.sqrt(o.n))), w = side > 0 ? 1 : 0;
    for (let k = 0; k < o.n; k++) { const a = side * (wing[w] + o.r + 1 + (k % cols) * sp), b = o.r + ((k / cols) | 0) * sp; out.push([mx + ux * a - nx * b, mz + uz * a - nz * b, o.slot, o.r]); }
    wing[w] += o.r + 1 + cols * sp; side = -side;
  }
  return { out, mx, mz, ux, uz, nx, nz, L, depth };
}
// add (Ctrl / Cmd): one more block, e.g. for the flanks; otherwise the line replaces the side's placement
function lineStart(cx, cy, add = false) {
  if (!armies[0] || !groundAt(cx, cy, hit)) return;
  lineDrag = { t: brushTeam, add, ax: hit.x, az: hit.z, bx: hit.x, bz: hit.z }; lineShow();
}
function lineMove(cx, cy) { if (lineDrag && groundAt(cx, cy, hit)) { lineDrag.bx = hit.x; lineDrag.bz = hit.z; lineShow(); } }
function lineEnd() {
  const d = lineDrag; lineDrag = null; if (lineViz) lineViz.visible = false; if (!d) return;
  const t = d.t, L = ARENA.W - 10, f = formationPositions(t, d.ax, d.az, d.bx, d.bz);
  if (!d.add) { const mine = new Set(lineMix(t).map(T => T.slot)); filterPaint(paint, paintS, t, k => !campaign.canErase(t) || !mine.has(paintS[t][k] | 0)); rebuildHash(); } // the line replaces what it places
  let placed = 0, skipped = 0;
  for (const [x, z, slot, r] of f.out) {
    if (paint[t].length / 2 >= MAXC) break;
    if (Math.abs(x) > L || Math.abs(z) > L || (terrain && !isWalkable(terrain, x, z)) || !campaign.canPaint(t, x, z) || occupied(x, z, r)) { skipped++; continue; }
    paint[t].push(x, z); paintS[t].push(slot); hAdd(x, z, r); placed++;
  }
  if (placed || !d.add) { syncPaint(); updateCountsUI(); } // the old placement of the side may be gone even if nothing fits
  if (skipped) toast(placed ? `${fmt(placed)} aufgestellt, ${fmt(skipped)} passten nicht hin (Rand, Wasser, belegt)` : 'Hier ist kein Platz für diese Formation');
}
function lineShow() {
  const d = lineDrag; if (!d) return;
  if (!lineViz) {
    lineViz = new THREE.Group();
    const front = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.85, depthWrite: false }));
    const body = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.22, depthWrite: false }));
    front.rotation.x = body.rotation.x = -Math.PI / 2; lineViz.add(front, body); lineViz.userData = { front, body }; scene.add(lineViz);
  }
  const f = formationPositions(d.t, d.ax, d.az, d.bx, d.bz), { front, body } = lineViz.userData, col = TEAMS[d.t].light, yaw = Math.atan2(f.ux, f.uz);
  for (const m of [front, body]) m.material.color.setHex(col);
  lineViz.visible = true; lineViz.position.set(f.mx, groundH(f.mx, f.mz) + 0.12, f.mz); lineViz.rotation.set(0, yaw, 0);
  // group axes: z = along the line (u), x = (u.z, -u.x); the block lies behind the front, away from the enemy (n)
  const sgn = Math.sign(f.nx * f.uz - f.nz * f.ux) || 1, back = Math.max(1, f.depth);
  front.scale.set(0.5, f.L, 1); front.position.set(0, 0, 0);
  body.scale.set(back, f.L, 1); body.position.set(-sgn * back / 2, -0.01, 0);
}
function paintMove(cx, cy) {
  if (!groundAt(cx, cy, hit)) return;
  if (Math.hypot(hit.x - lastStampX, hit.z - lastStampZ) >= brushSpacing(brushTeam) * (brushN > 1 ? 1.3 : 1.0)) stampAt(cx, cy);
}
function endPtr(e) {
  if (e.pointerType === 'touch') {
    const t = touches.get(e.pointerId); touches.delete(e.pointerId); pinch0 = 0; mid0 = null;
    if (lineDrag) { lineEnd(); return; }
    if (painting) { if (!touches.size) painting = false; return; }
    if (t && bPlace && simState === 'setup') { // a tap on a building removes it (touch has no Shift/right click), elsewhere it builds
      if (Math.hypot(t.x - t.sx, t.y - t.sy) < 10) { groundAt(t.x, t.y, hit); if (bLayer.pickRay(ray.ray, false) >= 0) removeBuildingAt(t.x, t.y); else placeBuildingAt(t.x, t.y); }
      return;
    }
    if (t && possessed < 0 && Math.hypot(t.x - t.sx, t.y - t.sy) < 10 && waves.click(t.x, t.y)) return; // feature:waves
    if (t && possessed < 0 && simState !== 'setup' && TOOLS[tool] && Math.hypot(t.x - t.sx, t.y - t.sy) < 10) { useTool(t.x, t.y); return; }
    if (t && possessed < 0 && Math.hypot(t.x - t.sx, t.y - t.sy) < 10 && performance.now() - t.t0 < 350) { const u = pickUnit(t.x, t.y); if (u >= 0) setPossess(u); }
    if (possessed >= 0 && (touches.size === 0 || (fpv && t && t.sx <= innerWidth * 0.5))) { joy.x = 0; joy.y = 0; }
    return;
  }
  if (lineDrag) { lineEnd(); ptr.down = false; return; }
  if (painting) { painting = false; ptr.down = false; return; }
  if (ptr.selecting) { ptr.selecting = false; if (selBox.up()) { ptr.down = false; return; } } // a plain click falls through
  if (bPlace && simState === 'setup' && ptr.down && ptr.btn === 2 && ptr.moved < 6) removeBuildingAt(e.clientX, e.clientY);
  if (ptr.down && possessed < 0 && ptr.btn === 0 && ptr.moved < 6 && waves.click(e.clientX, e.clientY)) { ptr.down = false; ptr.lmb = false; return; } // feature:waves
  if (ptr.down && possessed < 0 && ptr.btn === 0 && ptr.moved < 6 && performance.now() - ptr.t0 < 400) {
    if (simState !== 'setup' && TOOLS[tool]) useTool(e.clientX, e.clientY);
    else { const u = pickUnit(e.clientX, e.clientY); if (u >= 0) setPossess(u); else if (simState !== 'setup') upgradeAt(e.clientX, e.clientY); }
  }
  ptr.down = false; ptr.lmb = false;
}
canvas.addEventListener('pointerup', endPtr); canvas.addEventListener('pointercancel', endPtr);
canvas.addEventListener('pointerenter', () => { ptr.inside = true; }); canvas.addEventListener('pointerleave', () => { ptr.inside = false; hoverRing.visible = false; });
canvas.addEventListener('wheel', e => {
  e.preventDefault();
  // possessed: scrolling out of the first-person view returns to the chase camera, scrolling in past the closest step enters it
  if (possessed >= 0 && fpv) { if (e.deltaY > 0) setFpv(false); return; }
  if (possessed >= 0 && e.deltaY < 0 && camGoal.dist <= 3.05 && camCur.dist < 3.6) { setFpv(true); return; }
  camGoal.dist = clamp(camGoal.dist * Math.exp(e.deltaY * 0.0012), possessed >= 0 ? 3 : 4, 1600);
}, { passive: false });
function panBy(dx, dy) {
  const s = camCur.dist * 0.0016; const y = camCur.yaw;
  camGoal.tx += (-Math.cos(y) * dx - Math.sin(y) * dy) * s; camGoal.tz += (Math.sin(y) * dx - Math.cos(y) * dy) * s;
  kino = false; syncKino();
}
let sendSpecial = false, sendJump = false;
const joy = { x: 0, y: 0 };
addEventListener('keydown', e => {
  if (e.target.matches('input,select,textarea') || clipper.busy) return;
  if (shop.isOpen()) return; // crate overlay handles its own keys
  keys.add(e.code);
  if (e.ctrlKey || e.metaKey || e.altKey) return; // browser shortcuts (Strg+C …) are no game keys
  if (e.code === 'Escape' && e.defaultPrevented) return; // a feature already used this Esc (rally point, waves building, clip …)
  if (!$('#modal').hidden) { if (e.code === 'Escape') closeModal(); return; }
  if (pauseMenu && pauseMenu.isOpen) { if (e.code === 'Escape') pauseMenu.close(); return; } // nothing happens behind the pause menu
  if (anyWindowOpen()) return; // windows close themselves on Esc
  if (e.code === 'Escape' && bPlace && simState === 'setup') { setBPlace(false); return; } // Esc stops building first
  if (simState === 'setup' && selBox && selBox.key(e)) { e.preventDefault(); return; } // selection: Entf, R, Esc
  if (possessed < 0 && simState === 'running' && /^Digit[1-9]$/.test(e.code)) { const k = TOOL_KEYS[+e.code.slice(5) - 1]; if (k) setTool(k); }
  if (e.code === 'Escape' && performance.now() - lockLostAt < 300) return; // this Esc only released the mouse (first-person view)
  if (e.code === 'Escape' || e.code === 'KeyG') { if (possessed >= 0) setPossess(-1); else if (e.code === 'Escape' && simState !== 'setup' && pauseMenu) pauseMenu.toggle(); }
  if (e.code === 'Tab' && possessed >= 0) { e.preventDefault(); switchTeammate(); }
  if (e.code === 'Space') { e.preventDefault(); if (possessed >= 0) sendJump = true; else if (simState !== 'setup') togglePause(); }
  if (e.code === 'KeyF' && possessed < 0 && hovered >= 0) setPossess(hovered);
  if (e.code === 'KeyV' && possessed >= 0) setFpv(!fpv); // possessed: V = first person / chase camera
  else if (e.code === 'KeyV') { if (styleLocked()) toast('Der Stil lässt sich erst nach der Runde ändern.'); else { nextStyle(); toast(style.name + ': ' + style.desc); } }
  if (e.code === 'KeyR' && simState === 'setup') rotateB();
  if (e.code === 'KeyX' && simState === 'setup' && placeMode === 'paint') setEraser(!eraser); // brush <-> eraser
  if (e.code === 'KeyC' && simState !== 'setup') clipper.start();
});
addEventListener('keyup', e => keys.delete(e.code));
addEventListener('blur', () => { keys.clear(); ptr.lmb = false; });
$('#tAtk').addEventListener('pointerdown', e => { e.preventDefault(); ptr.lmb = true; });
$('#tAtk').addEventListener('pointerup', () => { ptr.lmb = false; });
$('#tAtk').addEventListener('pointerleave', () => { ptr.lmb = false; });
$('#tSp').addEventListener('click', () => { sendSpecial = true; });

let hovered = -1, hoverDirty = false;
function pickUnit(cx, cy) {
  if (!curB || simState === 'replay' || !groundAt(cx, cy, hit)) return -1;
  let best = -1, bd = 1e9;
  for (let i = 0; i < N; i++) {
    const o = i * 10; if (!curB[o + 9]) continue;
    const A = armyOf(i); const r = A.stats.r + 0.6 + camCur.dist * 0.006;
    const dx = curB[o] - hit.x, dz = curB[o + 2] - hit.z, d = dx * dx + dz * dz;
    if (d < r * r && d < bd) { bd = d; best = i; }
  }
  return best;
}
function armyOf(i) { return armies[GRPc[i]] || armies[i < split ? 0 : armies.length - 1]; } // the unit's group (its type)
function setPossess(i, silent) {
  if (mp.active && i >= 0 && armyOf(i).team !== mp.team) { toast('Im Mehrspieler steuerst du nur Einheiten deines Teams.'); return; }
  if (i >= 0 && bet.locked()) { if (!silent) toast('Wett-Modus: nur zuschauen'); return; } // feature:bet
  possessed = i;
  if (mp.active) mp.possess(i); else worker.postMessage({ cmd: 'possess', i });
  const on = i >= 0;
  $('#phud').hidden = !on; $('#keys').hidden = !on || COARSE; $('#tbtns').hidden = !on || !COARSE; $('#hint').hidden = on || simState === 'setup';
  $('#god').hidden = !on; $('#pfpv').hidden = !on; $('#kino').hidden = on; $('#tools').hidden = on || simState === 'setup'; $('#ctrl').classList.toggle('up', on);
  possRing.visible = on;
  if (!on && document.pointerLockElement === canvas) document.exitPointerLock();
  fpvEnter(); // first-person view: crosshair, no ring, look where the unit faces
  if (on) {
    const A = armyOf(i);
    kino = false; syncKino();
    camGoal.dist = clamp(A.stats.h * 5.5 + 6, 8, 140); camGoal.pitch = 0.98;
    $('#pname').textContent = `${A.ty.name} · ${TEAMS[A.team].name}`;
    $('#pname').style.color = TEAMS[A.team].css;
    possRing.material.color.setHex(TEAMS[A.team].light);
    if (!silent) { toast(`Du steuerst jetzt: ${A.ty.name} (${TEAMS[A.team].name})`); sfx.possess(A.team); }
  } else if (!silent) {
    camGoal.dist = clamp(camCur.dist * 2.2, 30, 600); camGoal.pitch = 0.88;
    toast('Gott-Modus'); sfx.possess(-1);
  }
}
function switchTeammate() {
  if (!curB || possessed < 0) return;
  const A = armyOf(possessed); const o = possessed * 10; const x = curB[o], z = curB[o + 2];
  let best = -1, bd = 1e18;
  for (let i = A.team === 0 ? 0 : split, e = A.team === 0 ? split : N; i < e; i++) {
    if (i === possessed || !curB[i * 10 + 9]) continue;
    const d = (curB[i * 10] - x) ** 2 + (curB[i * 10 + 2] - z) ** 2; if (d < bd) { bd = d; best = i; }
  }
  if (best >= 0) setPossess(best); else { setPossess(-1); }
}
let lastInputSend = 0;
function sendInput(now) {
  if (possessed < 0) return;
  let mx = 0, mz = 0;
  const f = (keys.has('KeyW') || keys.has('ArrowUp') ? 1 : 0) - (keys.has('KeyS') || keys.has('ArrowDown') ? 1 : 0);
  const r = (keys.has('KeyD') || keys.has('ArrowRight') ? 1 : 0) - (keys.has('KeyA') || keys.has('ArrowLeft') ? 1 : 0);
  const y = camCur.yaw; const fx2 = -Math.sin(y), fz = -Math.cos(y), rx = Math.cos(y), rz = -Math.sin(y);
  const jf = -joy.y, jr = joy.x;
  mx = fx2 * (f + jf) + rx * (r + jr); mz = fz * (f + jf) + rz * (r + jr);
  let ax = 0, az = 0, auto = COARSE && !fpv;
  if (fpv) {
    // first person: aim through the screen centre; looking at the sky or far away = a point straight ahead
    const r = canvas.getBoundingClientRect(), A = armyOf(possessed), far = Math.max(12, (A.stats.melee ? A.stats.reach + A.stats.r : A.stats.range) * 1.6);
    if (groundAt(r.left + r.width / 2, r.top + r.height / 2, hit) && Math.hypot(hit.x - rState.x, hit.z - rState.z) < far * 4) { ax = hit.x; az = hit.z; }
    else { ax = rState.x - Math.sin(y) * far; az = rState.z - Math.cos(y) * far; }
  } else if (!auto && groundAt(ptr.x, ptr.y, hit)) { ax = hit.x; az = hit.z; }
  if (keys.has('KeyQ')) camGoal.yaw += 0.03; if (keys.has('KeyE')) camGoal.yaw -= 0.03;
  const im = { cmd: 'input', mx, mz, ax, az, attack: ptr.lmb, special: sendSpecial, jump: sendJump, sprint: keys.has('ShiftLeft') || keys.has('ShiftRight'), auto };
  if (mp.active) mp.input(im); else worker.postMessage(im); // multiplayer: coalesced into one command per turn
  sendSpecial = false; sendJump = false;
}
function godKeys(dt) {
  if (possessed >= 0) return;
  const s = camCur.dist * 0.9 * dt;
  const f = (keys.has('KeyW') || keys.has('ArrowUp') ? 1 : 0) - (keys.has('KeyS') || keys.has('ArrowDown') ? 1 : 0);
  const r = (keys.has('KeyD') || keys.has('ArrowRight') ? 1 : 0) - (keys.has('KeyA') || keys.has('ArrowLeft') ? 1 : 0);
  if (f || r) { const y = camCur.yaw; camGoal.tx += (-Math.sin(y) * f + Math.cos(y) * r) * s; camGoal.tz += (-Math.cos(y) * f - Math.sin(y) * r) * s; kino = false; syncKino(); }
  if (keys.has('KeyQ')) camGoal.yaw += dt * 1.6; if (keys.has('KeyE')) camGoal.yaw -= dt * 1.6;
}

/* ---------------- render units ---------------- */
const rState = { x: 0, y: 0, z: 0, g: 0 };
let dustT = 0;
function lerpAng(a, b, t) { let d = b - a; while (d > Math.PI) d -= 6.2832; while (d < -Math.PI) d += 6.2832; return a + d * t; }
const cull = makeCuller(); // per-unit frustum test (src/render/culling.js)
let blobs = null;          // blob shadows when shadow maps are off (src/render/blobShadows.js)
function renderUnits(now) {
  if (!curB) return;
  const alpha = clamp((now - tCur) / frameMs, 0, 1);
  const pb = prevB && prevB.length === curB.length ? prevB : curB;
  const cx = camera.position.x, cz = camera.position.z, cy = camera.position.y;
  cull.update(camera, 2 + camCur.dist * 0.05); // last frame's camera: the margin covers this frame's motion
  const useBlob = Q.shadows === 0;
  if (useBlob && (!blobs || blobs.cap < N)) { if (blobs) { scene.remove(blobs.mesh); blobs.dispose(); } blobs = makeBlobShadows(Math.max(N, 1024)); scene.add(blobs.mesh); }
  if (blobs) { blobs.begin(); if (!useBlob) blobs.end(); }
  for (let g = 0; g < armies.length; g++) {
    const A = armies[g], t = A.team; const s0 = A.stats.s;
    if (A.mount) { mountRender(A, t, alpha, pb, mountCtx); continue; } // feature:mount
    const m0 = A.lod0.instanceMatrix.array, m1 = A.lod1.instanceMatrix.array;
    const a0 = A.lod0.geometry.getAttribute('aAnim').array, a1 = A.lod1.geometry.getAttribute('aAnim').array;
    const cap0 = A.lod0.instanceMatrix.count;
    const d0 = Q.lod0Dist * Math.max(1, Math.sqrt(s0) * (A.h > 2 ? 2 : 1)), d02 = d0 * d0;
    let n0 = 0, n1 = 0;
    const start = A.start, end = Math.min(A.end, N);
    const bob = A.m.kind === 'humanoid' ? A.h * 0.035 * s0 : A.m.kind === 'vehicle' ? 0 : A.h * 0.02 * s0;
    const lean = A.m.kind === 'humanoid' ? 0.09 : 0.04;
    // far units → impostor sprites; culling sphere around the body (+ its shadow when shadow maps are on)
    const dI = Q.impDist * Math.max(1, Math.sqrt(s0) * (A.h > 2 ? 2 : 1)), dI2 = A.imp ? dI * dI : Infinity;
    const IA = A.imp ? A.imp.aA.array : null, IB = A.imp ? A.imp.aB.array : null, capI = A.imp ? A.imp.cap : 0;
    const hh = A.h * s0 * 0.5, cr = A.h * s0 * 0.8 + (Q.shadows ? A.h * s0 * 2.5 : 0);
    let ni = 0;
    for (let i = start; i < end; i++) {
      const o = i * 10; if (!curB[o + 9]) continue;
      const x = pb[o] + (curB[o] - pb[o]) * alpha, y = pb[o + 1] + (curB[o + 1] - pb[o + 1]) * alpha, z = pb[o + 2] + (curB[o + 2] - pb[o + 2]) * alpha;
      const yaw = lerpAng(pb[o + 3], curB[o + 3], alpha); const tumble = pb[o + 4] + (curB[o + 4] - pb[o + 4]) * alpha;
      const walk = pb[o + 5] + (curB[o + 5] - pb[o + 5]) * alpha, ph = pb[o + 6] + (curB[o + 6] - pb[o + 6]) * alpha;
      const t1 = curB[o + 7], t0 = pb[o + 7], atk = t1 >= t0 && t0 >= 0 ? t0 + (t1 - t0) * alpha : t1, ex = lerpAng(pb[o + 8], curB[o + 8], alpha); // feature:slowmo: smooth swings at 0.1x
      const air = curB[o + 9] > 1.5; // worker: 1 = on the ground, 2 = airborne; y is absolute (ground height included)
      if (i === possessed) {
        rState.x = x; rState.y = y; rState.z = z; rState.g = air ? groundH(x, z) : y;
        if (walk > 0.4 && !air && slowmo.now() - dustT > 140 / Math.min(walk, 1.6) && !paused) { // feature:slowmo
          dustT = slowmo.now(); const r = A.stats.r;
          for (let q = 0; q < 2; q++) fx.spawn(x - Math.sin(yaw) * r * 0.6 + (Math.random() - 0.5) * r, y + 0.1, z - Math.cos(yaw) * r * 0.6 + (Math.random() - 0.5) * r, (Math.random() - 0.5) * 0.8, 0.6, (Math.random() - 0.5) * 0.8, 0.5, 0.12 + r * 0.25, 0, 2, 2, 0.9, 0.86, 0.7);
        }
      }
      if (i === possessed) { rState.eye = A.h * A.varS[i - start] * 0.88; if (fpv && !capture) continue; } // first person: the camera sits inside this unit
      if (i !== possessed && !cull.sphere(x, y + hh, z, cr)) continue; // off-screen: costs nothing
      if (useBlob) blobs.add(x, air ? groundH(x, z) : y, z, A.stats.r, y);
      const dx = x - cx, dz = z - cz, dd = dx * dx + dz * dz + cy * cy * 0.5;
      const yy = y + (air ? 0 : Math.abs(Math.sin(ph)) * bob * Math.min(walk, 1.5)), s = A.varS[i - start];
      if (dd > dI2 && i !== possessed && !air && tumble < 0.05 && tumble > -0.05 && ni < capI) {
        const q = ni++ * 4; IA[q] = x; IA[q + 1] = yy; IA[q + 2] = z; IA[q + 3] = yaw; IB[q] = s; IB[q + 1] = walk; IB[q + 2] = ph;
        continue;
      }
      let M, An, k;
      if ((dd < d02 || i === possessed) && n0 < cap0) { M = m0; An = a0; k = n0++; } else { M = m1; An = a1; k = n1++; }
      const pitch = tumble + lean * Math.min(walk, 1.6);
      const c = Math.cos(yaw), sn = Math.sin(yaw), cp = Math.cos(pitch), sp = Math.sin(pitch);
      const m = k * 16;
      M[m] = c * s; M[m + 1] = 0; M[m + 2] = -sn * s; M[m + 3] = 0;
      M[m + 4] = sn * sp * s; M[m + 5] = cp * s; M[m + 6] = c * sp * s; M[m + 7] = 0;
      M[m + 8] = sn * cp * s; M[m + 9] = -sp * s; M[m + 10] = c * cp * s; M[m + 11] = 0;
      M[m + 12] = x; M[m + 13] = yy; M[m + 14] = z; M[m + 15] = 1;
      const a = k * 4; An[a] = walk; An[a + 1] = ph; An[a + 2] = atk; An[a + 3] = ex;
      if (A.cv) convert.instance(A, An === a0, k, i); // feature:convert
    }
    A.lod0.count = n0; A.lod1.count = n1; if (A.imp) A.imp.commit(ni);
    for (const [mesh, n] of [[A.lod0, n0], [A.lod1, n1]]) {
      if (!n) continue;
      mesh.instanceMatrix.clearUpdateRanges(); mesh.instanceMatrix.addUpdateRange(0, n * 16); mesh.instanceMatrix.needsUpdate = true;
      const an = mesh.geometry.getAttribute('aAnim'); an.clearUpdateRanges(); an.addUpdateRange(0, n * 4); an.needsUpdate = true;
    }
    if (A.cv) convert.flush(A, n0, n1); // feature:convert
  }
  if (useBlob) blobs.end();
  convert.render(curB, pb, alpha); // feature:convert
  // projectiles (extrapolated)
  const dtp = Math.min(0.08, (now - projT) / 1000 * timeScale) * (paused ? 0 : 1); // feature:slowmo: cap in game time
  const PM = projMesh.instanceMatrix.array; const PC = projMesh.instanceColor.array;
  for (let k = 0; k < projN; k++) {
    const o = k * 7; const kind = projData[o + 6] % 10, arc = projData[o + 6] >= 10;
    const vx = projData[o + 3], vy = projData[o + 4] - (arc ? 14 * dtp : 0), vz = projData[o + 5];
    const x = projData[o] + vx * dtp, y = projData[o + 1] + projData[o + 4] * dtp - (arc ? 7 * dtp * dtp : 0), z = projData[o + 2] + vz * dtp;
    const st = wfx.projStyle(kind, arc, vx, projData[o + 4], vz), L = st.L, w = st.w; // arrows, stones, plasma, gas … (src/fx/weaponKinds.js)
    _e.set(-Math.atan2(vy, Math.hypot(vx, vz)), Math.atan2(vx, vz), 0, 'YXZ'); _q.setFromEuler(_e);
    _m.compose(_v.set(x, y, z), _q, _s.set(w, w, L)); _m.toArray(PM, k * 16);
    PC[k * 3] = st.r; PC[k * 3 + 1] = st.g; PC[k * 3 + 2] = st.b;
    if (!paused) wfx.trail(st, x, y, z);
  }
  thrown.update(alpha, camera.position); // feature:thrown (game time: age + alpha ticks)
  projMesh.count = projN; projMesh.instanceMatrix.needsUpdate = true; if (projMesh.instanceColor) projMesh.instanceColor.needsUpdate = true;
  // hover & possession rings
  if (possessed >= 0) { const A = armyOf(possessed); const r = A.stats.r * 1.5 + 0.3; possRing.position.set(rState.x, rState.g + 0.04, rState.z); possRing.scale.setScalar(r * (1 + Math.sin(now * 0.006) * 0.06)); }
  if (hovered >= 0 && possessed < 0 && curB[hovered * 10 + 9]) {
    const A = armyOf(hovered); const o = hovered * 10; hoverRing.visible = true; hoverRing.position.set(curB[o], groundH(curB[o], curB[o + 2]) + 0.05, curB[o + 2]); hoverRing.scale.setScalar(A.stats.r * 1.5 + 0.3);
    hoverRing.material.color.setHex(TEAMS[A.team].light);
  } else hoverRing.visible = false;
}

/* ---------------- style switching ---------------- */
let tourOn = false, tourNext = 0;
// the graphics style is fixed while a round runs (user choice): only in the setup and once the battle is decided
const styleLocked = () => simState !== 'setup' && !lastInfo.over;
let styleLockShown = null;
function syncStyleLock() {
  const l = styleLocked(); if (l === styleLockShown) return;
  styleLockShown = l; $('#styleBtn').hidden = l; $('#tour').hidden = l;
  if (l && tourOn) { tourOn = false; $('#tour').setAttribute('aria-pressed', 'false'); }
}
function setFog() {
  const e = Math.max(world.ext, world.extZ), f = style.fog;
  scene.fog.near = Math.max(50, e * f[0]); scene.fog.far = Math.max(scene.fog.near + 200, e * f[1] + 300);
  weatherFx.fog(); // feature:weather
}
function applyStyle(key) {
  const prevTorch = !!style.torches;
  styleKey = key; style = STYLES[key];
  try { localStorage.setItem('aw-style', key); } catch (e) { }
  SKY.setHex(style.sky); scene.fog.color.copy(SKY);
  hemi.color.setHex(style.hemi[0]); hemi.groundColor.setHex(style.hemi[1]); hemi.intensity = style.hemi[2];
  sun.color.setHex(style.sun[0]); sun.intensity = style.sun[1];
  renderer.toneMappingExposure = style.exposure;
  GU.cA.value.setHex(style.ground[0]); GU.cB.value.setHex(style.ground[1]); GU.cC.value.setHex(style.ground[2]); GU.cD.value.setHex(style.ground[3]);
  GU.uWater.value = style.water ? 1 : 0; if (style.water) GU.cW.value.setHex(style.water);
  GU.uSnow.value = style.snow || 0;
  for (const v of GU.uTorch.value) v.w = 0;
  if (!!style.torches !== prevTorch) { for (const l of torchLights) style.torches ? scene.add(l) : scene.remove(l); }
  buildAmbient(Q); setupPost(Q); setFog();
  refreshTerrainVis(); // terrain colours follow the style
  if (worldReady) { buildEnvironment(world, Q, style); envKey = envKeyOf(world); }
  torchSortT = 0;
  $('#style').value = key; $('#styleDesc').textContent = style.desc; $('#styleBtn').textContent = 'Stil: ' + style.name;
  weatherFx.restyle(); // feature:weather
}

/* ---------------- UI ---------------- */
const MAXC = 20000;
const lin2count = v => Math.max(1, Math.round(Math.pow(10, (v / 1000) * Math.log10(MAXC))));
const count2lin = c => Math.round(Math.log10(Math.max(1, c)) / Math.log10(MAXC) * 1000);
/* crates + diamonds: lock state, crate overlay (pauses the battle), rewards */
const shop = createShop({
  toast, types: TYPES,
  pauseGame: () => { if (simState !== 'running' || paused) return null; togglePause(); return () => { if (paused) togglePause(); }; },
  onChange: () => refreshTypeSelects(),
});
{ const q = document.createElement('div'); q.id = 'genQuota'; q.className = 'genquota'; q.setAttribute('aria-live', 'polite'); $('#gen0').parentElement.after(q); syncGenQuota(); shop.inv.subscribe(syncGenQuota); }
function typeOptions() { return shop.options('unit', Object.entries(TYPES)); }
function weaponOptions(t) { return shop.options('weapon', Object.entries(WEAPONS), $('#type' + t)?.value); }
function refreshWeaponSelect(t) { const sel = $('#weapon' + t); if (!sel) return; const cur = sel.value; sel.innerHTML = weaponOptions(t); sel.value = cur; }
function refreshTypeSelects() { for (let t = 0; t < 2; t++) { const sel = $('#type' + t), cur = sel.value; sel.innerHTML = typeOptions(); sel.value = cur; refreshWeaponSelect(t); } }
function armyUI(t) {
  const el = $('#army' + t);
  el.innerHTML = `<header><i></i><span>Armee ${TEAMS[t].name}</span><span class="hc" id="hc${t}"></span></header>
  <label for="type${t}">Einheit</label><select id="type${t}">${typeOptions()}</select>
  <div class="gr magic"><span></span><div class="row"><button class="ghost" id="wand${t}" title="Verwandelt die Einheit in eine von drei Varianten">Zauberstab</button><button class="ghost" id="dice${t}" title="Würfelt drei zufällige Einheiten zur Auswahl">Würfeln</button></div></div>
  <div class="gr" id="cntRow${t}"><label for="count${t}">Anzahl</label><div class="row"><input type="range" id="countR${t}" min="0" max="1000" aria-label="Anzahl Regler"><input type="number" id="count${t}" min="1" max="${MAXC}"></div></div>
  <label for="weapon${t}">Waffe</label><select id="weapon${t}">${weaponOptions(t)}</select>
  <label for="size${t}">Größe</label><div class="row"><input type="range" id="size${t}" min="1" max="20" step="1"><output id="sizeO${t}">×1</output></div>
  <div class="gr" id="rx${t}">
    <label for="prop${t}">Körper</label><div class="row"><input type="range" id="prop${t}" min="0" max="100" step="1" value="40"><output id="propO${t}"></output></div>
    <label for="build${t}">Statur</label><div class="row"><input type="range" id="build${t}" min="65" max="160" step="1" value="100"><output id="buildO${t}"></output></div>
    <label for="head${t}">Kopf</label><div class="row"><input type="range" id="head${t}" min="65" max="170" step="1" value="100"><output id="headO${t}"></output></div>
  </div>
  <p class="stats" id="stats${t}"></p>`;
  shop.guardSelect($('#type' + t), 'unit'); shop.guardSelect($('#weapon' + t), 'weapon', () => $('#type' + t).value); // before the game's listeners
  // a new type starts from its standard: own weapon and normal size (a scenario's giant x12 must not carry over)
  $('#type' + t).addEventListener('change', e => { $('#weapon' + t).value = TYPES[e.target.value].weapon; $('#size' + t).value = 1; $('#sizeO' + t).textContent = '×1'; syncRows(t); changed(); });
  $('#countR' + t).addEventListener('input', e => { $('#count' + t).value = lin2count(+e.target.value); changedSoon(); });
  $('#count' + t).addEventListener('change', e => { const v = clamp(+e.target.value || 1, 1, MAXC); e.target.value = v; $('#countR' + t).value = count2lin(v); changed(); });
  $('#weapon' + t).addEventListener('change', changed);
  $('#size' + t).addEventListener('input', () => { $('#sizeO' + t).textContent = '×' + $('#size' + t).value; changedSoon(); });
  for (const k of ['prop', 'build', 'head']) $('#' + k + t).addEventListener('input', () => { syncOut(t); changedSoon(); });
  $('#wand' + t).addEventListener('click', () => openWand(t));
  $('#dice' + t).addEventListener('click', () => openDice(t));
}
function syncOut(t) {
  $('#propO' + t).textContent = headsTall($('#prop' + t).value / 100).toFixed(1).replace('.', ',') + ' Köpfe';
  $('#buildO' + t).textContent = $('#build' + t).value + ' %';
  $('#headO' + t).textContent = $('#head' + t).value + ' %';
}
function syncRows(t) {
  const ty = TYPES[$('#type' + t).value];
  refreshWeaponSelect(t); // a unit's own standard weapon is always usable with it
  $('#rx' + t).hidden = !(ty.recipe && RECIPES[ty.recipe].template === 'humanoid');
  $('#wand' + t).disabled = !ty.recipe;
  syncOut(t);
}
armyUI(0); armyUI(1);
const fm = createFormation({ mixed: t => { try { return troops[t].length > 1; } catch { return false; } }, command: featCommand, scene, teams: TEAMS, canvas, groundAt, groundH, getState: () => simState, isPossessed: () => possessed >= 0, onChange: () => changed(), toast, // feature:formation
  ownTeam: () => mp.active ? mp.team : campaign.active ? 0 : -1, locked: () => bet.locked() || waves.on }); // campaign: own army only
setupExtras.push(fm.setupMsg); // feature:formation: { formation: [k0, k1] } in every setup (multiplayer: the host's)
mountUI(shop, changed); // feature:mount
/* ---- troops (mixed armies, src/game/groups.js): up to 4 per side, one total ("Anzahl") split by shares. The side's unit
   controls (type, weapon, size, body, mount) always show the selected troop. ---- */
const troops = [[], []], troopSel = [0, 0];
const ctlCfg = t => ({ type: $('#type' + t).value, weapon: $('#weapon' + t).value, size: +$('#size' + t).value, t: $('#prop' + t).value / 100, width: $('#build' + t).value / 100, head: $('#head' + t).value / 100, mount: $('#mount' + t) ? $('#mount' + t).value : '' });
function setCtl(t, c) {
  $('#type' + t).value = c.type; $('#weapon' + t).value = c.weapon; $('#size' + t).value = c.size; $('#sizeO' + t).textContent = '×' + c.size;
  $('#prop' + t).value = Math.round(c.t * 100); $('#build' + t).value = Math.round(c.width * 100); $('#head' + t).value = Math.round(c.head * 100);
  if ($('#mount' + t)) $('#mount' + t).value = c.mount || '';
  syncRows(t);
}
const storeTroop = t => { const T = troops[t][troopSel[t]]; if (T) Object.assign(T, ctlCfg(t)); };
const total = t => clamp(+$('#count' + t).value || 1, 1, MAXC);
const setTotal = (t, n) => { $('#count' + t).value = n; $('#countR' + t).value = count2lin(n); };
/** the selected troop of side t (count = the side's total) */
function readCfg(t) { const c = ctlCfg(t); delete c.mount; return { ...c, count: total(t) }; }
/** edit the selected troop (AI figure, wand, silhouette …); the other troops stay */
function writeCfg(t, [type, count, weapon, size, prop = 40, build = 100, head = 100]) {
  if (!troops[t].length) { setSide(t, [type, count, weapon, size, prop, build, head]); return; }
  setCtl(t, { type, weapon, size, t: prop / 100, width: build / 100, head: head / 100, mount: $('#mount' + t) ? $('#mount' + t).value : '' });
  setTotal(t, count); storeTroop(t); renderTroops(t);
}
/** a side with exactly one troop (scenarios, dice, bets, campaign, multiplayer, replays) */
function setSide(t, [type, count, weapon, size, prop = 40, build = 100, head = 100]) {
  const hadSlots = troops[t].length > 1;
  troops[t] = [{ slot: 0, share: 100, type, weapon, size, t: prop / 100, width: build / 100, head: head / 100, mount: '' }]; troopSel[t] = 0;
  setCtl(t, troops[t][0]); setTotal(t, count); renderTroops(t);
  if (hadSlots) filterPaint(paint, paintS, t, k => !(paintS[t][k] | 0)); // painted units of removed troops go too
  paintS[t] = new Array(paint[t].length / 2).fill(0); // one troop: every painted unit is it (also after paint[t] was replaced)
}
// zombies are recognised like convertRules does (recipe or name), so AI zombies count too
const isZombieType = k => { const ty = TYPES[k] || {}; return ty.recipe === 'zombie' || /zombie/i.test(ty.name || '') || /zombie/i.test(k); };
/** all troops of both sides as setup configs (count mode: the total split by shares; paint mode counts come from paint) */
function readGroups() {
  const out = [];
  for (let t = 0; t < 2; t++) {
    storeTroop(t);
    const list = troops[t], n = splitCount(total(t), list.map(T => T.share));
    // zombies last: risen victims take the side's reserve slots, which belong to its last group (feature:convert)
    list.forEach((T, k) => out.push({ ...T, team: t, count: n[k], order: T.slot + (isZombieType(T.type) ? 10 : 0) }));
  }
  return out;
}
const troopUI = [0, 1].map(t => {
  const el = document.createElement('div'); $('#army' + t).querySelector('header').after(el);
  return createTroopUI(el, {
    team: t, name: T => (TYPES[T.type] || {}).name || T.type,
    onSelect: k => { storeTroop(t); troopSel[t] = k; setCtl(t, troops[t][k]); renderTroops(t); setBrushTeam(t); showStats(); },
    onAdd: () => {
      storeTroop(t); const list = troops[t]; if (list.length >= MAX_TROOPS) return;
      const used = new Set(list.map(T => T.slot)), slot = [0, 1, 2, 3].find(s => !used.has(s)), base = list[troopSel[t]];
      const shares = sharesWithNew(list.map(T => T.share));
      // a classic second line first (archers, then other common troops) that the side does not have yet
      const ty = ['bogenschuetze', 'ritter', 'armbrustschuetze', 'hoplit', 'soldat', 'legionaer', 'musketier'].find(k => TYPES[k] && !shop.locked('unit', k) && !list.some(T => T.type === k)) || base.type;
      list.push({ ...base, slot, type: ty, weapon: TYPES[ty].weapon, size: 1, mount: '' }); list.forEach((T, k) => { T.share = shares[k]; });
      troopSel[t] = list.length - 1; setCtl(t, list[troopSel[t]]); renderTroops(t); setBrushTeam(t); changed();
    },
    onRemove: k => {
      storeTroop(t); const list = troops[t]; if (list.length < 2) return;
      const gone = list[k].slot, shares = sharesWithout(list.map(T => T.share), k);
      list.splice(k, 1); list.forEach((T, j) => { T.share = shares[j]; });
      troopSel[t] = Math.min(troopSel[t] > k ? troopSel[t] - 1 : troopSel[t], list.length - 1);
      filterPaint(paint, paintS, t, j => (paintS[t][j] | 0) !== gone); rebuildHash();
      setCtl(t, list[troopSel[t]]); renderTroops(t); changed();
    },
    onShares: (shares, done) => { troops[t].forEach((T, k) => { T.share = shares[k]; }); if (done) { renderTroops(t); changed(); } },
  });
});
function renderTroops(t) { troopUI[t].render(troops[t], troopSel[t]); if (typeof fm !== 'undefined') fm.refresh(); }
/** the campaign sets the own side's troops (cards) and which one the brush and the line place */
function setTroops(t, list, sel = 0) {
  troops[t] = list.map(T => ({ share: 0, size: 1, t: 0.4, width: 1, head: 1, mount: '', ...T }));
  const sum = troops[t].length; troops[t].forEach(T => { T.share = Math.round(100 / sum); });
  troopSel[t] = Math.max(0, Math.min(sel, troops[t].length - 1)); setCtl(t, troops[t][troopSel[t]]); renderTroops(t);
}
// the sandbox's troops and painted slots, kept while a campaign mission runs
const troopsSnap = () => ({ troops: troops.map(l => l.map(T => ({ ...T }))), sel: troopSel.slice(), paintS: paintS.map(a => a.slice()) });
function troopsRestore(s) {
  for (let t = 0; t < 2; t++) { troops[t] = s.troops[t]; troopSel[t] = s.sel[t]; if (troops[t][troopSel[t]]) setCtl(t, troops[t][troopSel[t]]); renderTroops(t); paintS[t] = s.paintS[t]; }
}
/** the panel shows the troops of a setup (multiplayer host, replay): one chip per group, shares from the counts */
function setSidesFrom(cfgs) {
  for (let t = 0; t < 2; t++) {
    const gs = normalizeGroups(cfgs).filter(c => c.team === t); if (!gs.length) continue;
    const sum = gs.reduce((a, c) => a + (c.count | 0), 0) || 1;
    troops[t] = gs.map(c => ({ slot: c.slot | 0, share: Math.max(1, Math.round((c.count | 0) * 100 / sum)), type: c.type, weapon: c.weapon, size: c.size, t: c.t, width: c.width, head: c.head, mount: c.mount || '' }));
    troopSel[t] = 0; setCtl(t, troops[t][0]); setTotal(t, Math.max(1, sum)); renderTroops(t);
  }
}
function showStats() {
  for (let t = 0; t < 2; t++) {
    const S = (firstOf(t) || armies[0]).stats;
    $('#stats' + t).textContent = `HP ${fmt(S.hp)} · Schaden ${fmt(S.dmg)} · ${S.melee ? 'Reichweite ' + (S.reach + S.r).toFixed(1) : 'Reichweite ' + S.range.toFixed(0)} m · Tempo ${S.speed.toFixed(1)} m/s`;
  }
}
let changeTimer = 0;
function changed() { clearTimeout(changeTimer); document.querySelectorAll('.chip').forEach(c => c.classList.remove('on')); setupBattle(readGroups(), placeMode === 'paint'); if (placeMode === 'paint') rebuildHash(); showStats(); }
function changedSoon() { clearTimeout(changeTimer); changeTimer = setTimeout(changed, 220); }
PRESETS.forEach((p, i) => {
  const b = document.createElement('button'); b.className = 'chip'; b.textContent = p.name; shop.markPreset(b, [p.a, p.b]);
  b.addEventListener('click', () => { if (placeMode === 'paint') setMode('count'); setSide(0, p.a); setSide(1, p.b); changed(); b.classList.add('on'); });
  $('#presets').appendChild(b);
});
// feature:dice — two random armies of about equal points, guess the winner, then watch (src/features/dice.js)
const dice = createDice({
  types: TYPES, cost: k => unitCost(k), allowType: k => !shop.locked('unit', k), toast, start,
  apply: (a, b) => { if (placeMode === 'paint') setMode('count'); setSide(0, a); setSide(1, b); changed(); },
  blocked: () => mp.active ? 'Im Mehrspieler gibt es keinen Würfel.' : bet.locked() ? 'Erst die Wette beenden.' : campaign.active ? 'Erst die Kampagnen-Mission verlassen.' : waves.on ? 'Erst die Verteidigung verlassen.' : simState !== 'setup' ? 'Würfeln geht vor der Schlacht.' : '',
});
{ const b = document.createElement('button'); b.className = 'chip dice-chip'; b.innerHTML = '<i class="ti ti-dice-5" aria-hidden="true"></i> Würfeln & tippen'; b.title = 'Zwei zufällige Armeen mit gleich vielen Punkten: tippe, wer gewinnt'; b.addEventListener('click', () => dice.open()); $('#presets').prepend(b); }
$('#start').addEventListener('click', start);
// feature:link — Schlacht per Link (src/features/battleLink.js): the setup in the URL fragment, the friend tips or replays it
const battleLink = createBattleLink({
  types: TYPES, weapons: WEAPONS, toast, start,
  buildings: Object.keys(BUILDINGS), styles: Object.keys(STYLES), terrains: Object.keys(TERRAIN_PRESETS),
  typeName: (k, n) => { const T = TYPES[k]; return T ? (n === 1 ? T.name : T.plural || T.name) : k; },
  validateRecipe: rec => { const v = validateUnitRecipe(rec); return v.ok ? repairOwnRecipe(v.recipe) : null; }, // like own AI figures (no stats from the link: they follow from the recipe)
  blocked: () => mp.active ? 'Im Mehrspieler gibt es keinen Link.' : campaign.active ? 'Kampagnen-Missionen lassen sich nicht teilen.' : waves.on ? 'Die Verteidigung lässt sich nicht teilen.' : bet.locked() ? 'Erst die Wette beenden.' : '',
  snapshot: () => {
    const groups = readGroups(), recipes = {};
    for (const G of groups) if (/^u_/.test(G.type) && TYPES[G.type] && RECIPES[TYPES[G.type].recipe]) { const { faceMode, ...r } = RECIPES[TYPES[G.type].recipe]; recipes[G.type] = r; }
    const pressed = sel => $(sel)?.getAttribute('aria-pressed') === 'true';
    return { groups: placeMode === 'paint' ? groups.map(G => ({ ...G, count: paintS[G.team].filter(x => (x | 0) === G.slot).length })) : groups,
      mode: placeMode, paint, paintS, buildings: placedB.filter(b => BUILDINGS[b.key]), terrain: terrainChoice, seed: mapSeed, style: styleKey,
      world: { weather: $('#weather')?.value || 'auto', crater: pressed('[data-crater="1"]'), infection: pressed('#convertInf'), conversion: pressed('#convertConv') }, recipes };
  },
  apply: s => {
    $('#banner').hidden = true; if (simState !== 'setup') $('#edit').click();
    const tmap = {};
    for (const [k, rec] of Object.entries(s.recipes)) { const rk = 'gen' + (++genN), tk = 'u_' + rk; RECIPES[rk] = { ...rec, faceMode: AI_FACE_MODE }; TYPES[tk] = { ...statsForRecipe(rec), recipe: rk }; tmap[k] = tk; }
    if (Object.keys(tmap).length) refreshTypeSelects();
    terrainChoice = s.terrain; mapSeed = s.seed; $('#terrain').value = terrainChoice; $('#terrainNew').disabled = terrainChoice === 'flach';
    if (s.style && s.style !== styleKey) applyStyle(s.style);
    const wsel = $('#weather'); if (wsel && [...wsel.options].some(o => o.value === s.world.weather)) { wsel.value = s.world.weather; wsel.dispatchEvent(new Event('change')); }
    const cr = $(`[data-crater="${s.world.crater ? 1 : 0}"]`); if (cr && cr.getAttribute('aria-pressed') !== 'true') cr.click();
    convert.setOptions(s.world.infection, s.world.conversion);
    placedB.length = 0; placedB.push(...s.buildings);
    const cfgs = s.groups.map(G => { const type = tmap[G.type] || G.type; return { ...G, type, weapon: G.weapon || TYPES[type].weapon }; });
    if (s.mode === 'paint') {
      if (placeMode !== 'paint') setMode('paint');
      setSidesFrom(cfgs); paint[0] = s.paint[0]; paint[1] = s.paint[1]; paintS[0] = s.paintS[0]; paintS[1] = s.paintS[1];
      changed(); rebuildHash(); syncPaint();
    } else { if (placeMode !== 'count') setMode('count'); setSidesFrom(cfgs); changed(); }
  },
});
{ // "Link teilen" under the start button and in the victory banner
  const mk = cls => { const b = document.createElement('button'); b.className = cls; b.type = 'button'; b.innerHTML = '<i class="ti ti-link" aria-hidden="true"></i> Link teilen'; b.title = 'Schlacht als Link schicken: Freunde tippen den Sieger oder spielen sie nach'; b.addEventListener('click', () => battleLink.share()); return b; };
  $('#start').after(mk('ghost link-share')); $('#banner .bbtn').appendChild(mk('link-share'));
}
$('#modeCount').addEventListener('click', () => setMode('count'));
$('#modePaint').addEventListener('click', () => setMode('paint'));
const setBrushTeam = t => { brushTeam = t; $('#brushB').setAttribute('aria-pressed', String(t === 0)); $('#brushR').setAttribute('aria-pressed', String(t === 1)); };
// brush or eraser: the team buttons say what a stroke does ("Blau setzen" / "Blau radieren")
function setEraser(on) {
  eraser = !!on; painting = false; if (on) lineTool = false;
  const a = touchArmed; // touch, not armed yet: no tool shows as active
  $('#paintTool').setAttribute('aria-pressed', String(a && !eraser && !lineTool)); $('#eraseTool').setAttribute('aria-pressed', String(a && eraser)); $('#lineTool').setAttribute('aria-pressed', String(a && lineTool));
  $('#brushB').textContent = eraser ? 'Blau radieren' : 'Blau setzen'; $('#brushR').textContent = eraser ? 'Rot radieren' : 'Rot setzen';
  document.body.classList.toggle('aw-erasing', eraser);
}
$('#paintTool').addEventListener('click', () => { touchArmed = true; lineTool = false; setEraser(false); }); $('#eraseTool').addEventListener('click', () => { touchArmed = true; setEraser(true); });
$('#lineTool').addEventListener('click', () => { touchArmed = true; lineTool = true; setEraser(false); });
$('#brushB').addEventListener('click', () => setBrushTeam(0)); $('#brushR').addEventListener('click', () => setBrushTeam(1));
$('#brush').addEventListener('input', e => { brushN = +e.target.value; $('#brushO').textContent = brushN; });
$('#clearPaint').addEventListener('click', () => { paint[0] = []; paint[1] = []; paintS[0] = []; paintS[1] = []; rebuildHash(); syncPaint(); });
document.querySelectorAll('#tools [data-tool]').forEach(b => b.addEventListener('click', () => setTool(b.dataset.tool)));
/* ---------------- wand + dice: pick one of three ---------------- */
let modalReroll = null;
function closeModal() { $('#modal').hidden = true; }
$('#mClose').addEventListener('click', closeModal);
$('#modal').addEventListener('click', e => { if (e.target.id === 'modal') closeModal(); });
$('#mReroll').addEventListener('click', () => modalReroll && modalReroll());
function thumbFromModel(m, team) {
  let mnx = 1e9, mxx = -1e9, mny = 1e9, mxy = -1e9; const front = new Map();
  for (const [k, v] of m.map) {
    const [x, y, z] = DEC(k), kk = x * 4096 + y, f = front.get(kk);
    if (!f || z > f.z) front.set(kk, { x, y, z, c: v.c });
    if (x < mnx) mnx = x; if (x > mxx) mxx = x; if (y < mny) mny = y; if (y > mxy) mxy = y;
  }
  const w = mxx - mnx + 1, h = mxy - mny + 1, px = Math.max(2, Math.floor(Math.min(136 / h, 170 / w)));
  const cv = document.createElement('canvas'); cv.width = w * px; cv.height = h * px;
  const g = cv.getContext('2d'), c2 = new THREE.Color();
  for (const f of front.values()) {
    teamColor(f.c, team, c2); c2.multiplyScalar(m.map.has(KEY(f.x, f.y + 1, f.z)) ? 0.92 : 1.08);
    g.fillStyle = '#' + c2.getHexString(); g.fillRect((f.x - mnx) * px, (mxy - f.y) * px, px, px);
  }
  return cv;
}
const thumbOf = (rec, team, o) => thumbFromModel(recipeModel(rec, o), team);
function openChoices(title, sub, opts, onPick, reroll) {
  $('#mTitle').textContent = title; $('#mSub').textContent = sub;
  const box = $('#mOpts'); box.innerHTML = '';
  for (const o of opts) {
    const b = document.createElement('button'); b.className = 'mopt';
    const tw = document.createElement('div'); tw.className = 'thumb'; try { tw.appendChild(o.thumb()); } catch (err) { }
    const n = document.createElement('b'); n.textContent = o.name;
    const d = document.createElement('span'); d.textContent = o.desc;
    b.append(tw, n, d);
    b.addEventListener('click', () => { closeModal(); onPick(o); });
    box.appendChild(b);
  }
  modalReroll = reroll || null; $('#mReroll').hidden = !reroll;
  $('#modal').hidden = false; box.querySelector('button')?.focus();
}
function openWand(t) {
  const base = $('#type' + t).value, B = TYPES[base];
  if (!B.recipe) { toast('Der Zauberstab wirkt auf Bauplan-Figuren wie Ritter, Trump oder Optimus.'); return; }
  const cfg = readCfg(t);
  const pick = () => Object.keys(VARIANTS).sort(() => Math.random() - 0.5).slice(0, 3);
  const show = keysV => openChoices('Zauberstab', `Wähle eine Verwandlung für ${B.name}.`,
    keysV.map(v => ({ v, name: `${VARIANTS[v].name}-${B.name}`, desc: VARIANTS[v].desc, thumb: () => thumbOf(VARIANTS[v].fn(RECIPES[B.recipe]), t, cfg) })),
    o => { const tk = registerMutant(base, o.v); refreshTypeSelects(); $('#type' + t).value = tk; syncRows(t); changed(); toast(o.name + ' erschaffen'); },
    () => show(pick()));
  show(pick());
}
function rollSpec() {
  const bases = Object.keys(TYPES).filter(k => !k.startsWith('u_') && !shop.locked('unit', k)), vk = Object.keys(VARIANTS);
  const base = bases[(Math.random() * bases.length) | 0], B = TYPES[base], wk = Object.keys(WEAPONS).filter(k => !shop.weaponLocked(k, base));
  const serp = !!(B.recipe && RECIPES[B.recipe].template === 'serpent');
  const v = B.recipe && Math.random() < 0.6 ? vk[(Math.random() * vk.length) | 0] : null;
  const w = serp ? 'faeuste' : wk[(Math.random() * wk.length) | 0];
  const size = serp || B.r > 1.5 ? 1 : (Math.random() < 0.7 ? 1 : 2 + ((Math.random() * 5) | 0));
  const prop = 0.15 + Math.random() * 0.7, build = 0.8 + Math.random() * 0.6, head = 0.8 + Math.random() * 0.6;
  return { base, v, w, size, prop, build, head, name: (v ? VARIANTS[v].name + '-' : '') + B.name, desc: `${WEAPONS[w].name} · Größe ×${size}` };
}
function openDice(t) {
  const roll = () => [rollSpec(), rollSpec(), rollSpec()];
  const show = specs => openChoices('Würfel', 'Wähle eine der drei gewürfelten Einheiten.',
    specs.map(sp => ({ ...sp, thumb: () => { const B = TYPES[sp.base]; if (!B.recipe) return thumbFromModel(MODELS[sp.base](), t); return thumbOf(sp.v ? VARIANTS[sp.v].fn(RECIPES[B.recipe]) : RECIPES[B.recipe], t, { t: sp.prop, width: sp.build, head: sp.head }); } })),
    sp => {
      let type = sp.base; if (sp.v) { type = registerMutant(sp.base, sp.v); refreshTypeSelects(); }
      const cur = readCfg(t); writeCfg(t, [type, cur.count, sp.w, sp.size, Math.round(sp.prop * 100), Math.round(sp.build * 100), Math.round(sp.head * 100)]);
      changed(); toast(sp.name + ' gewürfelt');
    }, () => show(roll()));
  show(roll());
}
$('#rematch').addEventListener('click', () => { $('#banner').hidden = true; setupBattle(readGroups()); start(); });
$('#edit').addEventListener('click', toSetup);
$('#edit2').addEventListener('click', () => { $('#banner').hidden = true; toSetup(); });
$('#god').addEventListener('click', () => setPossess(-1));
$('#pfpv').addEventListener('click', () => setFpv(!fpv));
$('#pause').addEventListener('click', togglePause);
document.querySelectorAll('[data-speed]').forEach(b => b.addEventListener('click', () => { if (mp.active) { mp.speed(+b.dataset.speed); return; } timeScale = +b.dataset.speed; document.querySelectorAll('[data-speed]').forEach(x => x.classList.toggle('on', x === b)); paused = false; $('#pause').textContent = 'Pause'; sendRun(); }));
$('#kino').addEventListener('click', () => { kino = !kino; syncKino(); });
// feature:slowmo: tempo slider 0.1x–4x, bullet time, Regie camera; API for replays via getSlowmo() in src/features/slowmo.js
const slowmo = createSlowmo({
  worker, camGoal, camCur, toast, ground: groundH, armies: () => armies, bufs: () => [prevB, curB],
  state: () => ({ simState, paused, timeScale, kino, possessed, gen, N, split, GRP: GRPc }),
  setTimeScale: v => { timeScale = v; sendRun(); }, setKino: on => { kino = on; syncKino(); },
  blocked: () => shop.isOpen() || !$('#modal').hidden,
});
STYLE_KEYS.forEach(k => { const o = document.createElement('option'); o.value = k; o.textContent = STYLES[k].name; $('#style').appendChild(o); });
/* terrain choice: Flach / Hügel / Terrassen / Flusstal; "Neu" rolls a new map of the same kind */
TERRAIN_KEYS.forEach(k => { const o = document.createElement('option'); o.value = k; o.textContent = TERRAIN_PRESETS[k].name; $('#terrain').appendChild(o); });
$('#terrain').value = terrainChoice; $('#terrainNew').disabled = terrainChoice === 'flach';
function newMap() { mapSeed = 0; setupBattle(readGroups(), placeMode === 'paint'); if (placeMode === 'paint') rebuildHash(); }
$('#terrain').addEventListener('change', e => {
  terrainChoice = e.target.value; try { localStorage.setItem('aw-terrain', terrainChoice); } catch (err) { }
  $('#terrainNew').disabled = terrainChoice === 'flach'; newMap();
});
$('#terrainNew').addEventListener('click', newMap);
$('#style').addEventListener('change', e => applyStyle(e.target.value));
const nextStyle = () => applyStyle(STYLE_KEYS[(STYLE_KEYS.indexOf(styleKey) + 1) % STYLE_KEYS.length]);
/* ---------------- AI blueprint generator: "500 Wikinger auf Eisbären" -> new unit type ---------------- */
let genN = 0, generating = false;
// gameplay stats for a generated recipe, scaled from comparable hand-made types by template and size
function statsForRecipe(rec) {
  const b = rec.build || {}, name = String(rec.name || 'Kreatur').slice(0, 40);
  const base = { name, plural: name, bravery: 0.6, debris: 26 };
  if (rec.template === 'quadruped') {
    const s = clamp(((b.length || 2) * (b.height || 1.5)) / 14, 0.05, 2), big = s > 0.5;
    return { ...base, bravery: big ? 9 : 0.8, hp: Math.round(200 + 4000 * s), speed: +(5.6 - 2.6 * Math.min(1, s)).toFixed(1), r: +clamp(0.5 * (b.length || 2), 0.5, 3).toFixed(2),
      mass: +(2 + 20 * s).toFixed(1), dmgMul: +Math.max(1.4, 6 * s).toFixed(1), stomp: big, weapon: big ? 'stosszaehne' : 'pranke', debris: Math.round(30 + 60 * s) };
  }
  if (rec.template === 'serpent') {
    const s = clamp((b.length || 10) / 12, 0.2, 2);
    return { ...base, bravery: 9, hp: Math.round(30000 * s), speed: 3.1, r: +(3 * s).toFixed(2), mass: Math.round(50 * s), dmgMul: Math.round(20 * s), stomp: true, weapon: 'faeuste', debris: Math.round(130 * s) };
  }
  // humanoids fight by their role (recipe `kampf`, else name / prompt / era, src/game/combatRole.js); ranged roles are a bit frailer
  const k = kampfOf(rec);
  return { ...base, hp: k === 'nahkampf' ? 130 : k === 'magie' ? 115 : 105, speed: 3.3, r: 0.52, mass: 1.2, dmgMul: 1, weapon: KAMPF_WEAPON[k] };
}
/* new AI figures cost diamonds: the first 3 per account are free, then ECONOMY.generation.cost each (src/game/economy.js).
   Library and already generated (cached) figures are always free; without free figures or diamonds the server
   is asked for library/cache hits only (allowModel false) and never spends on the model. */
function genNeedsDiamonds() {
  const q = shop.generation.quota();
  toast(`Neue KI-Figuren kosten ${q.cost} Diamanten – bekannte Figuren aus der Bibliothek bleiben gratis`);
  shop.packs();
}
function genCharge(r) {
  if (!r || r.source !== 'model' || r.cached) return '';
  const c = shop.generation.charge(); syncGenQuota();
  return !c ? '' : c.free ? ` · gratis (noch ${c.freeLeft})` : ` · −${c.cost} Diamanten`;
}
function syncGenQuota() {
  const el = $('#genQuota'); if (!el) return;
  const q = shop.generation.quota();
  el.textContent = q.freeLeft ? `Noch ${q.freeLeft} Gratis-Figur${q.freeLeft === 1 ? '' : 'en'} · danach ${q.cost} ◆ pro neue Figur` : `Neue KI-Figur: ${q.cost} ◆ · Bibliotheks-Figuren gratis`;
}
// user decision 02.10.2026: while the AI builds a figure, a dark silhouette of it can already be placed
const SIL_COLOR = '#3b4150';
function beginSilhouette(t, text, count) {
  const prev = readCfg(t), base = fallbackUnitRecipe(text);
  const dark = c => (c === 'team' ? c : SIL_COLOR);
  const rec = { ...base, name: text, colors: Object.fromEntries(Object.keys(base.colors || {}).map(k => [k, SIL_COLOR])), add: (base.add || []).map(a => ({ ...a, color: dark(a.color) })) };
  const rk = 'gen' + (++genN), tk = 'u_' + rk, st = statsForRecipe(rec);
  RECIPES[rk] = { ...rec, faceMode: AI_FACE_MODE }; TYPES[tk] = { ...st, name: `${st.name} (entsteht …)`, plural: `${st.name} (entsteht …)`, recipe: rk };
  refreshTypeSelects();
  writeCfg(t, [tk, count || prev.count, st.weapon, prev.size]); changed();
  return { t, rk, tk, prev, weapon: st.weapon };
}
// generation gave nothing (no diamonds, blocked, error): the side gets its previous army back if the silhouette still stands
function dropSilhouette(sil) {
  if (readCfg(sil.t).type === sil.tk && simState === 'setup') { const p = sil.prev; writeCfg(sil.t, [p.type, p.count, p.weapon, p.size, p.t * 100, p.width * 100, p.head * 100]); changed(); }
}
async function generateArmy(t) {
  if (genKind === 'building') return generateBuilding(t);
  const inp = $('#prompt'); let text = inp.value.trim(); if (!text || generating) return;
  // a leading number is the head count: "500 Wikinger" -> 500 x "Wikinger"
  let count = 0; const m = text.match(/^(\d[\d.\s]*)\s+(.+)$/);
  if (m) { count = clamp(parseInt(m[1].replace(/[.\s]/g, ''), 10) || 0, 0, MAXC); text = m[2]; }
  if (isBlocked(text)) { toast(BLOCKED_MESSAGE); return; }
  generating = true; inp.disabled = true; $('#gen0').disabled = $('#gen1').disabled = true;
  const sil = simState === 'setup' && !campaign.active && !bet.locked() ? beginSilhouette(t, text, count) : null;
  toast(sil ? `„${text}“ entsteht … du kannst die Silhouette schon aufstellen` : `Bauplan für „${text}“ wird erzeugt …`);
  try {
    const r = await generate('unit', text, { allowModel: shop.generation.can() });
    if (r.source === 'needs_diamonds') { if (sil) dropSilhouette(sil); genNeedsDiamonds(); return; }
    if (r.source === 'blocked') { if (sil) dropSilhouette(sil); toast(BLOCKED_MESSAGE); return; }
    if (r.source === 'daily_limit') { if (sil) dropSilhouette(sil); toast(DAILY_LIMIT_MESSAGE); return; }
    if (!r.recipe) { if (sil) dropSilhouette(sil); toast('Bauplan konnte nicht erzeugt werden.'); return; }
    const rec = r.recipe.kampf ? r.recipe : { ...r.recipe, kampf: kampfOf(r.recipe, text) }; // the role travels with the recipe (MP, replays, collection)
    const st = adoptGeneratedUnit(t, rec, count, true, sil), paid = genCharge(r);
    if (sil && simState !== 'setup') toast(`${st.name} ist fertig – zu sehen ab der nächsten Schlacht${paid}`);
    else toast(r.source === 'library' ? `${st.name} erschaffen (aus der Figurenbibliothek)` : r.source === 'model' ? `${st.name} erschaffen${r.cached ? ' (aus dem Speicher)' : ''}${paid}` : `KI nicht erreichbar: ${st.name} ist eine Platzhalter-Figur`);
    if (r.rateKey) showRating(r.rateKey, st.name);
  } finally { generating = false; inp.disabled = false; $('#gen0').disabled = $('#gen1').disabled = false; }
}
// register a generated recipe as a new unit type and give it to army t (text prompt and photo upload)
// sil: the silhouette (beginSilhouette) the figure replaces under the same keys - it only changes where it still stands
function adoptGeneratedUnit(t, recipe, count = 0, offerSave = true, sil = null) {
  recipe = repairOwnRecipe(recipe); // once, before registering: MP, replays and the collection get the repaired recipe
  const rk = sil ? sil.rk : 'gen' + (++genN), tk = 'u_' + rk, st = statsForRecipe(recipe);
  RECIPES[rk] = { ...recipe, faceMode: AI_FACE_MODE }; TYPES[tk] = { ...st, recipe: rk }; // AI figures render without the face painter
  refreshTypeSelects();
  if (sil) {
    // the silhouette turns into the figure (in a running battle: from the next one on, the sim already has its stats)
    for (let s = 0; s < 2; s++) {
      const c = readCfg(s); if (c.type !== tk) continue;
      writeCfg(s, [tk, c.count, WEAPONS[c.weapon] && recipe.template === 'humanoid' && c.weapon !== sil.weapon ? c.weapon : st.weapon, c.size, c.t * 100, c.width * 100, c.head * 100]);
    }
    if (simState === 'setup') changed();
  } else {
    const cur = readCfg(t);
    writeCfg(t, [tk, count || cur.count, WEAPONS[cur.weapon] && recipe.template === 'humanoid' ? cur.weapon : st.weapon, cur.size]);
    changed();
  }
  if (offerSave && armyWin) armyWin.generated(t, recipe, recipe.name || ''); // "In Sammlung speichern"
  return st;
}
/* "Meine Sammlung" (src/game/collection.js): saved own figures become unit types 'u_col_<id>' */
const colUnitKey = id => 'u_col_' + id;
function registerCollectionUnit(e) {
  const rk = 'col_' + e.id, tk = colUnitKey(e.id);
  const rec = repairOwnRecipe(e.recipe); if (rec !== e.recipe) collection.setRecipe(e.id, rec); // figures saved before the repair existed: once
  RECIPES[rk] = { ...rec, faceMode: AI_FACE_MODE }; TYPES[tk] = { ...statsForRecipe(rec), recipe: rk };
  return tk;
}
for (const e of collection.list('unit')) registerCollectionUnit(e);
refreshTypeSelects();
let armyWin = null, selBox = null, pauseMenu = null;
// "Foto": photo of you / a friend / a pet / a toy -> comic voxel caricature (src/ui/photoUpload.js)
createPhotoUpload({
  mount: $('#gen1').parentElement,
  canSend: () => { if (shop.generation.can()) return true; genNeedsDiamonds(); return false; },
  onRecipe: (t, r) => { const st = adoptGeneratedUnit(t, r.recipe), paid = genCharge(r); toast(`${st.name} erschaffen${r.cached ? ' (aus dem Speicher)' : ''} – für ${t ? 'Rot' : 'Blau'}${paid}`); },
});
$('#gen0').addEventListener('click', () => generateArmy(0));
$('#gen1').addEventListener('click', () => generateArmy(1));
$('#prompt').addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); generateArmy(e.shiftKey ? 1 : 0); } });
$('#shake').value = shakeMode;
$('#shake').addEventListener('change', e => { shakeMode = e.target.value; shakeAmt = 0; try { localStorage.setItem('aw-shake', shakeMode); } catch { /* private mode */ } });
$('#styleBtn').addEventListener('click', () => { nextStyle(); toast(style.name + ': ' + style.desc); });
$('#tour').addEventListener('click', () => { tourOn = !tourOn; tourNext = performance.now() + 6000; $('#tour').setAttribute('aria-pressed', String(tourOn)); if (tourOn) toast('Stil-Tour: alle 6 Sekunden ein neuer Stil'); });
$('#quality').addEventListener('change', e => {
  qMode = e.target.value; try { localStorage.setItem('aw-quality', qMode); } catch (err) { }
  Q = QUAL[qMode === 'auto' ? (weak ? 'low' : 'medium') : qMode]; applyQuality(); autoPr = 0; if (qMode === 'auto') autoQ.reset(); setupBattle(readGroups());
});
$('#fpsCap').value = String(fpsCap);
$('#fpsCap').addEventListener('change', e => {
  const v = +e.target.value, gpuSwitch = (v === 30) !== (fpsCap === 30);
  fpsCap = v; try { localStorage.setItem('aw-fps', String(v)); } catch (err) { }
  if (gpuSwitch) toast(v === 30 ? 'Akku sparen: der sparsame Grafikchip wird nach dem Neuladen genutzt.' : 'Volle Grafikleistung wieder nach dem Neuladen.');
});
function syncKino() { $('#kino').setAttribute('aria-pressed', String(kino)); }
function sendRun() { worker.postMessage({ cmd: 'run', running: (simState === 'running' || simState === 'replay') && !paused, timeScale }); }
function start() {
  // empty sandbox field (nothing painted, no trainer buildings): take the chosen armies in their formation
  if (placeMode === 'paint' && !paint[0].length && !paint[1].length && !hasTrainer(0) && !hasTrainer(1) && !campaign.active) { setMode('count'); }
  if (placeMode === 'paint' && [0, 1].some(t => !paint[t].length && !hasTrainer(t))) { toast('Setze für beide Armeen mindestens eine Einheit oder ein Gebäude, das Truppen ausbildet.'); return; }
  if (simState !== 'setup') setupBattle(readGroups());
  simState = 'running'; paused = false; sendRun(); updateUIState();
  toast('Klicke auf eine Einheit, um sie selbst zu steuern');
}
function toSetup() { setupBattle(readGroups()); }
function togglePause() { if (mp.active) { mp.speed(paused ? 1 : 0); return; } paused = !paused; $('#pause').textContent = paused ? 'Weiter' : 'Pause'; sendRun(); }
function updateUIState() {
  const setup = simState === 'setup';
  const rpl = simState === 'replay';
  $('#panel').hidden = !setup; $('#ctrl').hidden = setup || rpl; $('#tools').hidden = setup || rpl || possessed >= 0; $('#hint').hidden = setup || rpl || possessed >= 0; $('#banner').hidden = true;
  player.sync();
  sendRun();
}
function showVictory() {
  if (simState !== 'running') return;
  if (campaign.over()) return; // feature:campaign
  if (waves.on) { waves.showEnd(); return; } // feature:waves
  const w = lastInfo.winner;
  $('#winTxt').textContent = w === 0 ? 'Blau gewinnt' : w === 1 ? 'Rot gewinnt' : 'Unentschieden';
  $('#winTxt').style.color = w === 0 ? 'var(--blue-l)' : w === 1 ? 'var(--red-l)' : 'var(--fg)';
  if (w >= 0) { const gs = teamArmies(w), A = gs[0], n = teamSum(w, G => G.stats.count); $('#winSub').textContent = `Nach ${clock(lastInfo.t)} · ${fmt(lastInfo.alive[w])} von ${fmt(n)} ${gs.length > 1 ? 'Einheiten' : n === 1 ? A.ty.name : A.ty.plural} überleben`; }
  else $('#winSub').textContent = `Nach ${clock(lastInfo.t)} ist niemand mehr übrig.`;
  const zt = zones.victoryText(w); if (zt) $('#winSub').textContent = `Nach ${clock(lastInfo.t)} · ${zt}`; // feature:zone
  const dt = dice.finish(w); if (dt) $('#winSub').textContent += ' · ' + dt; // feature:dice
  const lt = battleLink.finish(w); if (lt) $('#winSub').textContent += ' · ' + lt; // feature:link
  if (bet.finish(lastInfo)) return; // feature:bet
  shop.countBattle(w === 0); $('#winReward').hidden = true; // free battles pay nothing: diamonds come from campaign stars and wave records
  $('#banner').hidden = false;
  // the banner opens under the crosshair: a player still clicking to attack must not hit rematch / leave by accident
  $('#banner').classList.add('fresh'); setTimeout(() => $('#banner').classList.remove('fresh'), 800);
  sfx.victory(w);
}
const clock = t => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
let toastTimer = 0;
// messages queue up instead of overwriting each other; long texts stay longer (about 45 ms per character)
const toastQ = []; let toastShownAt = 0, toastCur = '';
function toast(msg) {
  msg = String(msg);
  if (msg === toastCur || toastQ.includes(msg)) return;
  const age = performance.now() - toastShownAt;
  if (toastCur && age < 900) { // the current one stays readable for at least 0.9 s, then the next follows
    toastQ.push(msg); if (toastQ.length > 3) toastQ.shift();
    clearTimeout(toastTimer); toastTimer = setTimeout(nextToast, 900 - age); return;
  }
  showToast(msg);
}
function nextToast() {
  const el = $('#toast');
  if (toastQ.length) { showToast(toastQ.shift()); return; }
  el.style.opacity = 0; toastCur = ''; setTimeout(() => { if (!toastCur) el.hidden = true; }, 300);
}
function showToast(msg) {
  const el = $('#toast'); el.textContent = msg; el.hidden = false; el.style.opacity = 1; toastCur = msg; toastShownAt = performance.now();
  clearTimeout(toastTimer);
  toastTimer = setTimeout(nextToast, toastQ.length ? 900 : clamp(1800 + msg.length * 45, 2200, 6000));
}
let hudT = 0;
function updateHUD(now) {
  if (now - hudT < 120) return; hudT = now;
  const al = lastInfo.alive || [teamSum(0, A => A.stats.count), teamSum(1, A => A.stats.count)];
  $('#cnt0').textContent = fmt(al[0]); $('#cnt1').textContent = fmt(al[1]);
  const h0 = (lastInfo.hp ? lastInfo.hp[0] : hp0[0]) / hp0[0], h1 = (lastInfo.hp ? lastInfo.hp[1] : hp0[1]) / hp0[1];
  const tot = h0 + h1 || 1;
  $('#bar0').style.width = (h0 / tot * 100) + '%'; $('#bar1').style.width = (h1 / tot * 100) + '%';
  $('#clock').textContent = clock(lastInfo.t || 0);
  if (pInfo && possessed >= 0) {
    $('#php').style.width = (100 * pInfo.hp / pInfo.max) + '%';
    $('#psp').style.width = (100 * (1 - pInfo.sp)) + '%';
    $('#pspm').classList.toggle('ready', pInfo.sp <= 0);
    $('#pspt').textContent = pInfo.sp > 0 ? (pInfo.sp * 4).toFixed(1) + ' s' : 'bereit';
  }
}

/* ---------------- buildings: picker, placement, setup data, upgrades, AI ----------------
   Placed buildings are part of the battle setup (like painted armies): every change re-sends `setup` to the worker.
   Lockstep later: placements go into `setup` as { key, team, level, x, z, rot } (+ generated recipes), upgrades are the
   command { t: 'upg', id } (the worker queues it and applies it at tick start). */
const BREG = {}; // recipe registry: key -> { recipe, name, era, ai }
for (const [k, r] of Object.entries(BUILDINGS)) BREG[k] = { recipe: r, name: r.name, era: r.era };
const recipeOfB = k => BREG[k].recipe;
const placedB = [];
const MAXB = 200;
let bSim = [], bPlace = false, bGenN = 0, genKind = 'unit';
const bSel = { key: 'kaserne', team: 0, level: 1, rot: 0 };
// "Meine Sammlung": saved buildings are placeable like AI designs (key col_<id>, ai: true so MP/share carry the recipe)
function syncCollectionB() {
  const ids = new Set();
  for (const e of collection.list('building')) {
    const k = 'col_' + e.id; ids.add(k);
    if (BREG[k]) continue;
    const recipe = validateBuildingRecipe(e.recipe).recipe; // repaired copy: always compiles
    BREG[k] = { recipe, name: e.name, era: recipe.era, ai: true };
  }
  // a deleted entry stays only while it stands on the field or is selected
  for (const k of Object.keys(BREG)) if (k.startsWith('col_') && !ids.has(k) && bSel.key !== k && !placedB.some(p => p.key === k)) delete BREG[k];
}
syncCollectionB();
collection.subscribe(() => { syncCollectionB(); fillBuildingSelect(); });
const bLayer = createBuildingLayer({ scene, toonGrad: TOON_GRAD, recipeOf: recipeOfB, fx: () => ({ debris, fx, shake, debrisMul: Q.debrisMul, shadows: Q.shadows > 0 }) });
const zones = createZones({ scene, toonGrad: TOON_GRAD, gen: () => gen, simState: () => simState, terrain: () => terrain, groundH, groundAt, toast, // feature:zone
  resetup: () => placeMode === 'paint' ? syncPaint() : setupBattle(readGroups(), true) }); // feature:zone
setupExtras.push(() => ({ zones: zones.msg() })); // feature:zone: victory condition + zones reach every client (absent = annihilation)
const escH = v => String(v).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
function bldSetup() {
  ensureTerrain();
  // buildings stand on the terrain height at their centre; guns move up with them (sim data is world space)
  bSim = buildingSimData(placedB, recipeOfB).map(d => {
    const y = groundH(d.x, d.z);
    return y ? { ...d, y, lv: d.lv.map(L => L && { ...L, guns: (L.guns || []).map(g => ({ ...g, y: g.y + y, by: g.by != null ? g.by + y : g.by })) }) } : d;
  });
  bLayer.set(bSim);
  $('#bCount').textContent = placedB.length ? fmt(placedB.length) + ' gesetzt' : '';
  // feature:convert: infection/conversion options + the zombies' extra reserve; feature:campaign: objectives, waves' reserve, zones
  const bm = { buildings: bSim, reserve: [spawnReserve(bSim, 0), spawnReserve(bSim, 1)] };
  if (betNeutral) return bm; // feature:bet: no infection reserve, no mission objectives
  return campaign.setupMsg(convert.setup(bm, armies, placeMode === 'paint' ? paint : null));
}
const hasTrainer = t => bSim.some(b => b.team === t && b.lv.some(l => l && l.trains));
// re-send the setup after buildings changed, without rebuilding the army meshes
function syncBuildings() {
  if (!armies[0] || simState !== 'setup') return;
  if (placeMode === 'paint') { syncPaint(); return; }
  const bm = bldSetup();
  const os = split, oN = N;
  applyRanges(bm.reserve);
  for (const A of armies) if (A.stats.count + A.reserve > A.lod1.instanceMatrix.count) regrowMeshes(A);
  if (os !== split || oN !== N) prevB = curB = null;
  gen++; suppressFrame = true;
  postSetup({ cmd: 'setup', gen, seed: battleSeed, armies: groupStats(), positions: null, minW: placedB.length ? ARENA.W : 0, ...bm });
}
function bPlotAt(x, z) {
  const fp = bLayer.footprint(bSel.key, bSel.level), odd = bSel.rot & 1;
  const hw = (odd ? fp.d : fp.w) / 2, hd = (odd ? fp.w : fp.d) / 2;
  const sx = Math.round(x * 2) / 2, sz = Math.round(z * 2) / 2, L = ARENA.W - 10;
  let ok = Math.abs(sx) + hw <= L && Math.abs(sz) + hd <= L;
  if (ok) for (const b of bSim) if (Math.abs(sx - b.x) < hw + b.hw - 0.01 && Math.abs(sz - b.z) < hd + b.hd - 0.01) { ok = false; break; }
  return { x: sx, z: sz, ok, hw };
}
function placeBuildingAt(cx, cy) {
  if (!groundAt(cx, cy, hit)) return;
  const p = bPlotAt(hit.x, hit.z);
  if (!p.ok) { toast('Hier ist kein Platz: das Baufeld überlappt ein Gebäude oder liegt außerhalb.'); return; }
  if (!campaign.canBuild(bSel, p.x, p.hw)) return; // feature:campaign
  if (placedB.length >= MAXB) { toast(`Höchstens ${MAXB} Gebäude`); return; }
  placedB.push({ key: bSel.key, team: bSel.team, level: bSel.level, x: p.x, z: p.z, rot: bSel.rot });
  syncBuildings();
}
function removeBuildingAt(cx, cy) {
  groundAt(cx, cy, hit); // sets the ray
  const b = bLayer.pickRay(ray.ray, false);
  if (!campaign.canRemove(b)) return; // feature:campaign
  if (b >= 0) { placedB.splice(b, 1); syncBuildings(); }
}
function upgradeAt(cx, cy) {
  if (simState === 'replay') return;
  groundAt(cx, cy, hit); // sets the ray
  const b = bLayer.pickRay(ray.ray);
  if (b < 0) return;
  if (!campaign.canUpgrade()) return; // feature:campaign
  const s = bLayer.state(b), name = BREG[bSim[b].key].name;
  if (s.level >= 3) { toast(`${name} ist schon voll ausgebaut`); return; }
  if (mp.active) { if (!mp.upgrade(b, bSim[b].team)) return; } else worker.postMessage({ cmd: 'upgrade', id: b });
  toast(`${name} wird auf Stufe ${s.level + 1} ausgebaut`);
}
function updateGhost() {
  if (!bPlace || simState !== 'setup' || !ptr.inside || COARSE || !groundAt(ptr.x, ptr.y, hit)) { bLayer.ghost(null); return; }
  const p = bPlotAt(hit.x, hit.z);
  // red when it cannot be built here: overlap / map edge, or (campaign) beyond the own half, over budget, not unlocked
  bLayer.ghost({ key: bSel.key, level: bSel.level, team: bSel.team, rot: bSel.rot, x: p.x, z: p.z, y: groundH(p.x, p.z), ok: p.ok && !campaign.buildBlock(bSel, p.x, p.hw) });
}
function spawnPuff(x, z) {
  for (let i = 0; i < 6; i++) {
    const a = Math.random() * 6.28, sp = 0.5 + Math.random();
    fx.spawn(x, 0.3, z, Math.cos(a) * sp, 0.6 + Math.random() * 0.8, Math.sin(a) * sp, 0.8 + Math.random() * 0.5, 0.5 + Math.random() * 0.4, 0, 1.5, 2, 0.86, 0.8, 0.68);
  }
}
function fillBuildingSelect() {
  const opt = k => `<option value="${escH(k)}">${escH(BREG[k].name)}</option>`, keys = Object.keys(BREG);
  let html = ERAS.map(e => [e.name, keys.filter(k => !BREG[k].ai && BREG[k].era === e.key)])
    .concat([['Weitere', keys.filter(k => !BREG[k].ai && !ERAS.some(e => e.key === BREG[k].era))], ['KI-Entwürfe', keys.filter(k => BREG[k].ai && !k.startsWith('col_'))], ['Meine Sammlung', keys.filter(k => k.startsWith('col_'))]])
    .filter(g => g[1].length).map(([n, ks]) => `<optgroup label="${escH(n)}">${ks.map(opt).join('')}</optgroup>`).join('');
  $('#bKey').innerHTML = html; $('#bKey').value = bSel.key;
}
// size, HP, armour and what a building does (panel line + build window cards)
function bStatBits(key, level) {
  const c = compiledFor(key, recipeOfB(key), level), st = c.stats, guns = (st.turret ? 1 : 0) + c.slots.length;
  const bits = [`${c.footprint.w} × ${c.footprint.d} m`, `LP ${fmt(st.hp)}`, `Rüstung ${Math.round(st.armor * 100)} %`];
  if (st.trains.length) bits.push('bildet Truppen aus');
  if (st.troopCap) bits.push(`Truppen +${st.troopCap}`);
  if (guns) bits.push(`${guns} ${guns > 1 ? 'Geschütze' : 'Geschütz'}`);
  return bits;
}
function showBInfo() { $('#bInfo').textContent = [...bStatBits(bSel.key, bSel.level), `gedreht ${bSel.rot * 90}°`].join(' · '); }
function rotateB() { bSel.rot = (bSel.rot + 1) & 3; showBInfo(); }
function setBPlace(on) {
  bPlace = on;
  $('#bPlace').setAttribute('aria-pressed', String(on)); $('#bPlace').textContent = on ? 'Platzieren: an' : 'Platzieren';
  if (on) toast(COARSE ? `Tippe aufs Feld, um ${BREG[bSel.key].name} zu bauen. Tippen auf ein Gebäude entfernt es.` : `Klicke aufs Feld, um ${BREG[bSel.key].name} zu bauen. Shift-Klick oder Rechtsklick entfernt, R dreht.`);
  else bLayer.ghost(null);
}
function selectBuilding(key) { bSel.key = key; fillBuildingSelect(); showBInfo(); setBPlace(true); }
const setBTeam = t => { bSel.team = t; $('#bTeam0').setAttribute('aria-pressed', String(t === 0)); $('#bTeam1').setAttribute('aria-pressed', String(t === 1)); };
$('#bTeam0').addEventListener('click', () => setBTeam(0)); $('#bTeam1').addEventListener('click', () => setBTeam(1));
document.querySelectorAll('[data-blvl]').forEach(b => b.addEventListener('click', () => {
  bSel.level = +b.dataset.blvl; document.querySelectorAll('[data-blvl]').forEach(x => x.setAttribute('aria-pressed', String(x === b))); showBInfo();
}));
$('#bKey').addEventListener('change', e => selectBuilding(e.target.value));
$('#bPlace').addEventListener('click', () => setBPlace(!bPlace));
$('#bRot').addEventListener('click', rotateB);
$('#bClear').addEventListener('click', () => { placedB.length = 0; syncBuildings(); });
$('#modePaint').addEventListener('click', () => setBPlace(false));
// "Armee beschreiben": Einheit | Gebäude
const setGenKind = k => {
  genKind = k; $('#genU')?.setAttribute('aria-pressed', String(k === 'unit')); $('#genB')?.setAttribute('aria-pressed', String(k === 'building')); // the toggle left the panel (armies and buildings have their own windows)
  $('#prompt').placeholder = k === 'unit' ? 'z. B. 500 Wikinger auf Eisbären' : 'z. B. Eisburg mit blauen Fahnen';
};
$('#genU').addEventListener('click', () => setGenKind('unit')); $('#genB').addEventListener('click', () => setGenKind('building'));
const generateBuilding = t => buildingFromPrompt($('#prompt').value.trim(), t);
// AI building from a description (panel prompt or build window); returns { key, name, recipe, source } or null
async function buildingFromPrompt(text, t) {
  const inp = $('#prompt'); if (!text || generating) return null;
  generating = true; inp.disabled = true; $('#gen0').disabled = $('#gen1').disabled = true;
  toast(`Bauplan für das Gebäude „${text}“ wird erzeugt …`);
  try {
    const r = await generate('building', text, { validateBuilding: validateBuildingRecipe, fallbackBuilding: fallbackBuildingRecipe, allowModel: shop.generation.can() });
    if (r.source === 'needs_diamonds') { genNeedsDiamonds(); return null; }
    if (r.source === 'blocked') { toast(BLOCKED_MESSAGE); return null; }
    if (r.source === 'daily_limit') { toast(DAILY_LIMIT_MESSAGE); return null; }
    if (!r.recipe) { toast('Gebäude konnte nicht erzeugt werden.'); return null; }
    const paid = genCharge(r);
    const key = 'ki' + (++bGenN), name = r.recipe.name || text;
    BREG[key] = { recipe: r.recipe, name, era: r.recipe.era, ai: true };
    setBTeam(t); selectBuilding(key);
    toast(r.source === 'model' ? `${name} entworfen${r.cached ? ' (aus dem Speicher)' : ''}${paid}: klicke aufs Feld, um es zu bauen` : `KI nicht erreichbar: ${name} ist ein Platzhalter-Gebäude`);
    return { key, name, recipe: r.recipe, source: r.source };
  } finally { generating = false; inp.disabled = false; $('#gen0').disabled = $('#gen1').disabled = false; }
}
fillBuildingSelect(); showBInfo();
// feature:waves — mode "Verteidigung" (src/features/waves.js): what it may use from here
const waves = createWavesUI({
  worker, toast, command: featCommand, blocked: () => mp.active ? 'Die Verteidigung gibt es nur im Einzelspieler.' : bet.locked() ? 'Erst die Wette beenden.' : campaign.active ? 'Erst die Kampagnen-Mission verlassen.' : '', bLayer, ptr, groundH, pickUnit, puff: spawnPuff,
  buildings: () => ({ keys: Object.keys(BREG).filter(k => !BREG[k].ai), recipeOf: recipeOfB, name: k => BREG[k].name }),
  placed: () => placedB, bSim: () => bSim, hp0: () => hp0,
  setEnemy: A => { const g = armies.indexOf(teamArmies(1).pop()), O = armies[g]; Object.assign(A, { team: 1, slot: O.slot, reserve: O.reserve, start: O.start, resStart: O.resStart, end: O.end }); disposeArmy(O); unitMeshes(A); armies[g] = A; },
  ground: (cx, cy) => groundAt(cx, cy, hit), pickBuilding: (cx, cy) => { groundAt(cx, cy, hit); return bLayer.pickRay(ray.ray); },
  countMode: () => { if (placeMode === 'paint') setMode('count'); setBPlace(false); setTool('hand'); },
  begin: () => { setupBattle(readGroups()); start(); },
  running: () => simState === 'running', reward: steps => shop.milestone('waveRecord', steps),
});
// feature:campaign: chapter menu, puzzle setup, objectives HUD and result screen (src/features/campaign.js)
const campaign = createCampaign({
  reward: stars => shop.milestone('campaignStar', stars), rewardBonus: n => shop.milestone('campaignBonus', n), rewardDaily: () => shop.milestone('daily', 1),
  convertOptions: (inf, conv) => convert.setOptions(inf, conv),
  THREE, scene, groundH, toast, readCfg, writeCfg: setSide, changed, paint, paintS, setTroops, troopsSnap: () => troopsSnap(), troopsRestore: s => troopsRestore(s), rebuildHash, syncPaint, placedB,
  get: () => ({ simState, placeMode, lastInfo, tool, terrainChoice, mapSeed, styleKey, eraser }),
  setEraser, walkable: (x, z) => !terrain || isWalkable(terrain, x, z),
  view: d => { Object.assign(camGoal, { tx: 0, tz: 0, ty: groundH(0, 0), dist: d, pitch: 0.82, yaw: 0.55 }); kino = false; syncKino(); }, // overview of both halves
  setTerrain: (choice, seed) => { terrainChoice = choice; mapSeed = seed; },
  setTool: k => { tool = k; document.querySelectorAll('#tools [data-tool]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.tool === k))); },
  resync: () => { if (placeMode === 'paint') syncPaint(); else syncBuildings(); }, // re-send the setup (bought tool charges)
  blocked: () => mp.active ? 'Die Kampagne gibt es nur im Einzelspieler.' : bet.locked() ? 'Erst die Wette beenden.' : waves.on ? 'Erst die Verteidigung beenden.' : '',
});
convert.init({ scene, fx: () => fx, debris: () => debris, resync: () => syncBuildings() }); // feature:convert
setupExtras.push(convert.extras); // feature:convert: the host's infection/conversion options (+ reserve in the host's stats)
const worldWin = createWorldOptions(); // after every feature built its controls (weather, crater, convert; slowmo for bullet time)
if (worldWin) mutators.mount(worldWin.addCard); // feature:mutators
armyWin = createArmyWindow({ // "Armeen": sides, counts and every figure as a 3D card (src/ui/armyWindow.js)
  types: () => TYPES, recipes: () => RECIPES, weapons: () => WEAPONS, teams: TEAMS, readCfg, toast,
  formation: t => +($('#formation' + t)?.value || 0),
  locked: k => !!shop.locked('unit', k), colKey: colUnitKey,
  onCollectionAdd: e => { const k = registerCollectionUnit(e); refreshTypeSelects(); return k; },
  onCollectionRemove: () => { /* the type stays registered until reload: a running setup may still use it */ },
});
const buildWin = createBuildWindow({ registry: () => BREG, eras: ERAS, toast, // Bauen: drives #bKey/#bTeam/[data-blvl]/#bPlace
  info: (k, L) => bStatBits(k, L).join(' · '), generate: (text, t) => buildingFromPrompt(text, t) });
// the panel keeps only the placing controls of the old "Gebäude" block; choosing happens in the Bauen window
for (const el of [$('#bKey'), $('#bTeam0')?.parentElement, $('[data-blvl]')?.parentElement]) if (el) el.hidden = true;
$('#bInfo')?.nextElementSibling?.setAttribute('hidden', '');
const itemWin = createItemWindow({ toolKeys: TOOL_KEYS, shop, toast, // after grab + disasters added their belt buttons; pauses a single-player battle while open
  pause: () => { if (mp.active || simState !== 'running' || paused) return null; togglePause(); return () => { if (paused) togglePause(); }; } });
// selection box in the setup (src/ui/selectBox.js): painted units + buildings
const bRadius = (() => { const c = new Map(); return (key, level, rot) => { const k = key + level; if (!c.has(k)) { const f = bLayer.footprint(key, level); c.set(k, Math.hypot(f.w, f.d) / 2 + 0.6); } return c.get(k); }; })();
let selSyncT = 0;
// editing single units of a counted formation: switch to "Selbst aufstellen" first (it copies the formation unit by unit)
function toPaintMode(sel) { if (placeMode !== 'paint' && sel.units.length) { setMode('paint'); toast('Jetzt „Selbst aufstellen“: jede Einheit einzeln bearbeitbar'); } }
selBox = createSelectBox({
  canvas, camera, scene, groundH, toast,
  groundAt: (cx, cy) => groundAt(cx, cy, hit) ? { x: hit.x, z: hit.z } : null,
  active: () => simState === 'setup' && !anyWindowOpen() && !(mainMenu && mainMenu.open) && !bPlace,
  items: () => {
    const units = [];
    if (placeMode === 'paint') for (let t = 0; t < 2; t++) { if (!campaign.canErase(t)) continue; const a = paint[t], s = paintS[t]; for (let k = 0; k < a.length; k += 2) units.push({ t, k: k / 2, x: a[k], z: a[k + 1], r: radiusAt(t, s[k / 2] | 0) }); }
    else if (curB && armies[0] && curB.length === N * 10) { // "Anzahl": the formation units, numbered like setMode('paint') will copy them
      const L = ARENA.W - 12, cnt = [0, 0];
      for (let i = 0; i < N; i++) {
        const o = i * 10; if (!curB[o + 9]) continue;
        const x = curB[o], z = curB[o + 2]; if (Math.abs(x) > L || Math.abs(z) > L) continue;
        const t = i < split ? 0 : 1; units.push({ t, k: cnt[t]++, x, z, r: armyOf(i).stats.r });
      }
    }
    return { units, buildings: placedB.map((b, i) => ({ i, x: b.x, z: b.z, r: bRadius(b.key, b.level) })).filter(b => campaign.ownsB(b.i)) }; // feature:campaign: mission buildings stay put
  },
  remove: sel => {
    toPaintMode(sel);
    for (let t = 0; t < 2; t++) {
      if (!campaign.canErase(t)) continue;
      const drop = new Set(sel.units.filter(u => u.t === t).map(u => u.k)); if (!drop.size) continue;
      filterPaint(paint, paintS, t, k => !drop.has(k));
    }
    for (const i of sel.buildings.slice().sort((x, y) => y - x)) if (campaign.canRemove(i)) placedB.splice(i, 1);
    if (placeMode === 'paint') { rebuildHash(); syncPaint(); } else syncBuildings();
    const n = sel.units.length + sel.buildings.length; if (n) toast(`${n.toLocaleString('de-DE')} entfernt`);
  },
  move: (sel, dx, dz, done) => {
    toPaintMode(sel);
    const L = ARENA.W - 10, cl = v => clamp(v, -L, L);
    for (const u of sel.units) { const a = paint[u.t]; a[2 * u.k] = campaign.clampX(cl(a[2 * u.k] + dx), 0.5); a[2 * u.k + 1] = cl(a[2 * u.k + 1] + dz); }
    for (const i of sel.buildings) {
      const b = placedB[i]; if (!b) continue;
      const f = bLayer.footprint(b.key, b.level), hw = ((b.rot & 1) ? f.d : f.w) / 2; // feature:campaign: the whole footprint stays in the own half
      b.x = campaign.clampX(cl(b.x + dx), hw); b.z = cl(b.z + dz);
      if (done) { b.x = campaign.clampX(Math.round(b.x * 2) / 2, hw); b.z = Math.round(b.z * 2) / 2; }
    }
    if (sel.units.length) { if (done) { rebuildHash(); syncPaint(); } else localBuffer(); }
    if (sel.buildings.length && (done || performance.now() - selSyncT > 120)) { selSyncT = performance.now(); syncBuildings(); }
  },
  rotate: sel => { for (const i of sel.buildings) if (placedB[i]) placedB[i].rot = (placedB[i].rot + 1) & 3; syncBuildings(); },
});
// the setup panel as a hub of menu windows (src/ui/setupHub.js)
const hub = createSetupHub();
hub.add({ key: 'army', icon: 'ti-swords', title: 'Armeen', open: () => armyWin.open(),
  summary: () => { storeTroop(0); storeTroop(1); const side = t => `${total(t).toLocaleString('de-DE')} ${troops[t].map(T => TYPES[T.type]?.name || '').join(' + ')}`; return `${side(0)} gegen ${side(1)}`; } });
hub.add({ key: 'build', icon: 'ti-building-castle', title: 'Bauen', open: () => buildWin.open(), summary: () => { const n = placedB.length; return n ? `${n} Gebäude gesetzt` : 'Burgen, Türme, Kasernen …'; } });
hub.add({ key: 'items', icon: 'ti-backpack', title: 'Items', open: () => itemWin.open(), summary: () => 'Granaten, Katastrophen … in die Leiste legen' });
if (worldWin) hub.add({ key: 'world', icon: 'ti-world', title: 'Welt', open: () => worldWin.open(), summary: () => worldWin.summary() });
// Einstellungen: graphics quality, shake, frame cap and design move out of the panel into their own window
let pendingResume = null; // the pause menu opened the settings: the battle goes on when they close
const settingsWin = createWindow({ id: 'settingsWin', title: 'Einstellungen', icon: 'ti-settings', foot: 'Sounds: Kenney (CC0), Schüsse: Vincent Sevedge (CC BY 3.0) · gespeichert in diesem Browser' });
settingsWin.onClose(() => { const r = pendingResume; pendingResume = null; r?.(); });
{ const f = $('#panel .foot'); if (f) settingsWin.body.appendChild(f);
  const b = document.createElement('button'); b.type = 'button'; b.className = 'ghost settings-open'; b.innerHTML = '<i class="ti ti-settings" aria-hidden="true"></i> Einstellungen';
  b.addEventListener('click', () => settingsWin.open()); $('#start').after(b); }
initDock(); initThemeSwitch(toast); // after the tool belt, order bar and battle bar exist
initTopMenu(); // phones: campaign, crates, multiplayer behind ☰
{ // "Schlacht starten" right in the Armeen window
  const go = document.createElement('button'); go.type = 'button'; go.className = 'go'; go.innerHTML = '<i class="ti ti-swords" aria-hidden="true"></i> Schlacht starten';
  go.addEventListener('click', () => { armyWin.close(); if (simState === 'setup') start(); });
  const ok = armyWin.win.foot.querySelector('.aw-win-ok'); ok.classList.remove('go'); ok.classList.add('ghost'); armyWin.win.foot.appendChild(go);
}
// Hauptmenü: modes, "Wer kämpft gegen wen?" (both armies from the AI, then the battle), today's challenge
async function fightFromMenu(a, b) {
  if (simState !== 'setup') return false;
  for (const [t, text] of [[0, a], [1, b]]) {
    const before = readCfg(t).type; $('#prompt').value = text;
    await generateArmy(t);
    if (readCfg(t).type === before) return false; // no diamonds / generation failed: the toast says why
  }
  // suggest, don't start: the Armeen window shows both figures with count, weapon, size, mount … to tweak
  $('#prompt').value = ''; showChosen(); return true; // the menu opens its configurator (army sides inline)
}
const clickSel = sel => () => document.querySelector(sel)?.click();
const mainMenu = createMainMenu({
  sandbox: () => { if (campaign.active) campaign.leave(); }, campaign: clickSel('.campaign-btn'), bet: clickSel('.bet-open'), defence: clickSel('#wavesEnter'),
  multiplayer: clickSel('.mpbtn:not(.wo-topbtn)'), collection: () => armyWin.open(), crates: clickSel('#crateBtn'), settings: () => settingsWin.open(),
  fight: fightFromMenu, canFight: () => simState === 'setup' && !generating,
  daily: () => campaign.openDaily(), // Tagesschlacht "Überleben" (src/content/daily.js)
  boss: () => campaign.openBoss(), // Boss-Kampf (src/content/bosses.js)
  diamonds: () => +($('#diaBal')?.textContent || '0').replace(/\D/g, ''),
  attachSides: el => armyWin.attachSides(el), detachSides: () => armyWin.detachSides(), start: () => { if (simState === 'setup') start(); },
});
{ const P = new URLSearchParams(location.search); if (!P.has('bench') && !P.has('nomenu') && !/^#b=/.test(location.hash)) mainMenu.show(); }
// the sandbox opens on an EMPTY field (user request): armies show up once chosen (Armeen window, scenario, main menu)
// or painted; start() takes the chosen armies if the field is still empty
function emptyField() {
  if (campaign.active || simState !== 'setup') return;
  setMode('paint'); paint[0] = []; paint[1] = []; paintS[0] = []; paintS[1] = []; rebuildHash(); syncPaint(); updateCountsUI();
}
const showChosen = () => { if (placeMode === 'paint' && !paint[0].length && !paint[1].length && !campaign.active) setMode('count'); };
for (const t of [0, 1]) $('#army' + t)?.addEventListener('change', showChosen);
if (!new URLSearchParams(location.search).has('bench')) setTimeout(emptyField, 0);
setTimeout(() => battleLink.openFromHash(), 400); // feature:link: "#b=…" sets up a shared battle
pauseMenu = createPauseMenu({
  pause: () => { if (mp.active || simState !== 'running' || paused) return null; togglePause(); return () => { if (paused) togglePause(); }; },
  setup: () => $('#edit').click(), rematch: () => $('#rematch').click(), canRematch: () => !mp.active && simState === 'running',
  settings: resume => { settingsWin.open(); if (resume) pendingResume = resume; }, mainMenu: () => { $('#edit').click(); setTimeout(() => mainMenu.show(), 50); },
  mode: () => (mp.active ? 'mp' : bet.locked() ? 'bet' : ''),
  forfeit: () => { bet.forfeit(); toSetup(); updateUIState(); toast('Wette aufgegeben – der Einsatz ist weg.'); },
  leaveMp: () => mp.leave(),
});

/* ---------------- multiplayer: lobby, setup sync, lockstep (src/net/mpClient.js, docs/multiplayer.md) ----------------
   The host's battle (armies + exact stats, painted positions, buildings + sim data, terrain arrays, style, tool table,
   generated recipes) travels once in the relay's `start`; every client, the host included, builds it from that payload. */
const mp = createMultiplayer({
  worker, toast, hostSetup: mpHostSetup, startMatch: mpStartMatch, endMatch: mpEndMatch,
  possessed: i => { if (possessed !== i) setPossess(i, true); },
  possessRejected: () => { toast('Diese Einheit steuert schon ein Mitspieler oder sie ist gefallen.'); setPossess(-1, true); },
});
function mpHostSetup(seed) {
  clearTimeout(changeTimer); if (paintDirty) syncPaint();
  setupBattle(readGroups(), true); // armies + buildings exactly as the panel shows them
  const tag = seed.toString(36), units = {}, brec = {};
  const cfgs = armies.map(A => ({ ...A.cfg, team: A.team, slot: A.slot }));
  for (const c of cfgs) {
    const ty = TYPES[c.type];
    if (!c.type.startsWith('u_') || !ty || !RECIPES[ty.recipe]) continue; // built-in types exist on every client
    const rk = mpKey(tag, ty.recipe), tk = 'u_' + rk;
    units[tk] = { type: { ...ty, recipe: rk }, recipe: RECIPES[ty.recipe] };
    c.type = tk;
  }
  const placed = placedB.map(p => ({ ...p })), bs = bSim.map(b => ({ ...b }));
  for (const list of [placed, bs]) for (const p of list) {
    const R = BREG[p.key]; if (!R || !R.ai) continue;
    const k = mpKey(tag, p.key); brec[k] = { recipe: R.recipe, name: R.name, era: R.era }; p.key = k;
  }
  return {
    cfgs, placeMode, stats: groupStats(), reserve: [0, 1].map(t => teamArmies(t).reduce((r, A) => A.reserve || r, 0)),
    paint: placeMode === 'paint' ? [packF32(paint[0]), packF32(paint[1])] : null, paintS: placeMode === 'paint' ? [paintS[0].slice(), paintS[1].slice()] : null,
    placed, buildings: bs, minW: placeMode === 'paint' || placedB.length ? ARENA.W : 0,
    terrain: { preset: terrainChoice, seed: mapSeed, data: terrain ? packTyped(workerTerrain(terrain)) : null },
    style: styleKey, recipes: { units, buildings: brec }, extras: collectExtras(),
    tools: Object.fromEntries(Object.entries(TOOLS).filter(([k]) => !DISASTER_TOOLS[k]).map(([k, T]) => [k, { dmg: T.dmg, aoe: T.aoe, knock: T.knock, kind: T.fx === 'gas' ? KIND_GAS : T.kind }])),
  };
}
function mpStartMatch(S, info) {
  bet.cancel(); waves.leave(); campaign.leave(); // feature:bet, feature:waves, feature:campaign (single player only)
  slowmo.setLockstep(true); // feature:slowmo: lockstep runs at the host's speed (MP.ts), no slider or bullet time
  // generated recipes under match-unique keys; a recipe that fails validation only changes looks (the sim uses S.stats)
  for (const [tk, u] of Object.entries(S.recipes.units || {})) {
    if (!/^u_mp[0-9a-z]+_\w+$/.test(tk) || !u || !u.type || u.type.recipe !== tk.slice(2)) continue;
    const v = validateUnitRecipe(u.recipe, { fallbackName: String(u.type.name || '') });
    RECIPES[u.type.recipe] = { ...(v.ok && !v.errors.length ? u.recipe : v.recipe), faceMode: AI_FACE_MODE }; TYPES[tk] = { ...u.type };
  }
  for (const [k, b] of Object.entries(S.recipes.buildings || {})) {
    if (!/^mp[0-9a-z]+_\w+$/.test(k) || !b) continue;
    const v = validateBuildingRecipe(b.recipe), name = String(b.name || 'Gebäude').slice(0, 40);
    BREG[k] = { recipe: v.ok ? b.recipe : (v.recipe || fallbackBuildingRecipe(name)), name, era: b.era, ai: true };
  }
  if (!S.cfgs.every(c => TYPES[c.type]) || !S.buildings.every(b => BREG[b.key])) throw new Error('unbekannte Einheit oder Gebäude (andere Version?)');
  if (!Array.isArray(S.stats) || S.stats.length !== S.cfgs.length || ![0, 1].every(t => S.cfgs.some((c, g) => (c.team === undefined ? (g ? 1 : 0) : c.team) === t))) throw new Error('Aufstellung unvollständig (andere Version?)');
  refreshTypeSelects(); fillBuildingSelect();
  setSidesFrom(S.cfgs);
  placeMode = S.placeMode === 'paint' ? 'paint' : 'count';
  $('#modeCount').setAttribute('aria-pressed', String(placeMode === 'count')); $('#modePaint').setAttribute('aria-pressed', String(placeMode === 'paint'));
  $('#paintBox').hidden = placeMode !== 'paint'; $('#autoNote').hidden = placeMode === 'paint';
  const pp = placeMode === 'paint' ? [unpackF32(S.paint[0]), unpackF32(S.paint[1])] : null;
  for (let t = 0; t < 2; t++) { paint[t] = pp ? Array.from(pp[t]) : []; paintS[t] = pp && S.paintS && Array.isArray(S.paintS[t]) ? S.paintS[t].map(v => v | 0) : []; }
  placedB.length = 0; for (const p of S.placed) placedB.push({ ...p });
  // terrain: the host's arrays (generateTerrain uses Math.sin/hypot, so clients must not regenerate it)
  const wt = S.terrain.data ? unpackTyped(S.terrain.data) : null;
  if (wt && !validTerrain(wt)) throw new Error('Gelände ungültig');
  terrainChoice = wt && TERRAIN_PRESETS[S.terrain.preset] ? S.terrain.preset : 'flach'; mapSeed = S.terrain.seed >>> 0;
  $('#terrain').value = terrainChoice; $('#terrainNew').disabled = terrainChoice === 'flach';
  terrain = wt ? { ...wt, props: { trees: [], rocks: [] }, river: null } : null;
  terrainKey = terrain ? terrainChoice + ':' + mapSeed : 'flach'; terrainVisKey = ''; envKey = '';
  if (STYLES[S.style] && S.style !== styleKey) applyStyle(S.style); else refreshTerrainVis();
  // like setupBattle, but with the host's stats, buildings and seed plus the lockstep info for the worker
  mountDispose(armies, mountCtx); // feature:mount
  armies.forEach(disposeArmy);
  const mcfg = S.cfgs.map(c => ({ ...c, mount: typeof c.mount === 'string' ? c.mount : '' })); // the host sends its groups sorted
  armies = mcfg.map((c, g) => { const A = buildArmy(c.team ? 1 : 0, c); A.slot = c.slot | 0; return A; });
  mountArmies(armies, mcfg); // feature:mount: the rider-on-mount look; the sim uses the host's stats (stats.mount) below
  armies.forEach((A, g) => { A.stats = S.stats[g]; });
  applyRanges(S.reserve);
  rebuildHash();
  convert.adopt(S.extras && S.extras.convert || null, armies); // feature:convert: the host's options (recolouring)
  armies.forEach(unitMeshes);
  bSim = S.buildings; bLayer.set(bSim); $('#bCount').textContent = placedB.length ? fmt(placedB.length) + ' gesetzt' : '';
  const pos = paintPositions();
  gen++; prevB = curB = null; frameMs = 1000 / 30; mpFrame.ts = -1;
  simState = 'running'; paused = false; timeScale = 1; overShown = false; possessed = -1; pInfo = null; deathSwitchAt = 0;
  setPossess(-1, true); setTool('hand'); fm.reset(); crater.reset(); // feature:formation, feature:crater
  debris.clear(); fx.clear(); thrown.reset(); suppressFrame = false; // feature:thrown
  battleSeed = info.seed;
  worker.postMessage({ ...(S.extras && typeof S.extras === 'object' && !Array.isArray(S.extras) ? S.extras : {}), cmd: 'setup', gen, seed: info.seed, armies: S.stats, positions: pos, minW: S.minW || 0, buildings: S.buildings, reserve: S.reserve, terrain: terrain ? workerTerrain(terrain) : null, mp: info.mp });
  sentTerrainKey = terrainKey;
  if (placeMode === 'paint') localBuffer();
  resetTotals();
  updateCountsUI(); updateUIState();
  toast(`Mehrspieler: du spielst für ${TEAMS[mp.team].name}. Klicke auf eine eigene Einheit, um sie zu steuern.`);
  return gen;
}
function mpEndMatch() { slowmo.setLockstep(false); frameMs = 1000 / 30; timeScale = 1; $('#banner').hidden = true; toSetup(); }
function mpFrame(m) {
  // lockstep frames come less regularly than 30 Hz: stretch the interpolation span a little instead of snapping
  if (tCur) frameMs += (clamp(performance.now() - tCur, 25, 50) - frameMs) * 0.1;
  mp.onFrame(m);
  if (m.mp.ts === mpFrame.ts) return;
  mpFrame.ts = m.mp.ts; paused = m.mp.ts === 0; $('#pause').textContent = paused ? 'Weiter' : 'Pause'; // host's speed command
  if (!paused) { timeScale = m.mp.ts; document.querySelectorAll('[data-speed]').forEach(x => x.classList.toggle('on', +x.dataset.speed === timeScale)); }
}
/* ---------------- replays & clips ----------------
   recorder: the battle's setup (postSetup) + every command with the tick the worker applied it at ('cmdlog').
   player (src/replay/player.js): replay mode = the worker re-runs the setup with the recorded commands; seek/speed.
   clipper (src/ui/clip.js): renders the last 15 s with a second worker and the kino camera into a 9:16 video. */
const recorder = createRecorder();
const rpListeners = new Set();
let capture = null, rpN = 0, snapId = 0;
// every new battle setup carries the feature settings (setupExtras); a replay's recorded setup is sent as it was.
// Posted first, recorded after: feature hooks on postMessage (weather) may add resolved fields to the message.
function postSetup(msg, recorded) {
  if (!recorded) msg = { ...collectExtras(), ...msg };
  worker.postMessage(msg);
  if (simState !== 'replay') recorder.setup(msg, sceneInfo(), matchTitle());
}
const armyLabel = A => `${fmt(A.stats.count)} ${A.stats.count === 1 ? A.ty.name : A.ty.plural}`;
const sideLabel = t => { const gs = teamArmies(t); return gs.length === 1 ? armyLabel(gs[0]) : gs.map(armyLabel).join(' + '); };
function matchTitle() { return firstOf(0) && firstOf(1) ? `${sideLabel(0)} vs. ${sideLabel(1)}` : 'SpawnAnything'; }
// what a replay needs to draw the battle on another machine: army configs, generated types/recipes, buildings, map look
function sceneInfo() {
  const types = {}, recipes = {}, breg = {};
  for (const A of armies) {
    const k = A && A.cfg.type; if (!k || !k.startsWith('u_') || !TYPES[k]) continue;
    types[k] = TYPES[k]; if (TYPES[k].recipe) recipes[TYPES[k].recipe] = RECIPES[TYPES[k].recipe];
  }
  for (const p of placedB) if (BREG[p.key] && BREG[p.key].ai) breg[p.key] = BREG[p.key];
  return { cfgs: armies.map(A => A && { ...A.cfg, team: A.team, slot: A.slot }), types, recipes, breg, buildings: placedB.map(p => ({ ...p })), style: styleKey, placeMode };
}
function enterReplay(rep) {
  if (clipper.busy) return;
  bet.cancel(); // feature:bet
  const sim = structuredClone(rep.sim), sc = rep.scene || {}, rmap = {}, tmap = {}, bmap = {};
  // generated unit types and AI buildings travel with the replay; a key that means something else here is renamed
  const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  for (const [k, r] of Object.entries(sc.recipes || {})) { const k2 = RECIPES[k] && !same(RECIPES[k], r) ? `rp${++rpN}_${k}` : k; RECIPES[k2] = r; rmap[k] = k2; }
  for (const [k, ty] of Object.entries(sc.types || {})) {
    const t2 = { ...ty, recipe: rmap[ty.recipe] || ty.recipe }, k2 = TYPES[k] && !same(TYPES[k], t2) ? `u_rp${++rpN}_${k.slice(2)}` : k;
    TYPES[k2] = t2; tmap[k] = k2;
  }
  refreshTypeSelects();
  for (const [k, e] of Object.entries(sc.breg || {})) { const k2 = BREG[k] && !same(BREG[k].recipe, e.recipe) ? `rp${++rpN}_${k}` : k; BREG[k2] = { ...e, ai: true }; bmap[k] = k2; }
  fillBuildingSelect();
  placedB.length = 0; for (const p of sc.buildings || []) placedB.push({ ...p, key: bmap[p.key] || p.key });
  sim.buildings = (sim.buildings || []).map(b => ({ ...b, key: bmap[b.key] || b.key }));
  // map, look and placement mode of the recording
  const ts = sim.terrainSpec || { preset: 'flach', seed: 0 };
  terrainChoice = TERRAIN_PRESETS[ts.preset] ? ts.preset : 'flach'; mapSeed = ts.seed || 0;
  $('#terrain').value = terrainChoice; $('#terrainNew').disabled = terrainChoice === 'flach';
  if (sc.style && STYLES[sc.style] && sc.style !== styleKey) applyStyle(sc.style);
  placeMode = sim.positions ? 'paint' : 'count';
  // groups of the recording (classic recordings: two armies = one group per side)
  const gs = normalizeGroups((sc.cfgs && sc.cfgs.length === sim.armies.length ? sc.cfgs : sim.armies.map(() => ({}))).map((c, g) => ({ ...c, team: c.team ?? sim.armies[g].team ?? (g ? 1 : 0), slot: c.slot ?? 0, _g: g })));
  paint[0] = []; paint[1] = []; paintS[0] = []; paintS[1] = [];
  if (sim.positions) for (const c of gs) { const p = sim.positions[c._g] || []; for (let k = 0; k < p.length; k += 2) { paint[c.team].push(p[k], p[k + 1]); paintS[c.team].push(c.slot); } }
  $('#modeCount').setAttribute('aria-pressed', String(placeMode === 'count')); $('#modePaint').setAttribute('aria-pressed', String(placeMode === 'paint'));
  $('#paintBox').hidden = placeMode !== 'paint'; $('#autoNote').hidden = placeMode === 'paint';
  rebuildHash();
  const cfgs = gs.map(g0 => {
    const A = sim.armies[g0._g], c = { type: 'ritter', weapon: 'schwert', size: 1, t: 0.4, width: 1, head: 1, ...g0 };
    delete c._g;
    c.type = tmap[c.type] || c.type; if (!TYPES[c.type]) c.type = 'ritter'; if (!WEAPONS[c.weapon]) c.weapon = 'schwert';
    c.mount = A && A.mount && typeof A.mount.key === 'string' ? A.mount.key : ''; // feature:mount: as recorded
    c.count = A.count; return c;
  });
  try { setSidesFrom(cfgs); } catch (e) { /* UI only */ }
  setTool('hand'); $('#banner').hidden = true;
  setupBattle(cfgs, false, { seed: rep.seed >>> 0, sim });
  waves.watch(sim.waves || null, bSim.length); // feature:waves: bought buildings and wave armies of a defence battle
  simState = 'replay'; paused = false; frameMs = 1000 / 30; timeScale = 1;
  worker.postMessage({ cmd: 'replay', cmds: rep.cmds, hashes: rep.hashes || [], end: rep.ticks, view: 1 });
  updateUIState();
}
function exitReplay() {
  waves.watch(null); // feature:waves
  const b = document.querySelector('[data-speed].on'); timeScale = b ? +b.dataset.speed : 1; frameMs = 1000 / 30;
  toSetup();
}
function resetVisuals() { waves.resetVisuals(); bLayer.set(bSim); debris.clear(); fx.clear(); disasters.reset(); crater.reset(); }
// render a clip with another worker; returns the function that switches back to the original one
function swapWorker(w) {
  const prev = { worker, gen, frameMs, timeScale };
  for (const b of new Set([prevB, curB])) if (b && b.byteLength) worker.postMessage({ cmd: 'buf', gen, b }, [b.buffer]);
  prevB = curB = null;
  w.onmessage = worker.onmessage; worker = w; gen++; suppressFrame = true; frameMs = 1000 / 30; timeScale = 1;
  return () => {
    w.terminate(); prevB = curB = null;
    ({ worker, gen, frameMs, timeScale } = prev);
    resetVisuals(); sendRun(); // the original worker answers with a fresh frame
    worker.postMessage({ cmd: 'resync' }); // …and the long-lived feature visuals (craters, lava) again
  };
}
// fixed-size rendering for a clip: kino camera framed on the armies, no possession view, no dynamic resolution
function captureBegin(w, h, onFrame) {
  capture = { onFrame, possessed, kino, ring: possRing.visible, cam: { ...camGoal } };
  possessed = -1; hovered = -1; possRing.visible = false; hoverRing.visible = false;
  renderer.setPixelRatio(1); renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix(); resizePost(Q);
  document.body.classList.add('aw-capture');
  kino = true; syncKino();
  const p = clashPoint(); if (!p) return;
  const r = p.gap * 0.5 + p.rms * 0.3 + armies.reduce((m, A) => Math.max(m, A.stats.r), 0.5) * 3 + 6, th = Math.tan(camera.fov * Math.PI / 360) * Math.min(1, w / h);
  Object.assign(camGoal, { tx: p.x, tz: p.z, ty: groundH(p.x, p.z), dist: clamp(r / th * 0.8, 14, 320), pitch: 0.56 });
  Object.assign(camCur, camGoal);
}
// where the fighting is: midpoint between both armies' centres (a lone giant weighs as much as a horde), plus spread
function clashPoint() {
  if (!curB) return null;
  const S = [0, 0, 0, 0, 0, 0], st = Math.max(1, (N / 4000) | 0);
  for (let i = 0; i < N; i += st) { const o = i * 10; if (!curB[o + 9]) continue; const t = i < split ? 0 : 3; S[t] += curB[o]; S[t + 1] += curB[o + 2]; S[t + 2]++; }
  const c = [0, 3].filter(t => S[t + 2]).map(t => [S[t] / S[t + 2], S[t + 1] / S[t + 2]]);
  if (!c.length) return null;
  const x = (c[0][0] + c[c.length - 1][0]) / 2, z = (c[0][1] + c[c.length - 1][1]) / 2;
  let r2 = 0, n = 0;
  for (let i = 0; i < N; i += st) { const o = i * 10; if (curB[o + 9]) { r2 += (curB[o] - x) ** 2 + (curB[o + 2] - z) ** 2; n++; } }
  return { x, z, gap: c.length > 1 ? Math.hypot(c[0][0] - c[1][0], c[0][1] - c[1][1]) : 0, rms: Math.sqrt(r2 / n) };
}
function captureEnd() {
  if (!capture) return;
  const c = capture; capture = null;
  possessed = c.possessed; possRing.visible = c.ring; kino = c.kino; syncKino(); Object.assign(camGoal, c.cam);
  document.body.classList.remove('aw-capture'); renderer.setPixelRatio(pr); resize();
}
const game = {
  get state() { return simState; }, get paused() { return paused; }, get kino() { return kino; }, get clipBusy() { return clipper.busy; },
  canvas, coarse: COARSE, recorder, toast, title: matchTitle, info: () => lastInfo,
  listen(f) { rpListeners.add(f); return () => rpListeners.delete(f); },
  post(m) { worker.postMessage(m); },
  setPaused(p) { if (simState === 'setup') return; paused = !!p; $('#pause').textContent = paused ? 'Weiter' : 'Pause'; sendRun(); },
  setView(v) { frameMs = 1000 / 30 / v; timeScale = v; worker.postMessage({ cmd: 'rpview', v }); },
  setKino(on) { kino = !!on; syncKino(); },
  clip: () => clipper.start(), enterReplay, exitReplay, resetVisuals, swapWorker, captureBegin, captureEnd,
  makeWorker: () => new SimWorker(),
  setupMsg: rep => ({ cmd: 'setup', gen, ...structuredClone(rep.sim), terrain: workerTerrain(terrain) }),
  requestSnap(before) {
    const id = ++snapId;
    return new Promise(res => {
      const off = game.listen(m => { if (m.type === 'snap' && m.id === id) { clearTimeout(t); off(); res(m.snap); } });
      const t = setTimeout(() => { off(); res(null); }, 3000);
      worker.postMessage({ cmd: 'snapreq', before, id });
    });
  },
  // what a clip shows: the live battle (until shortly after it was decided) or the replay up to the current position
  clipSource() {
    if (simState === 'replay') { const c = player.current(); return c && { ...c, window: bestWindow(slowmo.getHighlights(), c.end) }; }
    if (simState !== 'running' || !recorder.ready) return null;
    const end = recorder.overAt ? Math.min(recorder.ticks, recorder.overAt + 45) : recorder.ticks;
    return { rep: recorder.build(end), end, window: bestWindow(slowmo.getHighlights(), end, recorder.overAt || 0) }; // auto clip: the best 15 s
  },
};
const player = createReplayPlayer(game);
const clipper = createClipper(game);
if (import.meta.env && import.meta.env.DEV) window.awReplay = { game, player, clipper, camGoal, camCur, terrain: () => terrain, setPossess, setFpv, possessed: () => possessed, start: () => start(), groups: cfgs => setupBattle(cfgs), armies: () => armies, link: () => battleLink }; // dev console access (camera: screenshots)

// feature:bet — betting mode: rolled close matchups, coins on Blue or Red, spectate only (src/features/bet.js)
const bet = createBet({
  types: TYPES, weapons: WEAPONS, recipes: RECIPES, toast,
  allowType: k => !shop.locked('unit', k), allowWeapon: (w, k) => !shop.weaponLocked(w, k),
  read: readCfg,
  // seed: a calibrated bet battle runs with one of the pool's measured seeds (src/features/betOdds.js rollFromPool)
  apply: (cfgs, seed) => { if (betNeutral) betNeutral.seed = seed || 0; for (let t = 0; t < 2; t++) { const ms = $('#mount' + t); if (ms) ms.value = ''; } if (placeMode === 'paint') setMode('count'); setBPlace(false); setTool('hand'); setSide(0, cfgs[0]); setSide(1, cfgs[1]); changed(); },
  takeBuildings: () => placedB.splice(0),
  putBuildings: list => { placedB.push(...list); syncBuildings(); },
  adopt: (t, recipe) => { adoptGeneratedUnit(t, recipe, 0, false); return readCfg(t).type; },
  start, info: () => lastInfo, blocked: () => mp.active || waves.on || campaign.active,
  // neutral ground while betting: flat field (the sandbox terrain comes back afterwards), see collectExtras
  neutral: on => {
    if (on && !betNeutral) { betNeutral = { terrainChoice, mapSeed }; terrainChoice = 'flach'; }
    else if (!on && betNeutral) { ({ terrainChoice, mapSeed } = betNeutral); betNeutral = null; }
    $('#terrain').value = terrainChoice; $('#terrainNew').disabled = terrainChoice === 'flach';
  },
});
/* ---------------- main loop ---------------- */
let last = performance.now(), fpsN = 0, fpsT = last, ftAvg = 16, capT = 0;
function resize() {
  if (capture) return; // a clip renders at its own fixed size
  const w = innerWidth, h = innerHeight;
  if (!autoPr && pr !== NATIVE_PR()) { pr = Q.pr = NATIVE_PR(); renderer.setPixelRatio(pr); } // window moved to another display
  renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
  if (typeof post !== 'undefined') resizePost(Q);
}
addEventListener('resize', resize); resize();
function frame(now) {
  requestAnimationFrame(frame);
  if (shop.isOpen()) { last = frame.prev = now; return; } // full-screen crate overlay: battle paused, skip rendering behind it (no frame-time spike after)
  if (fpsCap) { const iv = 1000 / fpsCap; if (now - capT < iv - 2) return; capT = Math.max(capT + iv, now - iv); } // frame cap
  const dt = Math.min(0.05, (now - last) / 1000); last = now;
  uTime.value += slowmo.frame(dt); // feature:slowmo: figures, water, grass and weather run on game time
  if (simState !== 'setup' && !paused) uGTime.value += dt * timeScale;
  if (deathSwitchAt && now > deathSwitchAt) { deathSwitchAt = 0; if (possessed >= 0) switchTeammate(); }
  godKeys(dt); sendInput(now); mp.tick(now);
  if (hoverDirty && possessed < 0 && !ptr.down) { hoverDirty = false; hovered = pickUnit(ptr.x, ptr.y); canvas.style.cursor = hovered >= 0 ? 'pointer' : 'default'; }
  if (painting && !erasing && now - lastStampT > 110) stampAt(ptr.x, ptr.y);
  if (paintDirty && now - lastSync > 50) syncPaint();
  renderUnits(now);
  grab.frame(dt, now); // feature:grab
  bLayer.frame(slowmo.now(), cull); updateGhost(); // feature:slowmo (building effects on game time)
  zones.frame(now); // feature:zone
  disasters.frame(dt); // feature:disaster
  waves.frame(now); // feature:waves
  updateCursorRing();
  fm.frame(now); // feature:formation
  updateCamera(dt);
  wfx.update(); sfx.update(now, dt, { state: simState, paused, timeScale, over: !!lastInfo.over, camDist: camCur.dist });
  // damage vignette
  const fl = flashT ? Math.max(0, 1 - (now - flashT) / 250) : 0;
  const nk = nukeT ? Math.max(0, 1 - (now - nukeT) / 700) : 0;
  if (!REDUCED) canvas.style.filter = nk > 0 ? `brightness(${1 + nk * 1.6}) saturate(${1 - nk * 0.5})` : fl > 0 ? `saturate(${1 + fl}) brightness(${1 + fl * 0.15})` : '';
  updateTorches(now); updateAmbient();
  weatherFx.frame(now); // feature:weather
  if (post.rt) updateOutline(post.mat.uniforms, camera, post.rt.height, post.olR, scene.fog);
  if (post.active && post.rt) { renderer.setRenderTarget(post.rt); renderer.render(scene, camera); renderer.setRenderTarget(null); renderer.render(post.scene, post.cam); }
  else renderer.render(scene, camera);
  if (capture) capture.onFrame(now); // clip: copy this frame into the video
  syncStyleLock();
  if (tourOn && now > tourNext) { tourNext = now + 6000; applyStyle(STYLE_KEYS[(STYLE_KEYS.indexOf(styleKey) + 1) % STYLE_KEYS.length]); }
  updateHUD(now);
  if (statsGL) statsGL.update();
  // fps + dynamic resolution
  fpsN++; ftAvg += ((now - (frame.prev || now)) - ftAvg) * 0.05; frame.prev = now;
  if (now - fpsT > 500) { $('#fps').textContent = Math.round(fpsN * 1000 / (now - fpsT)) + ' fps · ' + (pr >= NATIVE_PR() - 0.01 ? 'nativ' : (pr / NATIVE_PR() * 100 | 0) + '%'); fpsN = 0; fpsT = now; }
  if (qMode === 'auto' && !capture) autoQ.tick(now, fpsCap ? 1000 / fpsCap : 0); // not while a clip records (fixed size)
}
{ const k = styleKey; style = WIESE; applyStyle(k); }
setSide(0, PRESETS[0].a); setSide(1, PRESETS[0].b);
setupBattle(readGroups()); showStats();
document.querySelector('#presets .chip:not(.dice-chip)').classList.add('on');
requestAnimationFrame(frame);
setTimeout(() => shop.greet(), 1200);

/* ---------------- dev: GPU/CPU timing overlay (F3) ---------------- */
let statsGL = null;
addEventListener('keydown', async e => {
  if (e.code !== 'F3') return;
  e.preventDefault();
  if (!statsGL) { const { default: Stats } = await import('stats-gl'); statsGL = new Stats({ trackGPU: true, horizontal: true }); statsGL.init(renderer); document.body.appendChild(statsGL.dom); statsGL.dom.style.zIndex = 50; }
  else statsGL.dom.hidden = !statsGL.dom.hidden;
});

/* ---------------- benchmark: ?bench=1 (src/render/bench.js) ---------------- */
if (new URLSearchParams(location.search).has('bench')) import('./render/bench.js').then(b => b.runBench({
  renderer, camGoal, camCur, placedB, syncBuildings, groundH, post, setPossess, possessed: () => possessed, quality: () => Q, autoLevel: () => autoQ.level,
  bomb: (x, z, k) => { const T0 = TOOLS[k]; worker.postMessage({ cmd: 'bomb', x, z, dmg: T0.dmg, aoe: T0.aoe, knock: T0.knock, kind: T0.kind }); }, setPixelRatio: p => { pr = autoPr = p; renderer.setPixelRatio(p); resizePost(Q); },
}));
